// recalc.js — renderEstimate, scheduleRenderEstimate, and all their dependencies
// Extracted from script.js. script.js imports these via: import { ... } from './recalc.js'

import { effortConfig, calcJobHcUnits, blockHours, lockedQtyFieldKeys, nasEffortWeight } from './config.js';
import { AppState, architectureState, upgradeScope, expandedComponentRows, sites, hypervisorMigrationScopes, vb365RepoMigrationScopes, vbrRepoMigrationScopes,
         workloadPickerUiState, siteScopePickerUiState, defaultStep2CollapseState,
         existingEnvironmentFieldSchema, existingEnvironmentFieldSchemaByKey,
         existingEnvironmentCountFieldOrder,
         existingEnvironmentFieldLabels } from './state.js';
import { unstructuredDataWorkloadId, repoMigrationWorkloadId, kastenK10WorkloadId,
         veeamBackupForMicrosoft365WorkloadId, legacyWorkloadValueAliases,
         hypervisorLabels, cloudLabels, saasLabels, containerLabels, pluginLabels,
         pluginFullLabels, unstructuredDataLabels, sourceWorkloadLabels, agentLabels,
         agentVersionOptions, getAgentVersionOptions, getDefaultAgentVersion,
         veeamOneOptionLabels, implementationModeLabels, getImplementationModeLabel,
         engagementTypeLabels,
         architectureOptionLabels, tapeConnectivityTypeLabels,
         workloadOptionGroups, jobTypeOptions, sureBackupAllowedWorkloads,
         replicationAllowedWorkloads, cdpAllowedWorkloads, backupCopyExcludedWorkloads,
         jobWorkloadOptions, getJobTypeDef, getJobWorkloadDef, stripWorkloadUnitSuffix,
         getHypervisorBackupJobsFromSites, getHypervisorUnprotectedWorkloadsFromSites,
         isJobHypervisorMigrationSource, pruneStaleHypervisorMigrationScopes,
         inDevelopmentWorkloadStatusLabel, inDevelopmentWorkloadValues } from './workload-defs.js';
import { createExistingEnvironmentIconMarkup, createWorkloadIconMarkup,
         vbrIconMarkup, vbemIconMarkup, veeamOneIconMarkup,
         backupRepositoryIconMarkup, vroIconMarkup, backupProxyIconMarkup,
         wanAcceleratorIconMarkup, veeamAgentIconMarkup, enterprisePluginIconMarkup,
         backupJobsIconMarkup, sureBackupJobsIconMarkup, cdpPoliciesIconMarkup,
         replicationJobsIconMarkup, virtualLabIconMarkup,
         vb365ProxyIconMarkup, vb365RepositoryIconMarkup, vbfCloudRepositoryIconMarkup,
         vmwareWorkloadIconMarkup, vmwareCloudDirectorWorkloadIconMarkup,
         hypervWorkloadIconMarkup, nutanixWorkloadIconMarkup, ovirtWorkloadIconMarkup,
         kvmWorkloadIconMarkup, proxmoxWorkloadIconMarkup,
         scaleComputingHyperCoreWorkloadIconMarkup, hpeMorpheusVmEssentialsWorkloadIconMarkup,
         genericPluginWorkloadIconMarkup, genericAgentWorkloadIconMarkup,
         cloudWorkloadIconMarkup, microsoft365WorkloadIconMarkup,
         kubernetesWorkloadIconMarkup, unstructuredDataWorkloadIconMarkup,
         awsWorkloadIconMarkup, azureWorkloadIconMarkup, gcpWorkloadIconMarkup,
         workloadIconByValue } from './icons.js';
import { buildFormulaDebugPanelMarkup } from './formula-debug.js';
import { escapeHtml, isUnstructuredDataWorkload, isRepoMigrationWorkload,
         isProtectedTbWorkload, isNamespaceScopedWorkload, isUserScopedWorkload,
         isVb365ManagedWorkload, isVbfCloudManagedWorkload, getVbfCloudProvider,
         CLOUD_PROVIDER_CONFIGS, normalizeWorkloadValue, getWorkloadQuantityLabel,
         getWorkloadQuantityPlaceholder, getWorkloadQuantitySummary,
         normalizeProtectedTb, calculateUnstructuredDataInstances,
         formatProtectedTbValue, formatProtectedTbWholeNumber,
         normalizeEnvironmentCount, normalizeToggleMigratedCount } from './utils.js';
import { downloadJsonFile, buildExportFilename, generateSowMarkup } from './sow.js';
import { getExistingEnvironmentFieldDefinition, getExistingEnvironmentPrimaryParentField, isExistingEnvironmentChildOf,
         hasSelectedWorkloadValue, isInDevelopmentWorkloadValue,
         getRepoMigrationPickerOptionLabel, isExistingEnvironmentFieldAvailableForEngagement,
         getDefaultSiteName, getExistingEnvironmentCountFieldOrder,
         normalizeWorkloadCustomLabel, getProtectedTbSummary,
         getRepoMigrationWorkloads, getRepoMigrationWorkloadProtectedTb, mapVbrRepoWorkloadToSourceType,
         getWorkloadType, toggleValue, toggleLabel, getCanvasEngagementFlags,
         shouldShowArchitectureSiteTopology,
         inferSiteTopology, resolvePlatformArchitectureState,
         getImplementationMode, sanitizeToggleState,
         getSiteById, hasVbrAcrossAllSites, hasUnprotectedWorkloadsNeedingVbr, hasCloudWorkloads,
         gfGroupLabel, getGfWorkloadGroup, isSubCellNA,
         buildVeeamServerTypeChipMarkup, buildGfCompChipMarkup,
         getLinkedServerForJob, deriveEngagementType,
         normalizeSiteWorkloads } from './helpers.js';


// ─── Constants injected from script.js (needed by recalc.js) ─────────────────

const workloadPickerPlaceholderLabel = 'Select Workload...';

const GF_SVG_GRIP_LG = `<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true"><circle cx="2.5" cy="2" r="1.5"/><circle cx="7.5" cy="2" r="1.5"/><circle cx="2.5" cy="8" r="1.5"/><circle cx="7.5" cy="8" r="1.5"/><circle cx="2.5" cy="14" r="1.5"/><circle cx="7.5" cy="14" r="1.5"/></svg>`;
const GF_SVG_GRIP_SM = `<svg width="8" height="12" viewBox="0 0 8 12" fill="currentColor" aria-hidden="true"><circle cx="2" cy="2" r="1.2"/><circle cx="6" cy="2" r="1.2"/><circle cx="2" cy="6" r="1.2"/><circle cx="6" cy="6" r="1.2"/><circle cx="2" cy="10" r="1.2"/><circle cx="6" cy="10" r="1.2"/></svg>`;
const GF_SVG_PLUS    = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M5 1v8M1 5h8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
const GF_SVG_CLOSE   = `<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
const GF_SVG_CLONE   = `<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><rect x="4.5" y="4.5" width="7" height="7" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M1.5 8.5V2a.5.5 0 0 1 .5-.5h6.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>`;
const GF_SVG_PENCIL  = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>`;

const GF_SVG_ICONS = {
    'Hypervisors':       `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>`,
    'Cloud':             `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/></svg>`,
    'SaaS':              `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`,
    'Containers':        `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>`,
    'Applications':      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>`,
    'Unstructured Data': `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`,
    'Physical':          `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 8h10M7 12h4"/></svg>`,
};

const _S = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">`;
const _E = `</svg>`;
const GF_SVG_WORKLOAD_ICONS = {
    'vmware':
        _S + `<rect x="2" y="2" width="13" height="13" rx="2" fill="white" stroke="currentColor" stroke-width="1.8"/><rect x="9" y="9" width="13" height="13" rx="2" fill="white" stroke="currentColor" stroke-width="1.8"/>` + _E,
    'vmware-cloud-director':
        _S + `<path d="M17 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/><path d="M9 14h4v4"/>` + _E,
    'hyperv':
        _S + `<rect x="3" y="3" width="8" height="8" rx="1.2"/><rect x="13" y="3" width="8" height="8" rx="1.2"/><rect x="3" y="13" width="8" height="8" rx="1.2"/><rect x="13" y="13" width="8" height="8" rx="1.2"/>` + _E,
    'nutanix':
        _S + `<rect x="4" y="3" width="5" height="18" rx="1"/><rect x="11" y="3" width="5" height="9" rx="1"/><rect x="11" y="14" width="5" height="7" rx="1"/>` + _E,
    'ovirt':
        _S + `<circle cx="12" cy="12" r="6"/><path d="M12 4a8.5 8.5 0 0 1 7 3.5"/><path d="M19.5 6l.5 4-4-.5"/><path d="M12 20a8.5 8.5 0 0 1-7-3.5"/><path d="M4.5 18l-.5-4 4 .5"/>` + _E,
    'proxmox':
        _S + `<path d="M12 3l7.5 4.5v9L12 21l-7.5-4.5v-9z"/><path d="M12 3v18M4.5 7.5l7.5 4.5 7.5-4.5"/>` + _E,
    'scale-computing-hypercore':
        _S + `<rect x="2" y="4" width="20" height="5" rx="1.5"/><rect x="2" y="10" width="20" height="5" rx="1.5"/><rect x="2" y="16" width="20" height="4" rx="1.5"/>` + _E,
    'hpe-morpheus-vm-essentials':
        _S + `<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96L12 12l8.73-5.04M12 22.08V12"/>` + _E,
    'kvm':
        _S + `<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/><path d="M7 8l3.5 3L7 14"/><path d="M13 14h4"/>` + _E,
    'aws-ec2': _S + `<path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/><path d="M8 22c1.5 1.3 3.8 2 6.5 2s5-0.7 6.5-2"/><path d="M19.5 22.5l1.5.3-.3 1.5" transform="scale(0.85) translate(2.5,-1)"/>` + _E,
    'aws-rds': _S + `<path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/><path d="M8 22c1.5 1.3 3.8 2 6.5 2s5-0.7 6.5-2"/><path d="M19.5 22.5l1.5.3-.3 1.5" transform="scale(0.85) translate(2.5,-1)"/>` + _E,
    'azure-vm':
        _S + `<path d="M11.5 20l4-16 4 8.5"/><path d="M3 20h18"/><path d="M13 15h4.5"/>` + _E,
    'azure-sql':
        _S + `<path d="M11.5 20l4-16 4 8.5"/><path d="M3 20h18"/><path d="M13 15h4.5"/>` + _E,
    'gcp-vm':
        _S + `<path d="M21.5 12.5a9.5 9.5 0 1 0 0 2"/><path d="M15 12.5h6.5v-3.5"/>` + _E,
    'gcp-sql':
        _S + `<path d="M21.5 12.5a9.5 9.5 0 1 0 0 2"/><path d="M15 12.5h6.5v-3.5"/>` + _E,
    'veeam-backup-microsoft-365':
        _S + `<rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><path d="M16 16a4 4 0 1 0 0 .01"/><path d="M19 16h-3v-3"/>` + _E,
    'kasten-k10':
        _S + `<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><circle cx="12" cy="12" r="3"/>` + _E,
};

const UNMANAGED_MODIFIER_DEFS = [
    {
        key: 'protection',
        label: 'Protection',
        subs: [
            { key: 'backup',          label: 'Backup',       affinityGroup: 'backup'       },
            { key: 'backupCopy',      label: 'Backup Copy',  affinityGroup: 'backup'       },
            { key: 'replication',     label: 'Replication',  affinityGroup: 'replication'  },
            { key: 'cdpReplication',  label: 'CDP',          affinityGroup: 'replication'  },
            { key: 'verification',    label: 'Verification', affinityGroup: 'verification' },
            { key: 'malware',         label: 'Malware',      affinityGroup: 'verification' },
        ],
    },
    {
        key: 'operations',
        label: 'Operations',
        subs: [
            { key: 'outOfBox',          label: 'Out-of-Box'        },
            { key: 'customReports',     label: 'Custom Reports'    },
            { key: 'customAlarms',      label: 'Custom Alarms'     },
            { key: 'customDashboards',  label: 'Custom Dashboards' },
        ],
    },
    {
        key: 'orchestration',
        label: 'Orchestration',
        subs: [
            { key: 'vro', label: 'VRO' },
        ],
    },
];

const VAULT_DRAWER_ICONS = {
    'aws-s3': `<svg width="22" height="22" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect width="36" height="36" rx="5" fill="#FF9900"/><text x="18" y="16" text-anchor="middle" dominant-baseline="middle" font-size="12" font-weight="700" font-family="Arial,sans-serif" fill="white" letter-spacing="-0.3">AWS</text><path d="M7 27Q18 33 29 27" stroke="white" stroke-width="2.8" fill="none" stroke-linecap="round"/><path d="M24 23.5L29 27 24 30.5" stroke="white" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    'azure-blob': `<svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.5 1L.5 14h5l3-8 3.5 8h5L9 1z" fill="#0078D4"/><path d="M.5 14l5.5-1.2 2.8 3.2z" fill="#50E6FF"/><path d="M9 1l3.5 12.8h4.5z" fill="#0050D0"/></svg>`,
    'onprem-block': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="13" rx="2"/><circle cx="12" cy="13.5" r="3.25"/><circle cx="12" cy="13.5" r="1.1" fill="currentColor" stroke="none"/><path d="M19 10h.01" stroke-width="2.5"/><path d="M2 11h4" stroke-width="1.4" opacity="0.5"/></svg>`,
    'onprem-s3': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="5.5" rx="1.5"/><rect x="2" y="9.25" width="20" height="5.5" rx="1.5"/><rect x="2" y="16.5" width="20" height="5.5" rx="1.5"/><line x1="6" y1="4.75" x2="6.01" y2="4.75"/><line x1="6" y1="12" x2="6.01" y2="12"/><line x1="6" y1="19.25" x2="6.01" y2="19.25"/></svg>`,
    'cloud-s3': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><line x1="9" y1="15" x2="9.01" y2="15" stroke-width="2.5"/><line x1="12" y1="15" x2="12.01" y2="15" stroke-width="2.5"/><line x1="15" y1="15" x2="15.01" y2="15" stroke-width="2.5"/></svg>`,
    'dedup-appliance': `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#b45309" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><ellipse cx="12" cy="5" rx="8" ry="2.5"/><path d="M4 5v5c0 1.38 3.58 2.5 8 2.5s8-1.12 8-2.5V5"/><path d="M4 10v5c0 1.38 3.58 2.5 8 2.5s8-1.12 8-2.5v-5"/><path d="M9 19.5l3 2 3-2" stroke-width="1.4"/><line x1="12" y1="17.5" x2="12" y2="21.5" stroke-width="1.4"/></svg>`,
    'default': `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a4 4 0 0 1 8 0v2"/><circle cx="12" cy="13" r="2"/><line x1="12" y1="15" x2="12" y2="17"/></svg>`
};

const VEEAM_SERVER_TYPES = {
    vbr:    { label: 'VBR',        fullLabel: 'Veeam Backup & Replication',     color: '#0078d4', hasSubComponents: true },
    vbfaws: { label: 'VBfAWS',     fullLabel: 'Veeam Backup for AWS',           color: '#FF9900', hasSubComponents: false, versions: ['v9', 'v9.1', 'v10'] },
    vbfgc:  { label: 'VBfGC',      fullLabel: 'Veeam Backup for Google Cloud',  color: '#4285F4', hasSubComponents: false, versions: ['v5', 'v6', 'v7'] },
    vbfaz:  { label: 'VBfAz',      fullLabel: 'Veeam Backup for Azure',         color: '#0078d4', hasSubComponents: false, versions: ['v7', 'v8', 'v8.1'] },
    vb365:  { label: 'VB365',      fullLabel: 'Veeam Backup for Microsoft 365', color: '#0078d4', hasSubComponents: false, versions: ['v6', 'v7', 'v8'] },
    kasten: { label: 'Kasten K10', fullLabel: 'Kasten K10',                     color: '#0078d4', hasSubComponents: false, versions: ['v8.5.6', 'v8.5.7', 'v8.5.8'] },
    vbem:   { label: 'VBEM',       fullLabel: 'Veeam Backup Enterprise Manager', color: '#0078d4', hasSubComponents: false },
    vone:   { label: 'Veeam ONE',  fullLabel: 'Veeam ONE',                      color: '#0078d4', hasSubComponents: false },
};

// lockedQtyFieldKeys imported from config.js (single source of truth, avoids circular dep with greenfield-sortables.js)

// EE field keys that belong to VB365 and VBfCloud product groups (for sectioned rendering)
const VB365_EE_KEYS    = new Set(['vb365ProxyServers', 'vb365RepositoryServers']);
const VBFCLOUD_EE_KEYS = new Set(['vbfCloudRepositoryServers']);
const VB365_SERVER_TYPES      = ['vb365'];
const VBFCLOUD_SERVER_TYPES   = ['vbfaws', 'vbfgc', 'vbfaz'];
const CONTAINERS_SERVER_TYPES = ['kasten'];

const repoMigrationSourceStorageOptions = [
    { value: '',               label: 'Select source storage type' },
    { value: 'azure-blob',     label: 'Azure BLOB' },
    { value: 'aws-s3',         label: 'AWS S3' },
    { value: 'onprem-s3',      label: 'S3-Compatible (on-premises)' },
    { value: 'smb-nfs',        label: 'SMB/NFS' },
    { value: 'cloud-s3',       label: 'Cloud S3-compatible (Wasabi, Backblaze, etc.)' },
    { value: 'onprem-block',   label: 'On-premises block' },
    { value: 'dedup-appliance', label: 'Deduplication Appliance' }
];

const repoMigrationRepositoryOptions = [
    { value: '',                        label: 'Select repository type' },
    { value: 'sobr-performance-tier',   label: 'SOBR - Performance Tier' },
    { value: 'sobr-capacity-tier',      label: 'SOBR Capacity Tier' },
    { value: 'stand-alone',             label: 'Stand-alone' }
];

let renderEstimateRafId = 0;



// ─── Extracted functions ─────────────────────────────────────────────────────

function getExistingEnvironmentFieldDependents(field) {
    return existingEnvironmentFieldSchema.filter((definition) => isExistingEnvironmentChildOf(definition, field));
}


function getExistingEnvironmentOrderedChildKeys(field) {
    const parentDefinition = getExistingEnvironmentFieldDefinition(field);
    const explicitChildren = Array.isArray(parentDefinition?.children) ? parentDefinition.children : [];
    const inferredChildren = existingEnvironmentFieldSchema
        .filter((definition) => isExistingEnvironmentChildOf(definition, field))
        .map((definition) => definition.key);

    return Array.from(new Set([...explicitChildren, ...inferredChildren]))
        .filter((key) => {
            const definition = getExistingEnvironmentFieldDefinition(key);
            return isExistingEnvironmentFieldAvailableForEngagement(definition);
        });
}


function normalizeExistingEnvironmentToggle(value) {
    if (typeof value === 'number') {
        return value > 0 ? 'yes' : 'no';
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();

        if (/^\d+$/.test(trimmedValue)) {
            return parseInt(trimmedValue, 10) > 0 ? 'yes' : 'no';
        }
    }

    if (value === true || value === 'true' || value === '1') {
        return 'yes';
    }

    if (value === false || value === 'false' || value === '0') {
        return 'no';
    }

    return sanitizeToggleState(value, 'no');
}


function hasExistingEnvironmentFieldValue(existingEnvironment = {}, field) {
    const definition = getExistingEnvironmentFieldDefinition(field);
    if (!definition) {
        return false;
    }

    return definition.kind === 'toggle'
        ? normalizeExistingEnvironmentToggle(existingEnvironment?.[field]) === 'yes'
        : normalizeEnvironmentCount(existingEnvironment?.[field]) > 0;
}


function clearExistingEnvironmentDependentFields(existingEnvironment = {}, field) {
    getExistingEnvironmentFieldDependents(field).forEach((definition) => {
        delete existingEnvironment[definition.key];
        clearExistingEnvironmentDependentFields(existingEnvironment, definition.key);
    });

    return existingEnvironment;
}


function enforceExistingEnvironmentDependencies(existingEnvironment = {}) {
    const normalizedExistingEnvironment = { ...existingEnvironment };

    existingEnvironmentFieldSchema.forEach((definition) => {
        const parentField = getExistingEnvironmentPrimaryParentField(definition);

        if (!parentField || hasExistingEnvironmentFieldValue(normalizedExistingEnvironment, parentField)) {
            return;
        }

        normalizedExistingEnvironment[definition.key] = definition.kind === 'toggle' ? 'no' : 0;
    });

    return normalizedExistingEnvironment;
}


function createEmptyExistingEnvironment() {
    return normalizeExistingEnvironment({});
}


function normalizeSiteForEngagement(site) {
    // Preserve existingEnvironment when either:
    //   (a) the site has named server instances (VB365, Kasten, etc.), OR
    //   (b) the incoming existingEnvironment already has non-zero values (e.g. VBR count chips
    //       restored from a JSON import — VBR is managed directly in existingEnvironment, not via veeamServers).
    const hasNamedServers = Array.isArray(site.veeamServers) && site.veeamServers.length > 0;
    const hasEEData = site.existingEnvironment != null && typeof site.existingEnvironment === 'object'
        && Object.values(site.existingEnvironment).some((v) => (typeof v === 'number' && v > 0) || v === 'yes');
    const preserveEE = hasNamedServers || hasEEData;
    return {
        ...site,
        existingEnvironment: preserveEE
            ? (site.existingEnvironment || createEmptyExistingEnvironment())
            : createEmptyExistingEnvironment(),
        // Preserve custom labels when EE is preserved; otherwise reset (new/empty sites).
        existingEnvironmentCustomLabels: preserveEE
            ? (site.existingEnvironmentCustomLabels || {})
            : {},
        workloads: normalizeSiteWorkloads(Array.isArray(site.workloads) ? site.workloads : [])
    };
}


function getExistingEnvironmentSelectableFieldOrder(existingEnvironment = {}) {
    const normalizedExistingEnvironment = normalizeExistingEnvironment(existingEnvironment);

    return [
        ...getExistingEnvironmentCountFieldOrder(normalizedExistingEnvironment)
    ];
}


function getAvailableExistingEnvironmentFields(existingEnvironment = {}) {
    const activeKeys = new Set(
        getExistingEnvironmentItems(existingEnvironment).map((item) => item.key)
    );

    return getExistingEnvironmentSelectableFieldOrder(existingEnvironment)
        .filter((key) => !activeKeys.has(key));
}


function createExistingEnvironmentSelectionState(existingEnvironment = {}, currentField = '') {
    const normalizedExistingEnvironment = normalizeExistingEnvironment(existingEnvironment);

    if (currentField && Object.hasOwn(normalizedExistingEnvironment, currentField)) {
        normalizedExistingEnvironment[currentField] = 0;
        clearExistingEnvironmentDependentFields(normalizedExistingEnvironment, currentField);
    }

    return enforceExistingEnvironmentDependencies(normalizedExistingEnvironment);
}


function getGfChipIcon(workloadValue) {
    // 1. Exact per-workload icon
    if (GF_SVG_WORKLOAD_ICONS[workloadValue]) return GF_SVG_WORKLOAD_ICONS[workloadValue];
    // 2. Vendor-prefix fallback for cloud variants not explicitly listed
    if (workloadValue.startsWith('aws-'))   return GF_SVG_WORKLOAD_ICONS['aws-ec2'];
    if (workloadValue.startsWith('azure-')) return GF_SVG_WORKLOAD_ICONS['azure-vm'];
    if (workloadValue.startsWith('gcp-'))   return GF_SVG_WORKLOAD_ICONS['gcp-vm'];
    // 3. Group-level fallback
    const group = getGfWorkloadGroup(workloadValue);
    return GF_SVG_ICONS[group] || GF_SVG_ICONS['Hypervisors'];
}


// Alias — same normalisation, EE-specific name kept for call-site clarity
const normalizeExistingEnvironmentCustomLabel = normalizeWorkloadCustomLabel;


function normalizeExistingEnvironmentCustomLabels(rawLabels = {}) {
    if (!rawLabels || typeof rawLabels !== 'object') {
        return {};
    }

    return Object.keys(existingEnvironmentFieldLabels).reduce((labels, key) => {
        const customLabel = normalizeExistingEnvironmentCustomLabel(rawLabels[key]);

        if (customLabel) {
            labels[key] = customLabel;
        }

        return labels;
    }, {});
}


function normalizeExistingEnvironment(rawEnvironment = {}) {
    const normalizedEnvironment = {
        vbrServers: normalizeToggleMigratedCount(rawEnvironment.vbrServers ?? rawEnvironment.vbr),
        vbemServers: normalizeToggleMigratedCount(rawEnvironment.vbemServers ?? rawEnvironment.vbem),
        veeamOneServers: normalizeToggleMigratedCount(rawEnvironment.veeamOneServers ?? rawEnvironment.vone),
        vroServers: normalizeToggleMigratedCount(rawEnvironment.vroServers ?? rawEnvironment.vro),
        proxyServersWindows: normalizeEnvironmentCount(rawEnvironment.proxyServersWindows),
        proxyServersLinux: normalizeEnvironmentCount(rawEnvironment.proxyServersLinux),
        repositoryServers: normalizeEnvironmentCount(rawEnvironment.repositoryServers ?? rawEnvironment.repositories),
        wanAccelerators: normalizeEnvironmentCount(rawEnvironment.wanAccelerators ?? rawEnvironment.wan),
        veeamAgents: normalizeEnvironmentCount(
            rawEnvironment.veeamAgents
                ?? rawEnvironment.agentCount
                ?? rawEnvironment.agents
        ),
        enterprisePlugins: normalizeEnvironmentCount(
            rawEnvironment.enterprisePlugins
                ?? rawEnvironment.pluginCount
                ?? rawEnvironment.plugins
        ),
        backupJobs: normalizeEnvironmentCount(
            rawEnvironment.backupJobs
                ?? rawEnvironment.backupAndBackupCopyJobs
        ),
        backupCopyJobs: normalizeEnvironmentCount(rawEnvironment.backupCopyJobs),
        sureBackupJobs: normalizeEnvironmentCount(rawEnvironment.sureBackupJobs ?? rawEnvironment.surebackupJobs),
        cdpPolicies: normalizeEnvironmentCount(
            rawEnvironment.cdpPolicies
                ?? rawEnvironment.cdpPolicyCount
                ?? rawEnvironment.cdpCount
                ?? rawEnvironment.cdp
        ),
        replicationJobs: normalizeEnvironmentCount(
            rawEnvironment.replicationJobs
                ?? rawEnvironment.replicaJobs
                ?? rawEnvironment.replicationJobCount
        ),
        virtualLab: normalizeEnvironmentCount(
            rawEnvironment.virtualLab
                ?? rawEnvironment.virtualLabs
        ),
        vb365ProxyServers: normalizeEnvironmentCount(rawEnvironment.vb365ProxyServers),
        vb365RepositoryServers: normalizeEnvironmentCount(rawEnvironment.vb365RepositoryServers),
        vbfCloudRepositoryServers: normalizeEnvironmentCount(rawEnvironment.vbfCloudRepositoryServers)
    };

    return enforceExistingEnvironmentDependencies(normalizedEnvironment);
}


function getExistingEnvironmentItems(existingEnvironment = {}, existingEnvironmentCustomLabels = {}) {
    const normalizedExistingEnvironment = normalizeExistingEnvironment(existingEnvironment);
    const normalizedCustomLabels = pruneExistingEnvironmentCustomLabels(
        existingEnvironmentCustomLabels,
        normalizedExistingEnvironment
    );
    const fieldOrder = [
        ...getExistingEnvironmentCountFieldOrder(normalizedExistingEnvironment)
    ];

    return fieldOrder
        .map((key) => {
            const definition = getExistingEnvironmentFieldDefinition(key);
            const customLabel = normalizeExistingEnvironmentCustomLabel(normalizedCustomLabels[key]);
            const baseLabel = existingEnvironmentFieldLabels[key];

            return {
                key,
                label: baseLabel,
                ...(customLabel ? { customLabel } : {}),
                displayLabel: customLabel || baseLabel,
                kind: definition?.kind || 'count',
                parentKey: getExistingEnvironmentPrimaryParentField(definition),
                hierarchyLevel: getExistingEnvironmentPrimaryParentField(definition) ? 1 : 0,
                enabled: false,
                count: normalizeEnvironmentCount(normalizedExistingEnvironment[key])
            };
        })
        .filter((item) => item.enabled || item.count > 0);
}


function getExistingEnvironmentSnapshotQuantity(sites = [], field) {
    if (!field) {
        return 0;
    }

    return sites.reduce((total, site) => {
        const existingEnvironment = normalizeExistingEnvironment(site?.existingEnvironment);

        return total + normalizeEnvironmentCount(existingEnvironment[field]);
    }, 0);
}



function hasExistingEnvironment(site) {
    const itemCount = Array.isArray(site?.existingEnvironment)
        ? site.existingEnvironment.filter((item) => item?.kind === 'toggle' ? item?.enabled : normalizeEnvironmentCount(item?.count) > 0).length
        : getExistingEnvironmentItems(site?.existingEnvironment).length;

    return itemCount > 0;
}


function pruneExistingEnvironmentCustomLabels(rawLabels = {}, existingEnvironment = {}) {
    const normalizedLabels = normalizeExistingEnvironmentCustomLabels(rawLabels);
    const normalizedExistingEnvironment = normalizeExistingEnvironment(existingEnvironment);
    const activeKeys = new Set(
        getExistingEnvironmentSelectableFieldOrder(normalizedExistingEnvironment)
            .filter((key) => hasExistingEnvironmentFieldValue(normalizedExistingEnvironment, key))
    );

    return Object.entries(normalizedLabels).reduce((labels, [key, value]) => {
        if (activeKeys.has(key)) {
            labels[key] = value;
        }

        return labels;
    }, {});
}


function getExistingEnvironmentDisplayItems(existingEnvironment = {}, existingEnvironmentCustomLabels = {}, displayOrder = null) {
    const items = getExistingEnvironmentItems(existingEnvironment, existingEnvironmentCustomLabels);
    const itemMap = new Map(items.map((item) => [item.key, item]));
    const orderedItems = [];

    function appendField(definition) {
        if (!definition) {
            return;
        }

        const item = itemMap.get(definition.key);
        if (item) {
            orderedItems.push(item);
        }

        getExistingEnvironmentOrderedChildKeys(definition.key).forEach((childKey) => {
            const childDefinition = getExistingEnvironmentFieldDefinition(childKey);

            if (!isExistingEnvironmentFieldAvailableForEngagement(childDefinition)) {
                return;
            }

            const childItem = itemMap.get(childKey);
            if (childItem) {
                orderedItems.push(childItem);
            }
        });
    }

    const topLevelDefs = existingEnvironmentFieldSchema.filter((definition) => !getExistingEnvironmentPrimaryParentField(definition));

    if (displayOrder && displayOrder.length > 0) {
        const orderMap = new Map(displayOrder.map((key, i) => [key, i]));
        const sorted = [...topLevelDefs].sort((a, b) => {
            const ai = orderMap.has(a.key) ? orderMap.get(a.key) : Infinity;
            const bi = orderMap.has(b.key) ? orderMap.get(b.key) : Infinity;
            return ai - bi;
        });
        sorted.forEach((definition) => appendField(definition));
    } else {
        topLevelDefs.forEach((definition) => appendField(definition));
    }

    return orderedItems;
}


function buildJobRowMarkup(job, siteId) {
    const typeDef     = getJobTypeDef(job.jobType) || jobTypeOptions[0];
    const hasCount    = typeDef.hasCount !== false;
    const count       = hasCount ? (job.count || 0) : 0;
    const wlDef       = hasCount ? getJobWorkloadDef(job.workloadType) : null;
    const wlUnit      = wlDef ? wlDef.unit : 'items';
    const wlLabel     = stripWorkloadUnitSuffix(wlDef ? wlDef.label : (job.workloadType || ''), wlUnit);
    const wlIcon      = hasCount ? (getGfChipIcon(job.workloadType) || '') : '';
    const qtyEmpty    = count === 0 ? ' is-empty' : '';
    const jobDisplayLabel = job.customLabel || (hasCount ? wlLabel : typeDef.label);

    // Agent version toggle — only shown when the workload is a physical (agent) type
    const versionOpts   = hasCount ? getAgentVersionOptions(job.workloadType) : null;
    const currentVer    = job.agentVersion || (versionOpts ? versionOpts[1] : null);
    const versionRowHtml = versionOpts
        ? `<span class="gf-chip-row-version" title="Current agent version">
            ${versionOpts.map((v) =>
                `<label class="gf-chip-version-opt"><input type="radio" name="job-agent-version-${job.id}" value="${escapeHtml(v)}"${currentVer === v ? ' checked' : ''} data-gf-job-agent-version="${job.id}" data-gf-job-site="${siteId}"><span>${escapeHtml(v)}</span></label>`
            ).join('')}
           </span>`
        : '';

    // ── Server tag chip ───────────────────────────────────────────────────────
    // When the job has a qty row (hasCount), the server tag is placed right-aligned
    // inside that row so the chip stays two rows tall. For no-count jobs there is no
    // qty row, so the tag falls back to its own standalone row.
    const linkedEntry = getLinkedServerForJob(job);
    const GF_SVG_LINK = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`;
    let serverTagInner;
    if (linkedEntry) {
        const { server } = linkedEntry;
        serverTagInner = `<span class="gf-job-server-tag gf-job-server-tag--linked">
                <button type="button" class="gf-job-server-tag-btn"
                        data-gf-job-server-tag="${job.id}"
                        data-gf-job-site="${siteId}"
                        title="Managed by ${escapeHtml(server.name)} — click to change">${GF_SVG_LINK
                }<span class="gf-job-server-tag-type">${escapeHtml(server.type.toUpperCase())}</span
                ><span class="gf-job-server-tag-label">${escapeHtml(server.name)}</span></button>
                <button type="button" class="gf-job-server-tag-unlink"
                        data-gf-job-server-unlink="${job.id}"
                        data-gf-job-site="${siteId}"
                        title="Unlink from server"
                        aria-label="Unlink from server">${GF_SVG_CLOSE}</button>
            </span>`;
    } else {
        serverTagInner = `<button type="button" class="gf-job-server-tag gf-job-server-tag--unlinked"
                    data-gf-job-server-tag="${job.id}"
                    data-gf-job-site="${siteId}"
                    title="Link to a Veeam server managing this job">${GF_SVG_LINK
            } Link server</button>`;
    }
    // Wrap for placement: inline in qty row when hasCount, standalone row otherwise
    const serverTagQty       = `<span class="gf-chip-qty-server">${serverTagInner}</span>`;
    const serverTagStandaloneHtml = !hasCount ? `<span class="gf-chip-row-server">${serverTagInner}</span>` : '';

    return `<span class="gf-chip gf-chip--job" data-gf-job-row="${job.id}" data-gf-jobs-site="${siteId}">
        <span class="gf-chip-row-name">
            <span class="gf-chip-grip" title="Drag to reorder">${GF_SVG_GRIP_SM}</span>
            ${hasCount ? `<button type="button" class="gf-chip-icon gf-chip-type-icon-btn"
                    data-gf-open-job-workload-picker="${job.id}"
                    data-gf-job-site="${siteId}"
                    title="Change workload type">${wlIcon}</button>` : ''}
            <span class="gf-chip-label" contenteditable="true" spellcheck="false"
                  data-gf-job-label="${job.id}"
                  data-chip-default-label="${escapeHtml(hasCount ? wlLabel : typeDef.label)}"
                  title="${escapeHtml(hasCount ? wlLabel : typeDef.label)}">${escapeHtml(jobDisplayLabel)}</span><span class="gf-chip-label-pencil" aria-hidden="true">${GF_SVG_PENCIL}</span>
            <button type="button" class="gf-job-type-badge"
                    data-gf-open-job-type-picker="${job.id}"
                    data-gf-job-site="${siteId}"
                    title="Change job type">${escapeHtml(typeDef.label)}</button>
            <button type="button" class="gf-chip-remove" data-gf-job-remove="${job.id}" data-gf-job-site="${siteId}" aria-label="Remove job">${GF_SVG_CLOSE}</button>
        </span>
        ${hasCount ? `<span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${qtyEmpty}" type="number" min="0" step="1"
                   style="width:${Math.max(1, count > 0 ? count.toString().length : 1)}ch"
                   value="${count > 0 ? count : ''}" placeholder="0"
                   data-gf-job-count="${job.id}" data-gf-job-site="${siteId}"
                   aria-label="Quantity">
            <span class="gf-chip-unit">${escapeHtml(wlUnit)}</span>${serverTagQty}
        </span>` : ''}
        ${versionRowHtml}
        ${serverTagStandaloneHtml}
    </span>`;
}


function buildUnprotectedWorkloadRowMarkup(workload, siteId) {
    const wlDef   = getJobWorkloadDef(workload.workloadType);
    const wlUnit  = wlDef ? wlDef.unit : 'items';
    const wlLabel = stripWorkloadUnitSuffix(wlDef ? wlDef.label : (workload.workloadType || 'Unknown'), wlUnit);
    const count   = workload.count || 0;
    const icon    = getGfChipIcon(workload.workloadType) || '';
    const qtyEmpty = count === 0 ? ' is-empty' : '';
    const displayLabel = workload.customLabel || wlLabel;
    return `<span class="gf-chip gf-chip--unprotected" data-gf-unprotected-row="${workload.id}" data-gf-unprotected-site="${siteId}">
        <span class="gf-chip-row-name">
            <span class="gf-chip-grip" title="Drag to reorder">${GF_SVG_GRIP_SM}</span>
            <button type="button" class="gf-chip-icon gf-chip-type-icon-btn"
                    data-gf-open-unprotected-picker="${workload.id}"
                    data-gf-unprotected-site="${siteId}"
                    title="Change workload type">${icon}</button>
            <span class="gf-chip-label" contenteditable="true" spellcheck="false"
                  data-gf-unprotected-label="${workload.id}"
                  data-chip-default-label="${escapeHtml(wlLabel)}"
                  title="${escapeHtml(wlLabel)}">${escapeHtml(displayLabel)}</span><span class="gf-chip-label-pencil" aria-hidden="true">${GF_SVG_PENCIL}</span>
            <button type="button" class="gf-chip-remove" data-gf-unprotected-remove="${workload.id}" data-gf-unprotected-site="${siteId}" aria-label="Remove ${escapeHtml(wlLabel)}">${GF_SVG_CLOSE}</button>
        </span>
        <span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${qtyEmpty}" type="number" min="0" step="1"
                   style="width:${Math.max(1, count > 0 ? count.toString().length : 1)}ch"
                   value="${count > 0 ? count : ''}" placeholder="0"
                   data-gf-unprotected-count="${workload.id}"
                   data-gf-unprotected-site="${siteId}"
                   aria-label="Quantity for ${escapeHtml(wlLabel)}">
            <span class="gf-chip-unit">${escapeHtml(wlUnit)}</span>
        </span>
    </span>`;
}


function getWorkloadLabel(value) {
    if (!hasSelectedWorkloadValue(value)) {
        return workloadPickerPlaceholderLabel;
    }

    if (isRepoMigrationWorkload(value)) {
        return 'Migration Data';
    }

    return sourceWorkloadLabels[value] || agentLabels[value] || value;
}


function getWorkloadEffortUnits(workload) {
    const scopeCount = getWorkloadScopeCount(workload);

    if (scopeCount === 0) {
        return 0;
    }

    if (isNamespaceScopedWorkload(workload?.value)) {
        return scopeCount * effortConfig.domainMetricEffort.effortPerNamespace;
    }

    if (isUserScopedWorkload(workload?.value)) {
        return Math.ceil(scopeCount / effortConfig.domainMetricEffort.usersPer1000Block)
            * effortConfig.domainMetricEffort.effortPer1000Users;
    }

    return scopeCount;
}


function getWorkloadScopeCount(workload) {
    if (!workload || typeof workload !== 'object') {
        return 0;
    }

    const normalizedWorkloadValue = normalizeWorkloadValue(workload.value);

    if (!hasSelectedWorkloadValue(normalizedWorkloadValue)) {
        return 0;
    }

    if (isInDevelopmentWorkloadValue(normalizedWorkloadValue)) {
        return 0;
    }

    if (isProtectedTbWorkload(normalizedWorkloadValue)) {
        return calculateUnstructuredDataInstances(workload.protectedTb ?? 0);
    }

    return Math.max(0, parseInt(workload.count, 10) || 0);
}


function hasMeaningfulWorkloadScope(workload) {
    return getWorkloadScopeCount(workload) > 0;
}


/**
 * Effort units for a Step 1 source workload.
 * VM-based workloads (hypervisors, agents, etc.) count one unit per machine.
 * NAS / unstructured-data workloads store TB, not machines — so they are weighted down via
 * nasEffortWeight (perShare + light per-TB component) so file/object storage does not count
 * 1:1 with VMs in the design, implementation, and discovery block tables.
 */
function sourceWorkloadEffortUnits(workloadType, count) {
    const n = Math.max(0, parseInt(count, 10) || 0);
    if (isUnstructuredDataWorkload(workloadType)) {
        return nasEffortWeight.perShare + Math.round(n * nasEffortWeight.tbPerUnit);
    }
    return n;
}


function getSiteDisplayName(site, index) {
    const explicitName = typeof site?.name === 'string' ? site.name.trim() : '';
    return explicitName || getDefaultSiteName(index);
}


function getRepoMigrationProtectedTb(site) {
    return getRepoMigrationWorkloads(site).reduce(
        (sum, workload) => sum + getRepoMigrationWorkloadProtectedTb(workload),
        0
    );
}


function isModifierActive(modState, modDef, workloadType) {
    if (!modState) return false;
    return modDef.subs.some((sub) => !isSubCellNA(sub.key, workloadType) && !!modState[sub.key]);
}


function buildExistingEnvGroupMarkup(site, jobs, veeamGroupContent, namedTypesCount, compItemsCount, vaultCount = 0, compIsEmpty = false) {
    const siteId     = site.id;
    const totalCount = jobs.length + namedTypesCount + compItemsCount + vaultCount;

    return `<div class="gf-existing-env-wrapper" data-gf-site="${siteId}">
        <span class="gf-band-tip" data-tip="Veeam Upgrade / Health Check"></span>
        <div class="gf-existing-env-hdr">
            ${gfGroupLabel('Existing Veeam Environment', totalCount)}
            <button type="button" class="gf-add-btn--compact" data-gf-open-comp="${siteId}" title="Add a Veeam component" aria-label="Add component">${GF_SVG_PLUS}&nbsp;Component</button>
        </div>
        <div class="gf-chip-group gf-chip-group--comp${compIsEmpty ? ' gf-chip-group--empty' : ''}" data-gf-site="${siteId}">${veeamGroupContent}</div>
    </div>`;
}


// ── Source workload chip builders ─────────────────────────────────────────────

/**
 * Builds the protection badge HTML for a source workload chip summary row.
 * Shows one icon-badge per job type present.
 * Shows an "unprotected" badge when there are no jobs.
 */
function buildSwpChipBadges(workload) {
    const jobs = Array.isArray(workload.jobs) ? workload.jobs : [];

    const hasBackup      = jobs.some((j) => j.type === 'backup'      || j.type === 'sureBackup');
    const hasReplication = jobs.some((j) => j.type === 'replication' || j.type === 'sureReplica');
    const hasCdp         = jobs.some((j) => j.type === 'cdp');
    const hasBackupCopy  = jobs.some((j) => j.type === 'backupCopy');
    const hasAny         = hasBackup || hasReplication || hasCdp || hasBackupCopy;

    if (!hasAny) {
        return `<span class="swp-chip-badge swp-chip-badge--unprot" title="Unprotected">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3L20 7v5c0 5.25-3.6 10.2-8 11.95C7.6 22.2 4 17.25 4 12V7l8-4z"/><path d="M12 10v3M12 16.5h.01"/></svg>
        </span>`;
    }

    const _badge = (cls, title, svgPath) =>
        `<span class="swp-chip-badge swp-chip-badge--${cls}" title="${title}">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${svgPath}</svg>
        </span>`;

    return [
        hasBackup      && _badge('backup',      'Backup',      '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>'),
        hasReplication && _badge('replication', 'Replication', '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>'),
        hasCdp         && _badge('cdp',         'CDP',         '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>'),
        hasBackupCopy  && _badge('backupCopy',  'Backup copy', '<rect x="2" y="4" width="13" height="13" rx="2"/><path d="M5 15.5v2a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-11a2 2 0 0 0-2-2h-2"/>'),
    ].filter(Boolean).join('');
}

/**
 * Builds the expandable detail panel for a chip — one row per job.
 * Shows total count + "Unprotected" when no jobs exist.
 */
function buildSwpChipDetail(workload) {
    const jobs      = Array.isArray(workload.jobs) ? workload.jobs : [];
    const totalNum  = parseInt(workload.totalCount, 10) || 0;
    const totalText = getWorkloadQuantitySummary(workload.workloadValue, totalNum);

    const _pill = (cls, label) =>
        `<span class="swp-detail-pill swp-detail-pill--${cls}">${escapeHtml(label)}</span>`;

    const _typeLabel = { backup: 'Backup', replication: 'Replication', cdp: 'CDP', backupCopy: 'Backup copy', sureBackup: 'SureBackup', sureReplica: 'SureReplica' };
    const _typeCls   = { backup: 'backup', replication: 'replication', cdp: 'cdp', backupCopy: 'backupCopy', sureBackup: 'sure', sureReplica: 'sure' };

    if (jobs.length === 0) {
        return `<div class="swp-chip-detail"><div class="swp-chip-detail-row">
            <span class="swp-detail-count${totalNum === 0 ? ' swp-detail-count--zero' : ''}">${escapeHtml(totalText)}</span>
            <span class="swp-detail-pills">
                ${_pill('unprot', 'Unprotected')}
                <button type="button" class="swp-add-job-link" data-swp-add-job aria-label="Add a job to this workload">+ Add job</button>
            </span>
        </div></div>`;
    }

    const rows = jobs.map((j) => {
        const countNum = parseInt(j.count, 10) || 0;
        const cls      = _typeCls[j.type] || j.type;
        const lbl      = _typeLabel[j.type] || j.type;
        return `<div class="swp-chip-detail-row">
            <span class="swp-detail-count${countNum === 0 ? ' swp-detail-count--zero' : ''}">${countNum}<span class="swp-detail-count-total"> / ${escapeHtml(totalText)}</span></span>
            <span class="swp-detail-pills">${_pill(cls, lbl)}</span>
        </div>`;
    }).join('');

    return `<div class="swp-chip-detail">${rows}</div>`;
}

/**
 * Builds a single source workload chip for the site card.
 * Clicking the chip body opens source-workload-pane.js.
 * The chevron button expands an inline detail panel without opening the pane.
 */
function buildSwpChipMarkup(workload, siteId) {
    const defaultLabel  = sourceWorkloadLabels[workload.workloadValue] || workload.workloadValue || 'Workload';
    const label         = (workload.label && workload.label.trim()) ? workload.label.trim() : defaultLabel;
    const totalCount    = parseInt(workload.totalCount, 10) || 0;
    const countText     = getWorkloadQuantitySummary(workload.workloadValue, totalCount);
    const badges        = buildSwpChipBadges(workload);
    const hasProtection = Array.isArray(workload.jobs) && workload.jobs.length > 0;
    const detail        = buildSwpChipDetail(workload);

    return `<div
        class="swp-chip"
        data-swp-chip
        ${totalCount === 0 ? 'data-count-zero' : ''}
        ${hasProtection ? 'data-protected' : ''}
        data-swp-site-id="${siteId}"
        data-swp-workload-id="${workload.id}"
        data-swp-default-label="${escapeHtml(defaultLabel)}"
        role="button"
        tabindex="0"
        aria-label="${escapeHtml(label)} — click to configure"
        title="${escapeHtml(label)}"
    >
        <div class="swp-chip-main">
            <span class="swp-chip-grip" title="Drag to reorder or move to another site">${GF_SVG_GRIP_SM}</span>
            <span class="swp-chip-icon" aria-hidden="true">${getGfChipIcon(workload.workloadValue)}</span>
            <span class="swp-chip-label">${escapeHtml(label)}</span>
            <span class="swp-chip-badges">${badges}</span>
            <button type="button"
                class="swp-chip-remove-btn"
                data-swp-remove="${workload.id}"
                data-swp-site-id="${siteId}"
                aria-label="Remove ${escapeHtml(label)}"
                title="Remove"
            >${GF_SVG_CLOSE}</button>
        </div>
        ${detail}
    </div>`;
}

/**
 * Builds the full source workload section for a site card.
 * Replaces the legacy unprotected workload + jobs canvas sections.
 */
function buildSourceWorkloadSectionMarkup(site, siteIndex = 0) {
    const siteId    = site.id;
    const workloads = Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : [];
    const chipsHtml = workloads.map((w) => buildSwpChipMarkup(w, siteId)).join('');

    const countPill = workloads.length > 0
        ? `<span class="gf-chip-group-count">${workloads.length}</span>` : '';

    return `<div class="swp-section" data-swp-section="${siteId}">
        <div class="swp-section-row">
            <span class="swp-section-row__label">Source workloads${countPill}</span>
            <button type="button"
                class="gf-add-btn--compact"
                data-add-source-workload="${siteId}"
                title="Add a source workload to this site"
                aria-label="Add source workload"
            >${GF_SVG_PLUS}&nbsp;Workload</button>
        </div>
        ${workloads.length === 0 && siteIndex > 0
            ? `<div class="swp-chip-list" data-gf-site="${siteId}"></div>`
            : `<div class="swp-chip-list${workloads.length === 0 ? ' swp-chip-list--empty' : ''}" data-gf-site="${siteId}">${
                workloads.length === 0
                    ? `<span class="swp-empty-hint">No workloads added — click <strong>+ Workload</strong> to describe what's running at this site today.</span>`
                    : chipsHtml
              }</div>`
        }
    </div>`;
}

function buildGfSiteCardMarkup(site, index) {
    const displayName = getSiteDisplayName(site, index);
    const vaultWorkloads   = site.workloads.filter((w) => w.value && isRepoMigrationWorkload(w.value));
    const veeamServers     = Array.isArray(site.veeamServers) ? site.veeamServers : [];
    const compItems        = getCanvasCompItems(site);
    const sourceWorkloads  = Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : [];

    // Non-VBR server types render as grouped count chips (one per type)
    const namedServers     = veeamServers.filter((s) => s.type !== 'vbr');
    const uniqueNamedTypes = [...new Set(namedServers.map((s) => s.type))];
    const namedServerCount = namedServers.length;
    const componentCount   = namedServerCount + compItems.length;
    const totalTiles       = sourceWorkloads.length + vaultWorkloads.length + namedServerCount + compItems.length;
    const canRemove        = sites.length > 1;

    // Per-site category pills — zero-count pills are hidden
    const _spSvg  = (path) => `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
    const _spPill = (n, sing, plur, svgPath, type) => n === 0 ? '' :
        `<span class="gf-site-pill gf-site-pill--${type}">${_spSvg(svgPath)}<strong>${n}</strong> ${n === 1 ? sing : plur}</span>`;
    const pillsHtml = [
        _spPill(sourceWorkloads.length, 'workload', 'workloads',
            '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>', 'job'),
        _spPill(vaultWorkloads.length, 'repo', 'repos',
            '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v4c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 9v6c0 1.66 4.03 3 9 3s9-1.34 9-3V9"/>', 'repo'),
        _spPill(componentCount, 'component', 'components',
            '<rect x="3" y="5" width="18" height="6" rx="1.5"/><rect x="3" y="13" width="18" height="6" rx="1.5"/><path d="M7 8h.01M7 16h.01"/>', 'component'),
    ].filter(Boolean).join('');

    const vaultChipsHtml  = vaultWorkloads.map((w) => buildGfVaultChipMarkup(w, site.id)).join('');

    // Pass empty jobs array — jobs are replaced by sourceWorkloads (Phase 3+).
    // Legacy job chips are no longer rendered in the site card.
    const veeamGroupContent = buildSectionedVeeamGroupContent(site, site.id, compItems, vaultChipsHtml, []);
    const compIsEmpty       = !compItems.length && !uniqueNamedTypes.length && !vaultWorkloads.length;
    const zoneEmpty         = totalTiles === 0 ? ' is-empty' : '';

    const groupsHtml = [
        buildSourceWorkloadSectionMarkup(site, index),
        buildExistingEnvGroupMarkup(site, [], veeamGroupContent, uniqueNamedTypes.length, compItems.length, vaultWorkloads.length, compIsEmpty),
    ].join('');

    return `<div class="gf-site" id="gf-site-${site.id}" data-site-id="${site.id}">
        <div class="gf-site-hdr">
            <span class="gf-site-grip" title="Drag to reorder site">${GF_SVG_GRIP_LG}</span>
            <span class="gf-site-name"
                  contenteditable="true"
                  data-gf-site-name="${site.id}"
                  role="textbox"
                  spellcheck="false"
                  aria-label="Site name">${escapeHtml(displayName)}</span>
            <span class="gf-site-name-pencil" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg></span>
            <span class="gf-site-spacer"></span>
            <div class="gf-site-pills" id="gf-cnt-${site.id}">${pillsHtml}</div>
            <button type="button" class="gf-site-action-btn" data-gf-clone="${site.id}" title="Duplicate this site" aria-label="Clone site">
                ${GF_SVG_CLONE}
            </button>
            <button type="button" class="gf-site-action-btn gf-site-remove" data-gf-remove="${site.id}"
                    ${canRemove ? '' : 'disabled'} title="Remove this site" aria-label="Remove site">
                ${GF_SVG_CLOSE}
            </button>
        </div>
        <div class="gf-site-body">
            <div class="gf-wl-zone${zoneEmpty}" id="gf-wl-${site.id}" data-gf-site="${site.id}">${groupsHtml}</div>
        </div>
    </div>`;
}


// Short display labels for VBR repo source storage type badges (chip tag)
const VBR_REPO_SOURCE_SHORT = {
    'azure-blob':      'BLOB',
    'aws-s3':          'AWS S3',
    'onprem-s3':       'S3-Compat.',
    'smb-nfs':         'SMB/NFS',
    'cloud-s3':        'Cloud S3',
    'onprem-block':    'Block',
    'dedup-appliance': 'Dedup',
};

// Short display labels for VBR repo type badges (chip tag — stacked below source badge)
const VBR_REPO_TYPE_SHORT = {
    'sobr-performance-tier': 'SOBR–Perf',
    'sobr-capacity-tier':    'SOBR–Cap',
    'standalone':            'Standalone',
};

function buildGfVaultChipMarkup(workload, siteId) {
    const sourceType = workload.vdcVaultSourceStorageType || '';
    const repoType   = workload.vdcVaultRepositoryType   || '';
    // Repository components (added via + Add Component) fall back to 'Repository';
    // legacy standalone vault entries fall back to 'Vault Migration'.
    const fallbackLabel = workload.isRepositoryComponent ? 'Repository' : 'Vault Migration';

    const rawSourceLabel = (sourceType && getRepoMigrationPickerOptionLabel(repoMigrationSourceStorageOptions, sourceType))
        || fallbackLabel;
    const sourceLabel = workload.isRepositoryComponent ? 'VBR Repository' : rawSourceLabel;
    const repoLabel   = repoType
        ? getRepoMigrationPickerOptionLabel(repoMigrationRepositoryOptions, repoType)
        : null;

    const iconSvg  = VAULT_DRAWER_ICONS[sourceType] || VAULT_DRAWER_ICONS['default'];
    const tb       = typeof workload.protectedTb === 'number' ? workload.protectedTb : 0;
    const qtyEmpty = tb === 0 ? ' is-empty' : '';
    const tbWidth  = Math.max(2, formatProtectedTbValue(tb).toString().length);

    const sourceStorageLabel = sourceType ? getRepoMigrationPickerOptionLabel(repoMigrationSourceStorageOptions, sourceType) : '';
    // Repository components show source type as a clickable badge in the chip header — skip the meta row.
    const sourceStorageRow = (!workload.isRepositoryComponent && sourceStorageLabel)
        ? `<span class="gf-chip-meta-row">
                    <span class="gf-chip-meta-key">Source</span>
                    <span class="gf-chip-meta-val">${escapeHtml(sourceStorageLabel)}</span>
                </span>`
        : '';
    // Repository components show repo type as a stacked badge in the chip header — skip the meta row.
    const repoRow = (!workload.isRepositoryComponent && repoLabel)
        ? `<span class="gf-chip-meta-row">
                    <span class="gf-chip-meta-key">Repo</span>
                    <span class="gf-chip-meta-val">${escapeHtml(repoLabel)}</span>
                </span>`
        : '';
    const metaHtml = (sourceStorageRow || repoRow)
        ? `<span class="gf-chip-meta">
                ${sourceStorageRow}${repoRow}
            </span>`
        : '';

    // Pre-compute badge HTML so the template stays readable.
    // All badges (source type + optional repo type) go into the qty row, right-aligned,
    // so the name row stays clean and there is no need for a CSS grid spanning trick.
    let qtyRowBadges = '';
    if (workload.isRepositoryComponent && sourceType) {
        const srcShort   = VBR_REPO_SOURCE_SHORT[sourceType] || sourceType;
        const srcAria    = `Source: ${rawSourceLabel} — click to edit`;
        const sourceBadge = `<button type="button" class="gf-chip-version-badge gf-chip-repo-type-badge"
                        data-gf-vbr-repo-source-badge
                        data-gf-chip="${workload.rowId}"
                        data-gf-chip-site="${siteId}"
                        aria-label="${escapeHtml(srcAria)}"
                        title="${escapeHtml(srcAria)}"
                    >${escapeHtml(srcShort)}</button>`;
        const repoTypeShort = repoType ? (VBR_REPO_TYPE_SHORT[repoType] || repoType) : '';
        const repoBadge = repoTypeShort
            ? `<span class="gf-chip-version-badge gf-chip-repo-badge"
                            title="${escapeHtml(repoLabel || repoTypeShort)}"
                            aria-label="Repo type: ${escapeHtml(repoLabel || repoTypeShort)}"
                        >${escapeHtml(repoTypeShort)}</span>`
            : '';
        qtyRowBadges = `<span class="gf-chip-qty-badges">${sourceBadge}${repoBadge}</span>`;
    }

    return `<span class="gf-chip gf-chip--vault"
                  data-gf-chip="${workload.rowId}"
                  data-gf-chip-value="${escapeHtml(workload.value)}">
        <span class="gf-chip-row-name">
            <span class="gf-chip-grip" title="Drag to reorder">${GF_SVG_GRIP_SM}</span>
            <span class="gf-chip-icon" aria-hidden="true">${iconSvg}</span>
            <span class="gf-chip-label" contenteditable="true" spellcheck="false"
                  data-gf-chip-inline-label="${workload.rowId}"
                  data-chip-default-label="${escapeHtml(sourceLabel)}"
                  title="${escapeHtml(sourceLabel)}"
            >${escapeHtml(workload.customLabel || sourceLabel)}</span><span class="gf-chip-label-pencil" aria-hidden="true">${GF_SVG_PENCIL}</span>
        </span>
        <span class="gf-chip-row-qty">
            <span class="gf-chip-x-symbol" aria-hidden="true">×</span>
            <input class="gf-chip-qty${qtyEmpty}"
                   type="number"
                   min="0"
                   step="0.5"
                   style="width:${tbWidth}ch"
                   value="${tb > 0 ? formatProtectedTbValue(tb) : ''}"
                   placeholder="0"
                   data-gf-chip-qty="${workload.rowId}"
                   aria-label="Data to migrate in TB for ${escapeHtml(sourceLabel)}">
            <span class="gf-chip-unit">TB</span>${qtyRowBadges}
        </span>
        ${metaHtml}
        <button type="button" class="gf-chip-remove gf-chip-remove--corner"
                data-gf-chip-remove="${workload.rowId}"
                aria-label="Remove ${escapeHtml(sourceLabel)}">${GF_SVG_CLOSE}</button>
    </span>`;
}


function buildNamedServerChipsMarkup(site, siteId, typeFilter = null) {
    const servers = Array.isArray(site.veeamServers)
        ? site.veeamServers.filter((s) => s.type !== 'vbr' && (!typeFilter || typeFilter.includes(s.type)))
        : [];
    const countByType = {};
    servers.forEach((s) => { countByType[s.type] = (countByType[s.type] || 0) + 1; });
    const serverVersions = site.existingEnvironmentServerVersions || {};
    const customLabels = site.existingEnvironmentCustomLabels || {};
    return Object.entries(countByType).map(([typeKey, count]) => {
        const typeDef = VEEAM_SERVER_TYPES[typeKey];
        if (!typeDef) return '';
        return buildVeeamServerTypeChipMarkup(typeKey, typeDef, count, siteId, serverVersions[typeKey] || null, customLabels[typeKey] || '');
    }).join('');
}


/**
 * Build the veeam-group content HTML with chips sectioned by product (VBR → Backup for Cloud → Containers → VB365).
 * A product section label is injected between groups whenever more than one product is present.
 * The label element is not a .gf-chip so SortableJS leaves it in place during drags.
 * vaultChipsHtml — pre-built vault migration chip HTML; placed inside the VBR section so it
 *   doesn't bleed into the other product sections.
 * jobs — raw job array; classified by workloadType so each job is embedded inside its owning
 *   product section (VBR, VB365, Backup for Cloud, or Containers).  Each product section
 *   gets its own .gf-chip-group--jobs container.  The VBR jobs container is always rendered
 *   even when empty so SortableJS always has a drop target there.  Per-product containers
 *   for VB365 / VBfCloud / Containers are only rendered when jobs of that type exist.
 *   data-gf-has-product-comps="1" drives the CSS divider between component and job chips.
 */
function buildSectionedVeeamGroupContent(site, siteId, compItems, vaultChipsHtml = '', jobs = []) {
    // Split EE comp items by product group
    const vbrItems      = compItems.filter((item) => !VB365_EE_KEYS.has(item.key) && !VBFCLOUD_EE_KEYS.has(item.key));
    const vb365Items    = compItems.filter((item) => VB365_EE_KEYS.has(item.key));
    const vbfCloudItems = compItems.filter((item) => VBFCLOUD_EE_KEYS.has(item.key));

    const vbrChipsHtml      = vbrItems.map((item) => buildGfCompChipMarkup(item, siteId)).join('');
    const vb365ChipsHtml    = vb365Items.map((item) => buildGfCompChipMarkup(item, siteId)).join('');
    const vbfCloudChipsHtml = vbfCloudItems.map((item) => buildGfCompChipMarkup(item, siteId)).join('');

    // Named server chips split by product type (VBR named servers are handled separately and excluded here)
    const vb365ServerHtml       = buildNamedServerChipsMarkup(site, siteId, VB365_SERVER_TYPES);
    const vbfCloudServerHtml    = buildNamedServerChipsMarkup(site, siteId, VBFCLOUD_SERVER_TYPES);
    const containersServerHtml  = buildNamedServerChipsMarkup(site, siteId, CONTAINERS_SERVER_TYPES);

    // Vault migration chips are VBR-related repository entries — keep them in the VBR section.
    const vbrContent        = vbrChipsHtml + (vaultChipsHtml || '');
    const vb365Content      = vb365ServerHtml + vb365ChipsHtml;
    const vbfCloudContent   = vbfCloudServerHtml + vbfCloudChipsHtml;
    const containersContent = containersServerHtml;

    const hasVbr        = !!vbrContent;
    const hasVb365      = !!vb365Content;
    const hasVbfCloud   = !!vbfCloudContent;
    const hasContainers = !!containersContent;

    // Classify jobs by the product that manages the workload type
    const vbrJobs        = jobs.filter((j) => !isVb365ManagedWorkload(j.workloadType) && !isVbfCloudManagedWorkload(j.workloadType) && !isNamespaceScopedWorkload(j.workloadType));
    const vb365Jobs      = jobs.filter((j) => isVb365ManagedWorkload(j.workloadType));
    const vbfCloudJobs   = jobs.filter((j) => isVbfCloudManagedWorkload(j.workloadType));
    const containerJobs  = jobs.filter((j) => isNamespaceScopedWorkload(j.workloadType));

    // Helper — builds a jobs container for a product section.
    // Returns '' (no element) for optional product sections when there are no jobs,
    // so no empty box appears. VBR is the exception — its container is always rendered
    // (even empty) so SortableJS always has a drop target for unprotected→job promotions.
    const buildJobsContainer = (productJobs, hasProductComps, alwaysRender = false) => {
        const jobsHtml = productJobs.map((j) => buildJobRowMarkup(j, siteId)).join('');
        if (!jobsHtml && !alwaysRender) return '';
        return `<div class="gf-chip-group gf-chip-group--jobs${!jobsHtml ? ' gf-chip-group--empty' : ''}" data-gf-has-product-comps="${hasProductComps ? '1' : '0'}" data-gf-site="${siteId}">${jobsHtml}</div>`;
    };

    // Only show product-group labels when 2+ groups have content — a lone label
    // separates nothing and just adds visual noise.
    const activeSectionCount = [
        hasVbr        || vbrJobs.length,
        hasVbfCloud   || vbfCloudJobs.length,
        hasContainers || containerJobs.length,
        hasVb365      || vb365Jobs.length,
    ].filter(Boolean).length;
    const showLabels = activeSectionCount > 1;

    // Section order: VBR → Backup for Cloud → Containers → VB365
    let html = '';

    if (hasVbr || vbrJobs.length) {
        if (showLabels) html += `<span class="gf-ee-section-label">VBR</span>`;
        html += vbrContent;
    }
    html += buildJobsContainer(vbrJobs, hasVbr, /* alwaysRender */ true);

    if (hasVbfCloud || vbfCloudJobs.length) {
        if (showLabels) html += `<span class="gf-ee-section-label">Backup for Cloud</span>`;
        html += vbfCloudContent;
        html += buildJobsContainer(vbfCloudJobs, hasVbfCloud);
    }

    if (hasContainers || containerJobs.length) {
        if (showLabels) html += `<span class="gf-ee-section-label">Containers</span>`;
        html += containersContent;
        html += buildJobsContainer(containerJobs, hasContainers);
    }

    if (hasVb365 || vb365Jobs.length) {
        if (showLabels) html += `<span class="gf-ee-section-label">VB365</span>`;
        html += vb365Content;
        html += buildJobsContainer(vb365Jobs, hasVb365);
    }

    return html;
}


function buildUnprotectedWorkloadListMarkup(site, workloads) {
    const siteId = site.id;
    const isEmpty = workloads.length === 0;
    const chipsHtml = workloads.map((w) => buildUnprotectedWorkloadRowMarkup(w, siteId)).join('');
    const emptyClass = isEmpty ? ' gf-chip-group--empty' : '';
    return `<div class="gf-chip-group gf-chip-group--unprotected${emptyClass}" data-gf-site="${siteId}">
        <span class="gf-band-tip" data-tip="Greenfield"></span>
        <div class="gf-chip-group-hdr">
            ${gfGroupLabel('Unprotected Workloads', workloads.length)}
            <button type="button" class="gf-add-unprotected-btn gf-add-btn--compact" data-gf-add-unprotected="${siteId}" title="Add a currently unprotected workload to this site">${GF_SVG_PLUS}&nbsp;Workload</button>
        </div>
        ${chipsHtml}
    </div>`;
}


function getExistingEnvironmentSummary(existingEnvironment = {}, existingEnvironmentCustomLabels = {}) {
    const items = Array.isArray(existingEnvironment)
        ? existingEnvironment
        : getExistingEnvironmentDisplayItems(existingEnvironment, existingEnvironmentCustomLabels);
    const summary = items.map((item) => `${item.displayLabel || item.label}: ${item.count}`);

    return summary.length ? summary.join(' • ') : 'No existing Veeam components defined';
}



function getSiteScopeSummaryText(engagementType) {
    return engagementType === 'vdc-vault'
        ? 'Every site has migration data'
        : 'Every site has scoped workloads';
}


function getRepoMigrationScopedItemCount(siteCollection = sites) {
    return siteCollection.reduce(
        (sum, site) => sum + getRepoMigrationWorkloads(site).filter((workload) => hasMeaningfulWorkloadScope(workload)).length,
        0
    );
}


function getRepoMigrationTotalProtectedTb(siteCollection = sites) {
    return siteCollection.reduce((sum, site) => sum + getRepoMigrationProtectedTb(site), 0);
}


function hasExistingEnvironmentInAnySite(siteCollection = sites) {
    return siteCollection.some((site) => hasExistingEnvironment(site));
}


function normalizeArchitectureState(rawState = {}, siteCount = sites.length) {
    const source = rawState && typeof rawState === 'object' ? rawState : {};
    const showSiteTopology = shouldShowArchitectureSiteTopology(siteCount); // kept for guard-checks elsewhere
    // Unified canvas: Veeam Version row removed; always 'v13'.
    const veeamVersion = 'v13';
    const osPlatform = Object.hasOwn(architectureOptionLabels.osPlatform, source.osPlatform)
        ? source.osPlatform
        : 'linux';
    const deploymentType = Object.hasOwn(architectureOptionLabels.deploymentType, source.deploymentType)
        ? source.deploymentType
        : 'virtual';
    const remoteConfigurationDatabase = Object.hasOwn(architectureOptionLabels.remoteConfigurationDatabase, source.remoteConfigurationDatabase)
        ? source.remoteConfigurationDatabase
        : 'yes';
    const vbem = Object.hasOwn(architectureOptionLabels.vbem, source.vbem)
        ? source.vbem
        : 'yes';
    const vbrHa = osPlatform === 'linux' && Object.hasOwn(architectureOptionLabels.vbrHa, source.vbrHa)
        ? source.vbrHa
        : 'no';
    const databaseType = Object.hasOwn(architectureOptionLabels.databaseType, source.databaseType)
        ? source.databaseType
        : 'postgresql';
    const remoteDb = Object.hasOwn(architectureOptionLabels.remoteDb, source.remoteDb)
        ? source.remoteDb
        : 'no';
    const sqlAoag = Object.hasOwn(architectureOptionLabels.sqlAoag, source.sqlAoag)
        ? source.sqlAoag
        : 'no';
    const advancedRbac = Object.hasOwn(architectureOptionLabels.advancedRbac, source.advancedRbac)
        ? source.advancedRbac
        : 'no';
    const siteTopology = inferSiteTopology(siteCount);
    const privateNetworking = source.privateNetworking === 'yes' ? 'yes' : 'no';

    return {
        veeamVersion,
        osPlatform,
        deploymentType,
        vbrHa,
        isDistributed: false,
        databaseType,
        remoteConfigurationDatabase,
        networkConstraints: 'no',
        vbem,
        remoteDb,
        sqlAoag,
        advancedRbac,
        siteTopology,
        privateNetworking
    };
}


function buildArchitectureStateSnapshot() {
    return {
        veeamVersion: architectureState.veeamVersion,
        osPlatform: architectureState.osPlatform,
        deploymentType: architectureState.deploymentType,
        vbrHa: architectureState.vbrHa,
        isDistributed: false,
        databaseType: architectureState.databaseType,
        remoteConfigurationDatabase: architectureState.remoteConfigurationDatabase,
        networkConstraints: 'no',
        vbem: architectureState.vbem,
        remoteDb: architectureState.remoteDb,
        sqlAoag: architectureState.sqlAoag,
        advancedRbac: architectureState.advancedRbac,
        siteTopology: inferSiteTopology(),
        privateNetworking: hasCloudWorkloads() ? architectureState.privateNetworking : 'no'
    };
}


function getSelectedVeeamOneOptions() {
    return Object.keys(veeamOneOptionLabels)
    .filter((option) => toggleValue(document.getElementById(`veeam-one-${option}`)) === 'yes');
}


function getScopeMetricMetaText(engagementType) {
    return engagementType === 'vdc-vault'
        ? 'Backup data written across all migration sites'
        : 'Scoped workload quantities across all sites';
}


function getSiteScopeMetricLabel(engagementType) {
    return engagementType === 'vdc-vault'
        ? 'backup data written'
        : 'scoped quantity';
}


function getSiteScopeMetricValue(siteSummary, engagementType) {
    return engagementType === 'vdc-vault'
        ? getProtectedTbSummary(siteSummary.workloads.reduce((sum, workload) => sum + normalizeProtectedTb(workload.protectedTb), 0))
        : String(siteSummary.totalWorkloadCount);
}


function getCanvasCompItems(site) {
    const allItems = getExistingEnvironmentItems(
        site.existingEnvironment || {},
        site.existingEnvironmentCustomLabels || {}
    );
    // Expand locked-qty items (VBR, VRO, VONE, VB365 repos) into one chip per slot, each with its own badge.
    const vbrVersions      = site.existingEnvironmentVbrVersions      || {};
    const vbrPlatforms     = site.existingEnvironmentVbrPlatforms     || {};
    const vbrHaRoles       = site.existingEnvironmentVbrHaRoles       || {};
    const vbrHaPairIds     = site.existingEnvironmentVbrHaPairIds     || {};
    const vroVersions      = site.existingEnvironmentVroVersions      || {};
    const voneVersions     = site.existingEnvironmentVoneVersions     || {};
    const vb365RepoTypes   = site.existingEnvironmentVb365RepoTypes   || {};
    const vb365RepoConsumedTbs = site.existingEnvironmentVb365RepoConsumedTbs || {};
    return allItems.flatMap((item) => {
        if (lockedQtyFieldKeys.has(item.key) && item.count >= 1) {
            // VB365 repository servers: each slot gets its own chip (storage type + consumed TB are per-slot).
            if (item.key === 'vb365RepositoryServers') {
                return Array.from({ length: item.count }, (_, slot) => ({
                    ...item,
                    count: 1,
                    slotIndex: slot,
                    lockedQtySlot: slot, // marks chip as locked-qty (no editable input)
                    sourceRepoType:   vb365RepoTypes[slot]      || 'block',
                    sourceConsumedTb: vb365RepoConsumedTbs[slot] ?? 50,
                }));
            }

            // VBR: each instance gets its own chip — one chip per slot, preserving VSA HA pairing data.
            if (item.key === 'vbrServers') {
                return Array.from({ length: item.count }, (_, slot) => {
                    const ver    = vbrVersions[slot]  || null;
                    const plat   = vbrPlatforms[slot] || null;
                    const haRole = vbrHaRoles[slot]   || null;
                    const pairId = vbrHaPairIds[slot] || null;
                    let displayLabel = item.displayLabel;
                    if (plat === 'linux') {
                        if (haRole === 'primary')        displayLabel = 'VBR VSA HA (primary node)';
                        else if (haRole === 'secondary') displayLabel = 'VBR VSA HA (secondary node)';
                        else                             displayLabel = 'VBR VSA';
                    } else {
                        displayLabel = 'VBR Windows';
                    }
                    return {
                        ...item,
                        displayLabel,
                        count: 1,
                        slotIndex: slot,
                        lockedQtySlot: slot, // marks chip as locked-qty; slot index used for precise removal
                        sourceVersion: ver,
                        sourcePlatform: plat,
                        sourceHaRole: haRole,
                        sourcePairId: pairId,
                    };
                });
            }

            // VRO / VONE: group slots by version — same version → one chip showing the count.
            const groups = new Map();
            for (let slot = 0; slot < item.count; slot++) {
                let ver = null;
                if (item.key === 'vroServers') {
                    ver = vroVersions[slot] || null;
                } else if (item.key === 'veeamOneServers') {
                    ver = voneVersions[slot] || null;
                }
                const key = ver ?? '__none__';
                if (!groups.has(key)) groups.set(key, { sourceVersion: ver, count: 0 });
                groups.get(key).count++;
            }
            return Array.from(groups.values()).map(({ sourceVersion, count }) => ({
                ...item,
                count,
                lockedQtySlot: 0,
                sourceVersion,
                sourcePlatform: null,
                sourceHaRole: null,
                sourcePairId: null,
            }));
        }
        return [item];
    });
}


function collectUpgradeVbrRows() {
    const rows = [];
    for (let siteIdx = 0; siteIdx < sites.length; siteIdx++) {
        const site      = sites[siteIdx];
        const siteLabel = getSiteDisplayName(site, siteIdx);
        const namedVbrs = Array.isArray(site.veeamServers)
            ? site.veeamServers.filter((s) => s.type === 'vbr')
            : [];
        if (namedVbrs.length > 0) {
            namedVbrs.forEach((srv) => {
                rows.push({
                    id:        srv.instanceId,
                    name:      srv.name,
                    siteLabel
                });
            });
        } else {
            const legacyCount  = normalizeToggleMigratedCount(site.existingEnvironment?.vbrServers) || 0;
            const legacyHaRoles = site.existingEnvironmentVbrHaRoles || {};
            for (let i = 0; i < legacyCount; i++) {
                const instanceId = `env-vbr-${site.id}-${i}`;
                const haRole   = legacyHaRoles[i] || null;
                let name;
                if (haRole === 'primary')        name = 'VBR VSA HA (primary node)';
                else if (haRole === 'secondary') name = 'VBR VSA HA (secondary node)';
                else                             name = legacyCount > 1 ? `VBR-${String(i + 1).padStart(2, '0')}` : 'VBR';
                rows.push({
                    id:        instanceId,
                    name,
                    siteLabel
                });
            }
        }
    }
    return rows;
}


function buildEffortScopeMetrics(aggregatedWorkloads) {
    // Unified canvas: isExistingEnvironmentInScope() always false; always uses workload metrics.

    return {
        totalEffortUnits: aggregatedWorkloads.totalEffortUnits,
        selectedScopeCount: aggregatedWorkloads.selectedWorkloadCount,
        proxyCount: 0,
        repositoryCount: 0
    };
}


function buildAggregatedWorkloadData(
    siteCollection = sites,
    options = {}
) {
    const { excludeInactiveWorkloads = false } = options;
    const hypervisorTotals = new Map();
    const cloudTotals = new Map();
    const saasTotals = new Map();
    const containerTotals = new Map();
    const pluginTotals = new Map();
    const unstructuredDataTotals = new Map();
    const agentTotals = new Map();
    // Dispatch table — maps label-set → summary array key, totals map, item id key,
    // and optional count key (plugins use 'quantity'; all others use 'count').
    const _wlCats = [
        { labels: hypervisorLabels,   summaryKey: 'hypervisors',        totalsMap: hypervisorTotals, idKey: 'hypervisor' },
        { labels: cloudLabels,        summaryKey: 'cloudWorkloads',     totalsMap: cloudTotals,      idKey: 'workload'   },
        { labels: saasLabels,         summaryKey: 'saasWorkloads',      totalsMap: saasTotals,       idKey: 'workload'   },
        { labels: containerLabels,    summaryKey: 'containerWorkloads', totalsMap: containerTotals,  idKey: 'workload'   },
        { labels: pluginLabels,       summaryKey: 'plugins',            totalsMap: pluginTotals,     idKey: 'plugin', countKey: 'quantity' },
        { labels: agentLabels,        summaryKey: 'agents',             totalsMap: agentTotals,      idKey: 'agent'      },
    ];
    const siteSummaries = siteCollection.map((site, index) => {
        // Existing environment is captured as canvas chips (components), not a separate include flag.
        const existingEnvironmentState = createEmptyExistingEnvironment();
        const summary = {
            id: site.id,
            name: getSiteDisplayName(site, index),
            vdcVaultSourceStorageType: typeof site.vdcVaultSourceStorageType === 'string' ? site.vdcVaultSourceStorageType : '',
            vdcVaultRepositoryType: typeof site.vdcVaultRepositoryType === 'string' ? site.vdcVaultRepositoryType : '',
            vdcVaultTargetVault: typeof site.vdcVaultTargetVault === 'string' ? site.vdcVaultTargetVault : '',
            existingEnvironmentCustomLabels: pruneExistingEnvironmentCustomLabels(site.existingEnvironmentCustomLabels, existingEnvironmentState),
            existingEnvironment: getExistingEnvironmentItems(existingEnvironmentState, site.existingEnvironmentCustomLabels),
            existingEnvironmentState,
            workloads: [],
            hypervisors: [],
            cloudWorkloads: [],
            saasWorkloads: [],
            containerWorkloads: [],
            plugins: [],
            unstructuredData: [],
            agents: [],
            totalWorkloadCount: 0,
            repoComponentCount: 0,
            totalEffortUnits: 0,
            selectedWorkloadCount: 0
        };

        const workloads = site.workloads.filter((workload) => hasSelectedWorkloadValue(workload.value) && (!excludeInactiveWorkloads || hasMeaningfulWorkloadScope(workload)));

        workloads.forEach((workload) => {
            const count = getWorkloadScopeCount(workload);
            const effortUnits = getWorkloadEffortUnits(workload);
            const protectedTb = isProtectedTbWorkload(workload.value)
                ? normalizeProtectedTb(workload.protectedTb)
                : null;
            const customLabel = normalizeWorkloadCustomLabel(workload.customLabel);
            const label = customLabel || getWorkloadLabel(workload.value);
            const type = getWorkloadType(workload.value);
            summary.workloads.push({
                value: workload.value,
                label,
                ...(customLabel ? { customLabel } : {}),
                type,
                quantityLabel: getWorkloadQuantityLabel(workload.value),
                count,
                ...(protectedTb !== null ? { protectedTb } : {}),
                ...(workload.rowId ? { rowId: workload.rowId } : {}),
                ...(isRepoMigrationWorkload(workload.value) && workload.vdcVaultSourceStorageType
                    ? { vdcVaultSourceStorageType: workload.vdcVaultSourceStorageType }
                    : {}),
                ...(isRepoMigrationWorkload(workload.value) && workload.vdcVaultRepositoryType
                    ? { vdcVaultRepositoryType: workload.vdcVaultRepositoryType }
                    : {}),
                ...(isRepoMigrationWorkload(workload.value) && workload.vdcVaultTargetVault
                    ? { vdcVaultTargetVault: workload.vdcVaultTargetVault }
                    : {}),
                ...(isRepoMigrationWorkload(workload.value) && workload.isRepositoryComponent
                    ? { isRepositoryComponent: true }
                    : {})
            });
            if (workload.isRepositoryComponent) {
                summary.repoComponentCount += 1;
            } else if (isProtectedTbWorkload(workload.value)) {
                // TB-scoped workloads (NAS, object storage) are tracked via unstructuredDataTotals;
                // excluded from machine count to avoid mixing units in totalWorkloadCount.
            } else {
                summary.totalWorkloadCount += count;
            }
            summary.totalEffortUnits += effortUnits;
            summary.selectedWorkloadCount += (hasMeaningfulWorkloadScope(workload) && !workload.isRepositoryComponent) ? 1 : 0;

            // Standard category dispatch (hypervisors, cloud, saas, containers, plugins, agents)
            const _cat = _wlCats.find((c) => Object.hasOwn(c.labels, workload.value));
            if (_cat) {
                summary[_cat.summaryKey].push({ [_cat.idKey]: workload.value, label, [_cat.countKey || 'count']: count });
                _cat.totalsMap.set(workload.value, (_cat.totalsMap.get(workload.value) || 0) + count);
                return;
            }

            // Unstructured data — separate case: protectedTb field + different totals shape
            if (Object.hasOwn(unstructuredDataLabels, workload.value)) {
                summary.unstructuredData.push({
                    workload: workload.value,
                    label,
                    protectedTb: protectedTb || 0,
                    count,
                    ...(workload.rowId ? { rowId: workload.rowId } : {}),
                    ...(workload.vdcVaultSourceStorageType ? { vdcVaultSourceStorageType: workload.vdcVaultSourceStorageType } : {}),
                    ...(workload.vdcVaultRepositoryType    ? { vdcVaultRepositoryType:    workload.vdcVaultRepositoryType }    : {}),
                    ...(workload.vdcVaultTargetVault       ? { vdcVaultTargetVault:       workload.vdcVaultTargetVault }       : {})
                });
                const _udTotal = unstructuredDataTotals.get(workload.value) || { count: 0, protectedTb: 0 };
                unstructuredDataTotals.set(workload.value, {
                    count: _udTotal.count + count,
                    protectedTb: _udTotal.protectedTb + (protectedTb || 0)
                });
            }
            // Repo migration workloads contribute to vault sizing only — no summary row needed.
        });

        // Source workloads (Phase 4) — count toward workload type totals and summary counts.
        // Replaces unprotectedWorkloads read. Legacy unprotectedWorkloads removed in Phase 6.
        (Array.isArray(site.sourceWorkloads) ? site.sourceWorkloads : []).forEach((sw) => {
            const count = Math.max(0, parseInt(sw.totalCount, 10) || 0);
            const wv    = sw.workloadValue || '';
            // Unstructured data (NAS, object storage) uses TB as its count unit — exclude from
            // machine count to keep totalWorkloadCount as machines-only.
            if (!Object.hasOwn(unstructuredDataLabels, wv)) {
                summary.totalWorkloadCount += count;
            }
            if (count > 0) summary.selectedWorkloadCount += 1;

            // Accumulate into per-type global totals (drives configuration snapshot display)
            const _swCat = _wlCats.find((c) => Object.hasOwn(c.labels, wv));
            if (_swCat) {
                _swCat.totalsMap.set(wv, (_swCat.totalsMap.get(wv) || 0) + count);
            } else if (Object.hasOwn(unstructuredDataLabels, wv)) {
                const _t = unstructuredDataTotals.get(wv) || { count: 0, protectedTb: 0 };
                unstructuredDataTotals.set(wv, { count: _t.count + 1, protectedTb: _t.protectedTb + count });
            }
        });

        return summary;
    });

    const hypervisorCounts = Array.from(hypervisorTotals.entries()).map(([hypervisor, count]) => ({
        hypervisor,
        label: hypervisorLabels[hypervisor] || hypervisor,
        count
    }));
    const cloudWorkloadCounts = Array.from(cloudTotals.entries()).map(([workload, count]) => ({
        workload,
        label: cloudLabels[workload] || workload,
        count
    }));
    const saasWorkloadCounts = Array.from(saasTotals.entries()).map(([workload, count]) => ({
        workload,
        label: saasLabels[workload] || workload,
        count
    }));
    const containerWorkloadCounts = Array.from(containerTotals.entries()).map(([workload, count]) => ({
        workload,
        label: containerLabels[workload] || workload,
        count
    }));
    const pluginQuantities = Array.from(pluginTotals.entries()).map(([plugin, quantity]) => ({
        plugin,
        label: pluginLabels[plugin] || plugin,
        quantity
    }));
    const unstructuredDataCounts = Array.from(unstructuredDataTotals.entries()).map(([workload, totals]) => ({
        workload,
        label: unstructuredDataLabels[workload] || workload,
        count: totals.count,
        protectedTb: totals.protectedTb
    }));
    const agentCounts = Array.from(agentTotals.entries()).map(([agent, count]) => ({
        agent,
        label: agentLabels[agent] || agent,
        count
    }));

    const totalCloudWorkloadInstances = cloudWorkloadCounts.reduce((sum, item) => sum + item.count, 0);
    const totalSaasWorkloadInstances = saasWorkloadCounts.reduce((sum, item) => sum + item.count, 0);
    const totalContainerWorkloadInstances = containerWorkloadCounts.reduce((sum, item) => sum + item.count, 0);
    const totalAgentInstances = agentCounts.reduce((sum, item) => sum + item.count, 0);
    const totalWorkloadCount = siteSummaries.reduce((sum, site) => sum + site.totalWorkloadCount, 0);
    const totalRepoComponentCount = siteSummaries.reduce((sum, site) => sum + site.repoComponentCount, 0);
    const totalEffortUnits = siteSummaries.reduce((sum, site) => sum + site.totalEffortUnits, 0);
    const selectedWorkloadCount = siteSummaries.reduce((sum, site) => sum + site.selectedWorkloadCount, 0);

    return {
        siteSummaries,
        hypervisorCounts,
        cloudWorkloadCounts,
        saasWorkloadCounts,
        containerWorkloadCounts,
        pluginQuantities,
        unstructuredDataCounts,
        agentCounts,
        totalCloudWorkloadInstances,
        totalSaasWorkloadInstances,
        totalContainerWorkloadInstances,
        totalAgentInstances,
        totalWorkloadCount,
        totalRepoComponentCount,
        totalEffortUnits,
        selectedWorkloadCount
    };
}


// Returns the total TB of data covered by one vbrRepoMigration scope entry.
// Used by both the hours calculation and the design-hours total-TB rollup.
function getVbrRepoScopeTb(scope) {
    return sites.reduce((t, site) => {
        const repoWorkloads = getRepoMigrationWorkloads(site).filter((w) => w.isRepositoryComponent);
        if (scope.sourceRowId != null) {
            const match = repoWorkloads.find((w) => w.rowId === scope.sourceRowId);
            return t + (match ? getRepoMigrationWorkloadProtectedTb(match) : 0);
        }
        return t + repoWorkloads
            .filter((w) => mapVbrRepoWorkloadToSourceType(w) === scope.sourceType)
            .reduce((tt, w) => tt + getRepoMigrationWorkloadProtectedTb(w), 0);
    }, 0);
}


function needsNewVbrDeployment() {
    return (hasUnprotectedWorkloadsNeedingVbr() && !hasVbrAcrossAllSites())
        || window.step2VbrLanes.some((l) => l.type === 'new');
}


function isUnmanagedWorkloadScopeActive(workloadId, workloadType) {
    const state = window.step2MatrixState.unmanaged[String(workloadId)];
    if (!state) return false;
    return UNMANAGED_MODIFIER_DEFS.some((mod) =>
        isModifierActive(state[mod.key], mod, workloadType)
    );
}


function scheduleRenderEstimate() {
    if (renderEstimateRafId) {
        cancelAnimationFrame(renderEstimateRafId);
    }
    renderEstimateRafId = requestAnimationFrame(() => {
        renderEstimateRafId = 0;
        renderEstimate();
    });
}


function renderEstimate() {
    const aggregatedWorkloads = buildAggregatedWorkloadData(sites, {
        excludeInactiveWorkloads: true
    });
    const projectName = document.getElementById('project-name').value.trim();
    // Derive engagement scope from canvas state — engagement type as a UI concept is retired.
    const { hV: _hV, hW: _hW, hC: _hC } = getCanvasEngagementFlags();
    // useRepoMigrationMode = true whenever ANY vault scope is present (including hybrid canvases
    // that combine vault workloads with source workloads or components). This flag controls whether
    // vault-specific effort sections and sizing appear in the output.
    // Note: this is intentionally broader than engagementType === 'vdc-vault', which only fires
    // for pure-vault canvases (no source workloads and no components). On a hybrid canvas,
    // useRepoMigrationMode can be true while engagementType is 'greenfield'.
    const useRepoMigrationMode = _hV;
    const useStandardDesignSections = _hW || _hC || !_hV; // false only for a pure vault canvas
    // engagementType is used for SOW intro sentences and display labels; it describes the primary
    // engagement character. Use canvas flags (hV, hW, hC) for conditional logic.
    const engagementType = deriveEngagementType();
    const usePostDeploySection = useStandardDesignSections || useRepoMigrationMode;
    const implementationMode = getImplementationMode();
    const isDesignOnly = implementationMode === 'none';
    const advisorySupportRequired = isDesignOnly && usePostDeploySection ? toggleValue(document.getElementById('advisory-support-required')) : 'no';
    // Implementation style: skeleton (default, -15%) or white-glove (full effort)
    // Reads from the impl-delivery-style radio group; skeleton = reduce, white-glove = full
    const _implDeliveryStyle = !isDesignOnly
        ? (document.querySelector('input[name="impl-delivery-style"]:checked')?.value ?? 'white-glove')
        : 'skeleton';
    const templateJobsRequired = !isDesignOnly ? (_implDeliveryStyle === 'skeleton' ? 'yes' : 'no') : 'no';
    const enablementRequired = usePostDeploySection ? toggleValue(document.getElementById('enablement-required')) : 'no';
    const uatRequired = usePostDeploySection ? toggleValue(document.getElementById('uat-required')) : 'no';
    const asBuiltRequired = usePostDeploySection ? toggleValue(document.getElementById('as-built-required')) : 'no';
    const drRunbookRequired = usePostDeploySection ? toggleValue(document.getElementById('dr-runbook-required')) : 'no';
    const sopRequired = usePostDeploySection ? toggleValue(document.getElementById('sop-required')) : 'no';
    const siteCount = sites.length;
    const backupVerificationRequired = useStandardDesignSections ? document.getElementById('backup-verification-required').value : 'no';
    const sureBackupRequired = backupVerificationRequired !== 'no'
        ? (document.getElementById('sure-backup-required').value === 'yes' ? 'yes' : 'no')
        : 'no';

    const baseArchitecture = useStandardDesignSections
        ? buildArchitectureStateSnapshot()
        : normalizeArchitectureState({}, siteCount);
    const architecture = resolvePlatformArchitectureState(
        baseArchitecture,
        useStandardDesignSections
    );
    const veeamOneRequired = useStandardDesignSections ? toggleValue(document.getElementById('veeam-one-required')) : 'no';
    const vroRequired = useStandardDesignSections && [
        ...Object.values(window.step2MatrixState?.unmanaged || {}),
        ...Object.values(window.step2MatrixState?.migrationTargets || {}),
    ].some((s) => s.orchestration?.vro) ? 'yes' : 'no';
    const veeamOneOptions = useStandardDesignSections
        ? getSelectedVeeamOneOptions()
        : [];
    const tapeIntegrationRequired = useStandardDesignSections ? toggleValue(document.getElementById('tape-integration-required')) : 'no';
    const tapeConnectivityType = tapeIntegrationRequired === 'yes'
        ? document.getElementById('tape-connectivity-type').value
        : 'sas';
    const siemForwardingRequired = useStandardDesignSections
        ? toggleValue(document.getElementById('siem-forwarding-required'))
        : 'no';
    const hypervisorCounts = aggregatedWorkloads.hypervisorCounts;
    const cloudWorkloadCounts = aggregatedWorkloads.cloudWorkloadCounts;
    const saasWorkloadCounts = aggregatedWorkloads.saasWorkloadCounts;
    const containerWorkloadCounts = aggregatedWorkloads.containerWorkloadCounts;
    const pluginQuantities = aggregatedWorkloads.pluginQuantities;
    const unstructuredDataCounts = aggregatedWorkloads.unstructuredDataCounts;
    const agentCounts = aggregatedWorkloads.agentCounts;
    const siteSummaries = aggregatedWorkloads.siteSummaries;
    const totalCloudWorkloadInstances = aggregatedWorkloads.totalCloudWorkloadInstances;
    const totalSaasWorkloadInstances = aggregatedWorkloads.totalSaasWorkloadInstances;
    const totalContainerWorkloadInstances = aggregatedWorkloads.totalContainerWorkloadInstances;
    const totalAgentInstances = aggregatedWorkloads.totalAgentInstances;
    const totalWorkloadCount = aggregatedWorkloads.totalWorkloadCount;
    const totalRepoComponentCount = aggregatedWorkloads.totalRepoComponentCount;
    const totalEffortUnits = aggregatedWorkloads.totalEffortUnits;
    const totalUnstructuredDataProtectedTb = unstructuredDataCounts.reduce(
        (sum, item) => sum + normalizeProtectedTb(item.protectedTb),
        0
    );
    const useInstanceMetrics = !useRepoMigrationMode;

    // ── Job-centric effort calculation ───────────────────────────────────────────
    // Derive job-like objects from source workloads for HC unit calculation.
    // Old site.jobs (managed canvas) was removed in Phase 6; source workload jobs
    // are the equivalent — currently-configured protection on Step 1 workloads.
    // Shape matches calcJobHcUnits({ workloadType, jobType, count }).
    const allSiteJobs = sites.flatMap((s) =>
        (Array.isArray(s.sourceWorkloads) ? s.sourceWorkloads : []).flatMap((sw) =>
            (Array.isArray(sw.jobs) ? sw.jobs : []).map((j) => ({
                workloadType: sw.workloadValue,
                jobType:      j.type,
                count:        Math.max(0, parseInt(j.count, 10) || 0),
            }))
        )
    );
    // Note: allSiteJobs feeds HC unit calculation (calcJobHcUnits) and
    // _vbrJobVmCount (design/re-implementation scaling).
    // Per-type job arrays are not needed — protection-type effort is driven by
    // unprotected workloads × Step 2 matrix, not managed canvas jobs.

    // Source workloads (Phase 4) — adapted to flat shape used by scope checks.
    // Legacy unprotectedWorkloads no longer read here; removed in Phase 6.
    const allUnprotectedWorkloads = sites.flatMap((s) =>
        (Array.isArray(s.sourceWorkloads) ? s.sourceWorkloads : []).map((sw) => ({
            id:           sw.id,
            workloadType: sw.workloadValue,
            count:        Math.max(0, parseInt(sw.totalCount, 10) || 0),
            // effortCount weights NAS/unstructured TB down vs VMs (see sourceWorkloadEffortUnits).
            // For VM-based workloads this equals count.
            effortCount:  sourceWorkloadEffortUnits(sw.workloadValue, sw.totalCount),
        }))
    );
    // Only workloads with at least one Step 2 matrix selection contribute to effort.
    // Workloads with nothing ticked in Step 2 are out-of-scope and excluded.
    const scopedUnprotectedWorkloads = allUnprotectedWorkloads.filter((w) =>
        isUnmanagedWorkloadScopeActive(w.id, w.workloadType)
    );
    const unprotectedVmCount    = scopedUnprotectedWorkloads.reduce((sum, w) => sum + Math.max(0, w.effortCount || 0), 0);
    const unprotectedScopeCount = scopedUnprotectedWorkloads.filter((w) => (parseInt(w.count, 10) || 0) > 0).length;

    // Total effort scope — driven by unprotected (greenfield) workloads only.
    // Managed canvas jobs drive HC/upgrade effort (below), NOT implementation.
    const effortTotalInstances     = unprotectedVmCount;
    const effortSelectedScopeCount = unprotectedScopeCount;

    const postDeployScopeCount = useRepoMigrationMode
        ? (getRepoMigrationScopedItemCount(sites) || 1) // floor at 1 — mirrors standard mode
        : (effortSelectedScopeCount || 1); // floor at 1 so post-deploy is never zero when scope exists

    // VBR managed job VM count — used for both design and (edge case) re-implementation.
    // All canvas jobs are VBR-managed; defined here so it's available for implementation below.
    const _vbrJobVmCount = allSiteJobs.reduce((s, j) => s + Math.max(0, parseInt(j.count, 10) || 0), 0);

    // Implementation hours — greenfield (unprotected) workloads only.
    // Split by product so VBfCloud appliance work shows as a distinct line item in Step 3.
    // Single pass: classify each workload into VBR / VBfCloud / VB365 bucket.
    const [_vbrImplHours, _vbfCloudImplHours, _vb365ImplHours] = scopedUnprotectedWorkloads.reduce(
        ([vbr, cloud, vb365], w) => {
            const hrs = blockHours(effortConfig.implementationBlocks, Math.max(0, w.effortCount || 0));
            if (isVbfCloudManagedWorkload(w.workloadType)) return [vbr, cloud + hrs, vb365];
            if (isVb365ManagedWorkload(w.workloadType))    return [vbr, cloud, vb365 + hrs];
            return [vbr + hrs, cloud, vb365];
        },
        [0, 0, 0]
    );
    const unprotectedImplHours    = _vbrImplHours + _vbfCloudImplHours + _vb365ImplHours;
    const fullImplementationHours = unprotectedImplHours;

    // Design hours — split by product; each group uses its own block table.
    // VBR: hypervisors, agents, unstructured data, and anything not claimed by VB365/Kasten/VBfCloud.
    //   Includes managed canvas job VMs — all canvas jobs are VBR-managed, so job VM count
    //   drives VBR design effort even when there are no unprotected VBR workloads.
    // VB365: Microsoft 365 workloads.
    // Kasten: Kubernetes namespace-scoped workloads.
    // VBforCloud: AWS/Azure/GCP native workloads.
    // Single pass: classify each workload into VBR / VB365 / Kasten / VBfCloud bucket.
    const [_vbrDesignVmsBase, _vb365DesignVms, _kastenDesignVms, _vbfCloudDesignVms] = scopedUnprotectedWorkloads.reduce(
        ([vbr, vb365, kasten, cloud], w) => {
            const n = Math.max(0, w.effortCount || 0);
            if (isVbfCloudManagedWorkload(w.workloadType))  return [vbr, vb365, kasten, cloud + n];
            if (isVb365ManagedWorkload(w.workloadType))     return [vbr, vb365 + n, kasten, cloud];
            if (isNamespaceScopedWorkload(w.workloadType))  return [vbr, vb365, kasten + n, cloud];
            return [vbr + n, vb365, kasten, cloud];
        },
        [0, 0, 0, 0]
    );
    const _vbrDesignVms = _vbrJobVmCount + _vbrDesignVmsBase;
    // Each active product contributes 4h flat base + VM count modifier from its block table.
    const _designBase = 4;
    // Skeleton delivery mode reduces implementation effort only — design is always full effort
    // regardless of delivery style or mode (REA, skeleton, white-glove).
    const _templateJobsActive = !isDesignOnly && _implDeliveryStyle === 'skeleton';
    const _vbfCloudDesignHours = _vbfCloudDesignVms > 0
        ? Math.round(_designBase + blockHours(effortConfig.designBlocksVbfCloud, _vbfCloudDesignVms))
        : 0;
    const _vb365DesignHours = _vb365DesignVms > 0
        ? Math.round(_designBase + blockHours(effortConfig.designBlocksVb365, _vb365DesignVms))
        : 0;
    // VBR and Kasten design hours — extracted as named constants (like VB365/VBfCloud)
    // so each platform can be shown as its own line in the Step 3 Design breakdown.
    const _vbrDesignHours = _vbrDesignVms > 0
        ? Math.round(_designBase + blockHours(effortConfig.designBlocksVbr, _vbrDesignVms))
        : 0;
    const _kastenDesignHours = _kastenDesignVms > 0
        ? Math.round(_designBase + blockHours(effortConfig.designBlocksKasten, _kastenDesignVms))
        : 0;
    // Veeam ONE design — gated on the Step 2 VONE toggle. Scales with the total monitored
    // estate (same "whole estate" count used for discovery): all source-workload VMs +
    // existing-environment instances + repository components. Uses its own 2h base.
    const _voneMonitoredVms = allUnprotectedWorkloads.reduce((s, w) => s + Math.max(0, w.effortCount || 0), 0)
        + totalWorkloadCount + totalRepoComponentCount;
    const _voneDesignVms  = veeamOneRequired === 'yes' ? _voneMonitoredVms : 0;
    const _voneDesignBase = 2;
    const _voneDesignHours = _voneDesignVms > 0
        ? Math.round(_voneDesignBase + blockHours(effortConfig.designBlocksVone, _voneDesignVms))
        : 0;
    // Veeam Recovery Orchestrator design — gated on the VRO toggle. Scales with the same
    // estate count as VONE (orchestrated recovery scope ≈ protected estate). Uses a 4h base.
    const _vroDesignVms   = vroRequired === 'yes' ? _voneMonitoredVms : 0;
    const _vroDesignBase  = 4;
    const _vroDesignHours = _vroDesignVms > 0
        ? Math.round(_vroDesignBase + blockHours(effortConfig.designBlocksVro, _vroDesignVms))
        : 0;
    const designHours = Math.round(
          _vbrDesignHours
        + _vb365DesignHours
        + _kastenDesignHours
        + _vbfCloudDesignHours
        + _voneDesignHours
        + _vroDesignHours
    );

    // ── Discovery effort ─────────────────────────────────────────────────────
    // Three additive components:
    //   1. Workload block hours  — scales with all workloads in scope (EE inventory + unprotected) + managed job VMs
    //   2. Component hours       — direct rate × count per Veeam component type (VBR, proxies, repos, VB365, VBfCloud, Kasten)
    //   3. Site overhead         — flat 2h per site (every site needs its own scoping session, including site 1)
    // Note: pure greenfield engagements get workload block hours + site overhead (no component hours).

    // 1. Workload scale — source workload total count (all groups, all sites) drives discovery.
    // Legacy job VM count removed (Phase 4); allSiteJobs retained for HC calculation only.
    const allSourceWorkloadVmCount = allUnprotectedWorkloads.reduce((sum, w) => sum + Math.max(0, w.effortCount || 0), 0);
    const discoveryWorkloadCount   = allSourceWorkloadVmCount + totalWorkloadCount + totalRepoComponentCount;
    const discoveryWorkloadHours   = blockHours(effortConfig.discoveryBlocks, discoveryWorkloadCount);

    // 2. Component discovery — direct hours per unit, weighted by component type
    const dc = effortConfig.discoveryComponent;
    const componentDiscoveryHours = sites.reduce((sum, s) => {
        const ee = s.existingEnvironment || {};
        const veeamServers = Array.isArray(s.veeamServers) ? s.veeamServers : [];
        return sum
            + normalizeEnvironmentCount(ee.vbrServers)              * dc.vbrServer
            + (normalizeEnvironmentCount(ee.proxyServersWindows)
               + normalizeEnvironmentCount(ee.proxyServersLinux))   * dc.vbrProxy
            + normalizeEnvironmentCount(ee.repositoryServers)       * dc.vbrRepository
            + veeamServers.filter(sv => sv.type === 'vb365').length * dc.vb365Server
            + normalizeEnvironmentCount(ee.vb365ProxyServers)       * dc.vb365Proxy
            + normalizeEnvironmentCount(ee.vb365RepositoryServers)  * dc.vb365Repository
            + veeamServers.filter(sv => ['vbfaws','vbfaz','vbfgc'].includes(sv.type)).length * dc.vbfCloudServer
            + normalizeEnvironmentCount(ee.vbfCloudRepositoryServers) * dc.vbfCloudRepository
            + veeamServers.filter(sv => sv.type === 'kasten').length * dc.kastenServer;
    }, 0);

    // 3. Site overhead
    const discoverySiteHours = siteCount * effortConfig.discoveryPerSite;

    const discoveryHours = discoveryWorkloadHours + componentDiscoveryHours + discoverySiteHours;
    const kickoffHours           = effortConfig.baselineHours.kickoff;
    const customerKickoffHours   = effortConfig.baselineHours.customerKickoff;

    // ── HC / Upgrade effort — driven by managed canvas jobs + Veeam components ──
    // upgradeHealthCheckHours: flat 16h baseline (prep, scripting, report) whenever
    //   any managed job or component row has HC selected in the Step 2 matrix.
    // jobValidationHours: scales with blended effort units across all managed jobs
    //   and Veeam component/server instances.
    // hcActive: driven entirely by Step 2 matrix HC selections (upgrade-health-check-review-required).
    const hcActive = !!(document.getElementById('upgrade-health-check-review-required')?.checked);
    const sureReplicaActiveForHc = document.getElementById('sure-replica-required')?.value === 'yes';

    // Blended job units: per-job weight × job-type factor × (1 + floor(count/50))
    const managedJobHcUnits = allSiteJobs.reduce(
        (sum, j) => sum + calcJobHcUnits(j, sureReplicaActiveForHc), 0
    );

    // ── Upgrade component effort ─────────────────────────────────────────────────
    // Driven by Veeam Components matrix + Engagement Settings proxy/repo toggles.
    // VBR=6h each, proxy=1h each, repo=1h each.
    //
    // Per-instance state: if upgradeScope.perVbr[id] exists, use linuxVsa to determine
    // if upgrading. Falls back to type-level state. Defaults to NOT upgrading when
    // state is uninitialized — components with nothing selected in Step 2 contribute
    // no effort to Step 3.
    const _isVbrUpgrading = (row) => {
        const s = upgradeScope.perVbr[row.id];
        if (s !== undefined) return s.linuxVsa === true;
        return window.step2MatrixState.components['vbr']?.upgrade?.linuxVsa === true;
    };
    const _isVbrHcActive = (instanceId) => {
        const s = upgradeScope.perVbr[instanceId];
        if (s !== undefined) return s.hc === true;
        return window.step2MatrixState.components['vbr']?.assessment?.hc === true;
    };

    // Veeam server instances + infra comp chips (proxies, repos…)
    // Only count instances where HC or upgrade is selected in Step 2 — components
    // with no selections are excluded from all Step 3 effort calculations.
    const componentHcUnits = sites.reduce((sum, s) => {
        const scopedServers = Array.isArray(s.veeamServers)
            ? s.veeamServers.filter((sv) => {
                if (sv.type === 'vbr') {
                    return _isVbrHcActive(sv.instanceId) || _isVbrUpgrading({ id: sv.instanceId });
                }
                // Named non-VBR instance: prefer serverManaged[id]._self, fall back to type-level components
                const sm = window.step2MatrixState.serverManaged?.[sv.instanceId];
                if (sm?._self) {
                    return sm._self.assessment?.hc === true || sm._self.upgrade?.linuxVsa === true;
                }
                return window.step2MatrixState.components[sv.type]?.assessment?.hc === true
                    || window.step2MatrixState.components[sv.type]?.upgrade?.linuxVsa === true;
            })
            : [];
        // Comp items (proxies, repos) are counted only when the site has at least one
        // server with HC or upgrade selected — they have no individual matrix row.
        const comps = scopedServers.length > 0 ? getCanvasCompItems(s).length : 0;
        return sum + scopedServers.length + comps;
    }, 0);

    // Auto-inferred components (no named server instance) also contribute HC units
    // when HC or upgrade is selected. Only counted if no named server of that type
    // already exists across ALL sites (to avoid double-counting).
    const _autoInferredTypes = ['vb365', 'vbfaws', 'vbfaz', 'vbfgc', 'kasten'];
    const autoInferredHcUnits = _autoInferredTypes.reduce((sum, type) => {
        const hasNamed = sites.some((s) => Array.isArray(s.veeamServers) && s.veeamServers.some((sv) => sv.type === type));
        if (hasNamed) return sum;  // already counted above via the named-server loop
        const isActive = window.step2MatrixState.components[type]?.assessment?.hc === true
            || window.step2MatrixState.components[type]?.upgrade?.linuxVsa === true;
        return sum + (isActive ? 1 : 0);
    }, 0);

    const totalHcUnits = managedJobHcUnits + componentHcUnits + autoInferredHcUnits;

    const upgradeHealthCheckHours = hcActive ? effortConfig.upgradeHealthCheck.full : 0;
    const jobValidationHours      = hcActive ? blockHours(effortConfig.jobValidation.blocks, totalHcUnits) : 0;
    const anyUpgradeVbr   = collectUpgradeVbrRows().some(_isVbrUpgrading);
    const anyUpgradeProxy = anyUpgradeVbr;  // inferred: if VBR is upgraded, proxies must be too
    const anyUpgradeRepo  = anyUpgradeVbr;  // inferred: if VBR is upgraded, repos must be too
    // Non-VBR named instances: check serverManaged[id]._self first, fall back to type-level components.
    const _isNonVbrServerUpgrading = (type) => {
        const namedOfType = sites.flatMap((s) => (Array.isArray(s.veeamServers) ? s.veeamServers : []).filter((sv) => sv.type === type));
        if (namedOfType.length > 0) {
            return namedOfType.some((sv) => {
                const sm = window.step2MatrixState.serverManaged?.[sv.instanceId];
                if (sm?._self) return sm._self.upgrade?.linuxVsa === true;
                return false;
            });
        }
        // Fall back to type-level components state (auto-inferred/legacy)
        return window.step2MatrixState.components[type]?.upgrade?.linuxVsa === true;
    };
    const anyUpgradeVb365  = _isNonVbrServerUpgrading('vb365');
    const anyUpgradeVbfAws = _isNonVbrServerUpgrading('vbfaws');
    const anyUpgradeVbfAz  = _isNonVbrServerUpgrading('vbfaz');
    const anyUpgradeVbfGc  = _isNonVbrServerUpgrading('vbfgc');
    const anyUpgradeKasten = _isNonVbrServerUpgrading('kasten');
    const upgradeActive    = anyUpgradeVbr || anyUpgradeProxy || anyUpgradeRepo
        || anyUpgradeVb365 || anyUpgradeVbfAws || anyUpgradeVbfAz || anyUpgradeVbfGc || anyUpgradeKasten;

    // VBR instance count: only count VBRs selected for upgrade (linuxVsa === true)
    const upgradeVbrCount = collectUpgradeVbrRows().filter(_isVbrUpgrading).length;
    // Proxy / Repo counts: proxies and repos are site-level chips (existingEnvironment.*),
    // not owned by individual VBR instances. Count a site's proxies/repos for upgrade only
    // if at least one VBR at that site is being upgraded.
    const _anyVbrUpgradingAtSite = (s) => {
        const namedVbrs = Array.isArray(s.veeamServers) ? s.veeamServers.filter((sv) => sv.type === 'vbr') : [];
        if (namedVbrs.length > 0) {
            return namedVbrs.some((srv) => _isVbrUpgrading({ id: srv.instanceId }));
        }
        // Legacy site: VBR rows are synthetic ids env-vbr-{siteId}-{i}
        const legacyCount = normalizeToggleMigratedCount(s.existingEnvironment?.vbrServers) || 0;
        return Array.from({ length: legacyCount }, (_, i) => `env-vbr-${s.id}-${i}`)
            .some((instanceId) => _isVbrUpgrading({ id: instanceId }));
    };
    const upgradeProxyCount = sites.reduce((sum, s) =>
        sum + (_anyVbrUpgradingAtSite(s) ? (
            Math.max(0, parseInt(s.existingEnvironment?.proxyServersWindows, 10) || 0) +
            Math.max(0, parseInt(s.existingEnvironment?.proxyServersLinux, 10) || 0)
        ) : 0), 0);
    const upgradeRepoCount = sites.reduce((sum, s) =>
        sum + (_anyVbrUpgradingAtSite(s) ? Math.max(0, parseInt(s.existingEnvironment?.repositoryServers, 10) || 0) : 0), 0);

    // VB365 proxy/repo: gated on VB365 upgrading (anyUpgradeVb365 declared above) and site having a VB365 server.
    const _anyVb365AtSite = (s) =>
        Array.isArray(s.veeamServers) && s.veeamServers.some((sv) => sv.type === 'vb365');
    const upgradeVb365ProxyCount = anyUpgradeVb365
        ? sites.reduce((sum, s) =>
            sum + (_anyVb365AtSite(s) ? Math.max(0, parseInt(s.existingEnvironment?.vb365ProxyServers, 10) || 0) : 0), 0)
        : 0;
    const upgradeVb365RepoCount = anyUpgradeVb365
        ? sites.reduce((sum, s) =>
            sum + (_anyVb365AtSite(s) ? Math.max(0, parseInt(s.existingEnvironment?.vb365RepositoryServers, 10) || 0) : 0), 0)
        : 0;

    // VBfCloud repositories: hosted on VBR infrastructure, so gated on VBR upgrading at the same site.
    const upgradeVbfCloudRepoCount = sites.reduce((sum, s) =>
        sum + (_anyVbrUpgradingAtSite(s) ? Math.max(0, parseInt(s.existingEnvironment?.vbfCloudRepositoryServers, 10) || 0) : 0), 0);

    // VBfAWS / VBfAz / VBfGC / Kasten: count named server instances; fall back to 1
    // for auto-inferred entries (i.e. workload added in Step 1 but no explicit server instance).
    const _namedServerCount = (type) =>
        sites.reduce((n, s) => n + (Array.isArray(s.veeamServers) ? s.veeamServers.filter((sv) => sv.type === type).length : 0), 0);
    const upgradeVbfAwsCount = anyUpgradeVbfAws ? Math.max(1, _namedServerCount('vbfaws')) : 0;
    const upgradeVbfAzCount  = anyUpgradeVbfAz  ? Math.max(1, _namedServerCount('vbfaz'))  : 0;
    const upgradeVbfGcCount  = anyUpgradeVbfGc  ? Math.max(1, _namedServerCount('vbfgc'))  : 0;
    const upgradeKastenCount = anyUpgradeKasten  ? Math.max(1, _namedServerCount('kasten')) : 0;

    const upgradeComponentHours = (anyUpgradeVbr    ? upgradeVbrCount          * effortConfig.upgradeComponent.vbr          : 0)
                                + (anyUpgradeProxy   ? upgradeProxyCount        * effortConfig.upgradeComponent.proxy        : 0)
                                + (anyUpgradeRepo    ? upgradeRepoCount         * effortConfig.upgradeComponent.repo         : 0)
                                + (anyUpgradeVb365   ? upgradeVb365ProxyCount   * effortConfig.upgradeComponent.vb365Proxy   : 0)
                                + (anyUpgradeVb365   ? upgradeVb365RepoCount    * effortConfig.upgradeComponent.vb365Repo    : 0)
                                + (anyUpgradeVbr     ? upgradeVbfCloudRepoCount * effortConfig.upgradeComponent.vbfCloudRepo : 0)
                                + (anyUpgradeVbfAws  ? upgradeVbfAwsCount       * effortConfig.upgradeComponent.vbfaws       : 0)
                                + (anyUpgradeVbfAz   ? upgradeVbfAzCount        * effortConfig.upgradeComponent.vbfaz        : 0)
                                + (anyUpgradeVbfGc   ? upgradeVbfGcCount        * effortConfig.upgradeComponent.vbfgc        : 0)
                                + (anyUpgradeKasten  ? upgradeKastenCount       * effortConfig.upgradeComponent.kasten       : 0);

    // Pre-flight: applies whenever upgrade is active, regardless of HC.
    // HC-only (no upgrade) has no pre-flight — there's nothing to prepare for.
    const upgradePreFlightHours = upgradeActive
        ? blockHours(effortConfig.upgradePreFlight.blocks, totalHcUnits)
        : 0;

    const templateJobsReduction = templateJobsRequired === 'yes'
        ? Math.round(fullImplementationHours * (1 - effortConfig.templateJobs.implReductionMultiplier))
        : fullImplementationHours;
    const implementationHours = implementationMode === 'veeam-ps' ? templateJobsReduction : 0;
    // Split impl hours into VBR and VBfCloud portions (preserving any template reduction ratio).
    const _implReductionRatio = fullImplementationHours > 0 ? implementationHours / fullImplementationHours : 1;
    const _vbrImplHoursAdj      = Math.round(_vbrImplHours      * _implReductionRatio);
    const _vbfCloudImplHoursAdj = Math.round(_vbfCloudImplHours * _implReductionRatio);
    const _vb365ImplHoursAdj    = Math.round(_vb365ImplHours    * _implReductionRatio);
    const advisoryHours = advisorySupportRequired === 'yes'
        ? Math.round(fullImplementationHours * effortConfig.advisorySupport.multiplier)
        : 0;


    const veeamOneHours = veeamOneRequired === 'yes'
        ? effortConfig.veeamOne.baseline + (effortSelectedScopeCount * effortConfig.veeamOne.perHypervisor)
        : 0;

    const veeamOneOptionHours = veeamOneRequired === 'yes'
        ? veeamOneOptions.length * effortConfig.veeamOne.customOption
        : 0;

    const vroHours = vroRequired === 'yes'
        ? effortConfig.vro.baseline + (effortSelectedScopeCount * effortConfig.vro.perScopedItem)
        : 0;

    // ── Protection-type hours: driven by unprotected workloads × Step 2 matrix ────
    // Managed canvas jobs (Step 1) drive health-check and upgrade effort only —
    // not design/implement effort for individual protection types.

    // Backup Verification (SureBackup): flat rate per unprotected workload with verification selected in Step 2 matrix
    const sureBackupHours = scopedUnprotectedWorkloads.reduce((sum, w) => {
        return window.step2MatrixState.unmanaged[String(w.id)]?.protection?.verification
            ? sum + effortConfig.sureBackup.flat
            : sum;
    }, 0);

    // Backup Copy: baseline + block hours per unprotected workload with backupCopy selected
    const backupCopyHours = scopedUnprotectedWorkloads.reduce((sum, w) => {
        if (!window.step2MatrixState.unmanaged[String(w.id)]?.protection?.backupCopy) return sum;
        const count = Math.max(0, w.effortCount || 0);
        return count > 0
            ? sum + effortConfig.backupCopy.baseline + blockHours(effortConfig.backupCopy.blocks, count)
            : sum;
    }, 0);
    const backupCopyRequired = scopedUnprotectedWorkloads.some(
        (w) => window.step2MatrixState.unmanaged[String(w.id)]?.protection?.backupCopy
    ) ? 'yes' : 'no';

    // Replication: block hours per unprotected workload with replication selected
    const replicationHours = scopedUnprotectedWorkloads.reduce((sum, w) => {
        if (!window.step2MatrixState.unmanaged[String(w.id)]?.protection?.replication) return sum;
        const count = Math.max(0, w.effortCount || 0);
        return sum + blockHours(effortConfig.replicationBlocks, count);
    }, 0);
    const replicationRequired = scopedUnprotectedWorkloads.some(
        (w) => window.step2MatrixState.unmanaged[String(w.id)]?.protection?.replication
    ) ? 'yes' : 'no';

    // SureReplica: flat rate, requires replication to be in scope
    const sureReplicaRequired = replicationRequired === 'yes'
        ? (document.getElementById('sure-replica-required')?.value === 'yes' ? 'yes' : 'no')
        : 'no';
    const sureReplicaHours = sureReplicaRequired === 'yes' ? effortConfig.sureReplica.flat : 0;

    // CDP Replication: block hours per unprotected workload with cdpReplication selected
    const cdpReplicationHours = scopedUnprotectedWorkloads.reduce((sum, w) => {
        if (!window.step2MatrixState.unmanaged[String(w.id)]?.protection?.cdpReplication) return sum;
        const count = Math.max(0, w.effortCount || 0);
        return sum + blockHours(effortConfig.cdpReplicationBlocks, count);
    }, 0);
    const cdpReplicationRequired = scopedUnprotectedWorkloads.some(
        (w) => window.step2MatrixState.unmanaged[String(w.id)]?.protection?.cdpReplication
    ) ? 'yes' : 'no';

    // ── Hypervisor migration effort ───────────────────────────────────────────────
    // Per scope: flat decommission cost + new job implementation (block-sized by target VM count)
    // + optional proxy reconfiguration + optional post-cutover restore testing.
    // Only counted when the HVM section toggle is explicitly enabled.
    const hypervisorMigrationHours = AppState.hvmEnabled ? hypervisorMigrationScopes.reduce((sum, scope) => {
        const targetCount = Math.max(0, parseInt(scope.targetVmCount, 10) || 0);
        // When the scope is in the workload matrix, implementation hours move to the Platform category
        const newJobHours = (!scope.includeInScope && targetCount > 0)
            ? blockHours(effortConfig.implementationBlocks, targetCount)
            : 0;
        const proxyHours  = scope.includeProxyReconfig   ? effortConfig.hypervisorMigration.proxyReconfig   : 0;
        const testHours   = scope.includeRestoreTesting  ? effortConfig.hypervisorMigration.restoreTesting   : 0;
        return sum + effortConfig.hypervisorMigration.decommissionPerScope + newJobHours + proxyHours + testHours;
    }, 0) : 0;
    const hypervisorMigrationRequired = AppState.hvmEnabled && hypervisorMigrationScopes.length > 0;

    // ── VB365 repository migration effort ────────────────────────────────────────────
    // Effort = blockHours(dataSizeBlocks, consumed TB) — derived from Step 1 repo consumed TB.
    const vb365TotalConsumedTb = sites.reduce((sum, site) => {
        const tbs = site.existingEnvironmentVb365RepoConsumedTbs || {};
        return sum + Object.values(tbs).reduce((t, v) => t + (parseFloat(v) || 0), 0);
    }, 0);
    const vb365TotalRepoCount = Math.max(1,
        sites.reduce((sum, site) =>
            sum + (parseInt(site.existingEnvironment?.vb365RepositoryServers, 10) || 0), 0)
    );
    const vb365InstalledVersion = (() => {
        for (const site of sites) {
            const v = (site.existingEnvironmentServerVersions || {}).vb365;
            if (v) return v;
        }
        return 'v8';
    })();
    const vb365RepoMigrationHours = vb365RepoMigrationScopes.length > 0
        ? blockHours(effortConfig.vb365RepositoryMigration.dataSizeBlocks, vb365TotalConsumedTb)
        : 0;
    const vb365RepoMigrationRequired = vb365RepoMigrationScopes.length > 0;

    // ── VBR repository migration effort ──────────────────────────────────────────
    // Per scope: base assessment + optional VHR hardening setup + per-repository reconfiguration
    // + SOBR complexity add-on (when source is SOBR Performance Tier)
    // + data movement effort scaled by estimated data volume (TB).
    // Target type (block-performance-tier, blob-performance-tier, vdc-vault-aws, vdc-vault-azure)
    // is stored on each scope for SOW output; effort is currently uniform across target types.
    const vbrRepoMigrationHours = vbrRepoMigrationScopes.reduce(
        (sum, scope) => sum + blockHours(effortConfig.vbrRepoMigration.dataSizeBlocks, getVbrRepoScopeTb(scope)),
        0
    );
    const vbrRepoMigrationRequired = vbrRepoMigrationScopes.length > 0;

    // ── Repository migration design effort ────────────────────────────────────
    // Design hours for VBR and VB365 repo migrations — applies in both REA and Standard PS modes.
    // Input: total data volume (TB) across all scopes for each product.
    const vbrRepoMigrationTotalTb = vbrRepoMigrationScopes.reduce(
        (sum, scope) => sum + getVbrRepoScopeTb(scope),
        0
    );
    const vbrRepoMigrationDesignHours  = vbrRepoMigrationRequired
        ? blockHours(effortConfig.repoMigrationDesignBlocks, vbrRepoMigrationTotalTb) : 0;
    const vb365RepoMigrationDesignHours = vb365RepoMigrationRequired
        ? blockHours(effortConfig.repoMigrationDesignBlocks, vb365TotalConsumedTb) : 0;

    // ── Migration target implementation hours (when includeInScope is ticked) ──────
    // These replace the newJobHours that were removed from the HVM category above.
    // They appear under the Platform category alongside regular unprotected workload impl hours.
    const migrationTargetImplHours = AppState.hvmEnabled
        ? hypervisorMigrationScopes.reduce((sum, scope) => {
            if (!scope.includeInScope) return sum;
            const targetCount = Math.max(0, parseInt(scope.targetVmCount, 10) || 0);
            return targetCount > 0
                ? sum + blockHours(effortConfig.implementationBlocks, targetCount)
                : sum;
        }, 0)
        : 0;

    // Extra protection-type hours for migration target rows (driven by scope matrix checkboxes).
    // All four tallied in a single reduce to avoid four separate passes over the scopes array.
    const [migrationTargetSureBackupHours, migrationTargetBackupCopyHours,
           migrationTargetReplicationHours, migrationTargetCdpHours] = AppState.hvmEnabled
        ? hypervisorMigrationScopes.reduce(([sb, bc, rep, cdp], scope) => {
            if (!scope.includeInScope) return [sb, bc, rep, cdp];
            const k     = `migration-${scope.id}`;
            const ms    = window.step2MatrixState.migrationTargets[k]?.protection || {};
            const count = Math.max(0, parseInt(scope.targetVmCount, 10) || 0);
            return [
                ms.verification  ? sb + effortConfig.sureBackup.flat : sb,
                (ms.backupCopy   && count > 0) ? bc  + effortConfig.backupCopy.baseline + blockHours(effortConfig.backupCopy.blocks, count) : bc,
                (ms.replication  && count > 0) ? rep + blockHours(effortConfig.replicationBlocks,   count) : rep,
                (ms.cdpReplication && count > 0) ? cdp + blockHours(effortConfig.cdpReplicationBlocks, count) : cdp,
            ];
        }, [0, 0, 0, 0])
        : [0, 0, 0, 0];
    // ── End hypervisor migration effort ───────────────────────────────────────────

    // ── End job-centric effort calculation ────────────────────────────────────────

    const tapeIntegrationHours = tapeIntegrationRequired === 'yes'
        ? effortConfig.tapeIntegration[tapeConnectivityType] || 0
        : 0;

    const distributedDeploymentHours = 0;

    const vsaV13AdjustmentHours = 0; // veeamVersion is always 'v13'

    // ── New VBR installation effort ───────────────────────────────────────────
    // Applies when a new VBR server is being deployed as part of this engagement.
    // Per-lane values are used when placement lanes exist; otherwise the global
    // architecture card values are used (implied single new VBR).
    const newVbrLanes = window.step2VbrLanes.filter((l) => l.type === 'new');
    const impliedNewVbr = useStandardDesignSections && needsNewVbrDeployment() && newVbrLanes.length === 0;

    // ── Edge case: existing VBR replaced by new VBR in Step 2 ────────────────
    // When Step 2 has a new VBR lane AND there are managed canvas jobs, those
    // jobs must be re-created on the new VBR. This is not an upgrade — it's a
    // fresh job configuration pass. Adds implementation effort scaled to job VMs.
    // Only applies in Standard PS mode (isDesignOnly suppresses it, same as all impl).
    //
    // ⚠️  FUTURE WORK — VSA MIGRATION (vsa-migration.js):
    // This edge case is currently the manual workaround for Windows VBR → Linux VSA (v13)
    // migrations: the consultant deletes the existing Windows VBR in Step 2 and adds a
    // new Linux VSA lane, which triggers this logic.
    //
    // When the VSA Migration section in Step 2 is fully implemented (currently "Coming Soon"
    // in the UI — see vsa-migration.js), it will have its own dedicated effort formula that
    // covers VBR replacement + job re-configuration + cutover. At that point:
    //   1. Remove or gate this `vbrJobReimplementationHours` block to avoid double-counting
    //      with the VSA migration effort.
    //   2. The VSA migration formula in vsa-migration.js (VSA_MIGRATION_HOURS_PER_VBR) should
    //      absorb the job re-configuration effort currently modelled here.
    const vbrJobReimplementationHours = (newVbrLanes.length > 0 && _vbrJobVmCount > 0)
        ? blockHours(effortConfig.implementationBlocks, _vbrJobVmCount)
        : 0;
    // Expansion hours — scope expansion of existing jobs (delta VMs from Step 2 matrix stepper).
    // Summed across all serverManaged job rows where targetCount > currentCount.
    // Each job's delta is accumulated then run through expansionBlocks as a single total.
    let _expansionDeltaTotal = 0;
    const expansionScopes = []; // for SOW narrative: { workloadType, jobType, currentCount, targetCount }
    Object.entries(window.step2MatrixState?.serverManaged || {}).forEach(([instanceId, serverState]) => {
        // Resolve server label from namedServerInstances across all sites
        const serverLabel = (() => {
            for (const site of sites) {
                const sv = (site.veeamServers || []).find((s) => s.instanceId === instanceId);
                if (sv) return sv.name || sv.type || instanceId;
                // Synthetic VBR
                const m = instanceId.match(/^env-vbr-(\S+)-(\d+)$/);
                if (m && String(site.id) === m[1]) {
                    return (site.existingEnvironmentCustomLabels?.vbrServers) || 'VBR Server';
                }
            }
            return instanceId;
        })();
        Object.entries(serverState).forEach(([jobKey, jobState]) => {
            if (jobKey === '_self' || !jobState?.expansion) return;
            if (jobState.expansion.enabled !== true) return;
            const { targetCount, currentCount, poolCount } = jobState.expansion;
            const effective = targetCount ?? poolCount ?? 0;
            const delta = Math.max(0, effective - (currentCount || 0));
            if (delta <= 0) return;
            _expansionDeltaTotal += delta;
            // Derive workload + job type labels from jobKey: "{swId}-{jobType}"
            const [, rawJobType] = jobKey.match(/^\d+-(.+)$/) || [null, jobKey];
            expansionScopes.push({ serverLabel, rawJobType, currentCount: currentCount || 0, targetCount: effective });
        });
    });
    const expansionHours = _expansionDeltaTotal > 0
        ? blockHours(effortConfig.expansionBlocks, _expansionDeltaTotal)
        : 0;

    const newVbrInstallHours = useStandardDesignSections
        ? newVbrLanes.length > 0
            ? newVbrLanes.reduce((sum, lane) => {
                const base    = lane.deploymentType === 'physical'
                    ? effortConfig.newVbrInstall.physical
                    : effortConfig.newVbrInstall.virtual;
                const linuxAdj = lane.osPlatform === 'linux' ? effortConfig.architectureAdjustments.linux : 0;
                return sum + base + linuxAdj;
            }, 0)
            : impliedNewVbr
                ? (() => {
                    const base     = architecture.deploymentType === 'physical'
                        ? effortConfig.newVbrInstall.physical
                        : effortConfig.newVbrInstall.virtual;
                    const linuxAdj = architecture.osPlatform === 'linux' ? effortConfig.architectureAdjustments.linux : 0;
                    return base + linuxAdj;
                })()
                : 0
        : 0;

    // VBfCloud appliance install — flat 4h per new appliance lane added in Step 2.
    // VBfCloud appliance install — flat hours per appliance lane defined in Step 2.
    // Primary source: explicit lanes from window.step2CloudLanes (user added in Step 2).
    // Fallback: one implied appliance per unique cloud provider with workloads in scope —
    // handles states loaded from JSON or imported before the Step 2 gate was in place.
    const newVbfCloudLanes = Array.isArray(window.step2CloudLanes)
        ? window.step2CloudLanes.filter(l => ['vbfaws', 'vbfaz', 'vbfgc'].includes(l.cloudProvider))
        : [];
    const _vbfCloudApplianceCount = newVbfCloudLanes.length > 0
        ? newVbfCloudLanes.length
        : new Set(
            scopedUnprotectedWorkloads
                .filter(w => isVbfCloudManagedWorkload(w.workloadType))
                .map(w => getVbfCloudProvider(w.workloadType))
                .filter(Boolean)
          ).size;
    const newVbfCloudInstallHours = useStandardDesignSections && _vbfCloudApplianceCount > 0
        ? _vbfCloudApplianceCount * effortConfig.newVbfCloudInstall
        : 0;

    // VB365 server install — flat hours per new VB365 lane defined in Step 2.
    // Fallback: one implied server if VB365 workloads exist but no lanes are defined
    // (handles JSON-imported states that predate the Step 2 lane system).
    const newVb365Lanes = Array.isArray(window.step2Vb365Lanes)
        ? window.step2Vb365Lanes.filter(l => l.type === 'new')
        : [];
    const _vb365NewServerCount = newVb365Lanes.length > 0
        ? newVb365Lanes.length
        : (_vb365DesignVms > 0 && (!Array.isArray(window.step2Vb365Lanes) || window.step2Vb365Lanes.length === 0))
            ? 1
            : 0;
    const newVb365InstallHours = useStandardDesignSections && _vb365NewServerCount > 0
        ? _vb365NewServerCount * effortConfig.newVb365Install
        : 0;

    // Linux and distributed topology adjustments — Linux is now captured in newVbrInstallHours
    // (per-lane or implied), so only distributed topology remains here.
    const architecturePlatformHours = useStandardDesignSections
        ? (siteCount > 1 && architecture.siteTopology === 'distributed' ? effortConfig.architectureAdjustments.distributedSiteTopology : 0)
        : 0;

    const simpleModeCoreHours = kickoffHours + customerKickoffHours + discoveryHours
        + designHours + vbrRepoMigrationDesignHours + vb365RepoMigrationDesignHours
        + advisoryHours
        + (isDesignOnly ? 0 : implementationHours);

    const enablementHours = enablementRequired === 'yes' ? 6 : 0;
    const uatHours = uatRequired === 'yes' ? postDeployScopeCount * effortConfig.uatHours.perHypervisor : 0;
    const asBuiltHours = asBuiltRequired === 'yes' ? effortConfig.asBuiltDocumentation.flat : 0;
    const drRunbookHours = drRunbookRequired === 'yes' ? effortConfig.drRunbook.flat : 0;
    const sopHours = sopRequired === 'yes' ? effortConfig.sop.flat : 0;
    const handoverWorkshopHours = effortConfig.handoverWorkshop.flat;

    const subTotalHours = kickoffHours + customerKickoffHours + discoveryHours
        + designHours + vbrRepoMigrationDesignHours + vb365RepoMigrationDesignHours
        + advisoryHours
        + (isDesignOnly ? 0 :
            + upgradePreFlightHours
            + upgradeHealthCheckHours
            + jobValidationHours
            + implementationHours
            + vbrJobReimplementationHours
            + veeamOneHours
            + veeamOneOptionHours
            + vroHours
            + newVbrInstallHours
            + upgradeComponentHours
            + sureBackupHours
            + backupCopyHours
            + replicationHours
            + sureReplicaHours
            + cdpReplicationHours
            + tapeIntegrationHours
            + distributedDeploymentHours
            + vsaV13AdjustmentHours
            + architecturePlatformHours
            + hypervisorMigrationHours
            + vb365RepoMigrationHours
            + vbrRepoMigrationHours
            + migrationTargetImplHours
            + migrationTargetSureBackupHours
            + migrationTargetBackupCopyHours
            + migrationTargetReplicationHours
            + migrationTargetCdpHours
            + expansionHours)
        + enablementHours
        + uatHours
        + asBuiltHours
        + drRunbookHours
        + sopHours
        + handoverWorkshopHours;

    // ── Project management and contingency overhead ───────────────────────────
    // Both are computed as a percentage of the activity subtotal so that they
    // scale naturally with engagement size and are included in the grand total.
    const projectManagementHours = Math.round(subTotalHours * effortConfig.projectManagement.rate);
    const contingencyHours       = Math.round(subTotalHours * effortConfig.contingency.rate);
    const baseHours              = subTotalHours + projectManagementHours + contingencyHours;

    const estimatedHours = Math.max(1, Math.round(baseHours));
    const estimatedDays = Math.ceil(estimatedHours / 8);

    const platformHours = kickoffHours + customerKickoffHours + discoveryHours
        + designHours + vbrRepoMigrationDesignHours + vb365RepoMigrationDesignHours
        + (isDesignOnly ? 0 : implementationHours + vbrJobReimplementationHours
            + newVbrInstallHours + distributedDeploymentHours
            + vsaV13AdjustmentHours + architecturePlatformHours);

    // simpleModeCoreHours covers fixed baseline, design, implementation, skills transfer,
    // and site effort. For VDC Vault Migration these are suppressed from the Platform
    // category, so roll them into migrationHours so they appear in the Migration card.
    const migrationHours = simpleModeCoreHours;

    const protectionHours = isDesignOnly ? 0 : (sureBackupHours
        + backupCopyHours
        + replicationHours
        + sureReplicaHours
        + cdpReplicationHours
        + tapeIntegrationHours);

    const operationsHours = isDesignOnly ? 0 : (veeamOneHours + veeamOneOptionHours);

    const orchestrationHours = isDesignOnly ? 0 : vroHours;

    const postDeployCareHours = enablementHours
        + uatHours
        + asBuiltHours
        + drRunbookHours
        + sopHours
        + handoverWorkshopHours;

    const showPlatformCategory = useStandardDesignSections;
    const showProtectionCategory = useStandardDesignSections;
    const showOperationsCategory = useStandardDesignSections;
    const showOrchestrationCategory = useStandardDesignSections;

    const categoryBreakdown = [
        ...(showPlatformCategory
            ? [
                {
                    id: 'platform',
                    title: 'Platform',
                    description: 'Core platform delivery, implementation, site topology, and architecture adjustments.',
                    hours: Math.round(platformHours)
                }
            ]
            : []),
        ...(showProtectionCategory
            ? [
                {
                    id: 'protection',
                    title: 'Protection',
                    description: 'Backup verification, copy, replication, CDP, tape, and resilience controls.',
                    hours: Math.round(protectionHours)
                }
            ]
            : []),
        ...(showOperationsCategory
            ? [
                {
                    id: 'operations',
                    title: 'Operations',
                    description: 'Veeam ONE, dashboards, reporting, alarms, and operational tooling.',
                    hours: Math.round(operationsHours)
                }
            ]
            : []),
        ...(showOrchestrationCategory
            ? [
                {
                    id: 'orchestration',
                    title: 'Orchestration',
                    description: 'Recovery orchestration effort driven by the scoped VRO options in Step 2.',
                    hours: Math.round(orchestrationHours)
                }
            ]
            : []),
        ...(useRepoMigrationMode
            ? [
                {
                    id: 'migration',
                    title: 'Migration',
                    description: 'Fixed baseline, design, implementation, site, and vault migration sizing effort.',
                    hours: Math.round(migrationHours)
                }
            ]
            : []),
        ...(hypervisorMigrationRequired
            ? [
                {
                    id: 'hypervisor-migration',
                    title: 'Hypervisor Migration',
                    description: 'Decommission of source platform jobs, new backup job implementation on target platform, proxy reconfiguration, and post-cutover restore testing.',
                    hours: Math.round(hypervisorMigrationHours)
                }
            ]
            : []),
        ...(vb365RepoMigrationRequired && !isDesignOnly
            ? [
                {
                    id: 'vb365-repo-migration',
                    title: 'VB365 Repository Migration',
                    description: 'Assessment, reconfiguration, and verification effort for migrating VB365 backup repositories between storage types.',
                    hours: Math.round(vb365RepoMigrationHours)
                }
            ]
            : []),
        ...(vbrRepoMigrationRequired && !isDesignOnly
            ? [
                {
                    id: 'vbr-repo-migration',
                    title: 'VBR Repository Migration',
                    description: 'Assessment, target configuration, job reconfiguration, data migration, and validation effort for migrating VBR backup repositories to the configured target (Block/BLOB Performance Tier or VDC Vault).',
                    hours: Math.round(vbrRepoMigrationHours)
                }
            ]
            : []),
        {
            id: 'post-deploy-care',
            title: 'Post-Deploy Care',
            description: 'Enablement, UAT, As-Built documentation, and DR runbook effort driven by explicit Step 2 post-deploy selections.',
            hours: Math.round(postDeployCareHours)
        },
        {
            id: 'project-management',
            title: 'Project Management',
            description: `Coordination, stakeholder communication, status reporting, and risk management across all phases (${Math.round(effortConfig.projectManagement.rate * 100)}% of activity subtotal).`,
            hours: projectManagementHours
        },
        {
            id: 'contingency',
            title: 'Contingency',
            description: `Risk buffer to accommodate scope uncertainties and unforeseen complexity (${Math.round(effortConfig.contingency.rate * 100)}% of activity subtotal).`,
            hours: contingencyHours
        }
    ];

    // ── Phase breakdown (Plan & Design → Implementation → Handover) ─────────────
    // Re-distributes the same hour variables into customer-facing project phases.
    // Hours are always recommended (non-adjusted) values; the phase total may differ
    // from totalAdjustedHours when category adjustments are active.
    const phPlanDesign = Math.round(kickoffHours + customerKickoffHours + discoveryHours + designHours + vbrRepoMigrationDesignHours + vb365RepoMigrationDesignHours);
    const handoverSignOffHours = 1;
    const phHandover   = Math.round(enablementHours + uatHours + asBuiltHours + drRunbookHours + sopHours + handoverWorkshopHours + handoverSignOffHours);

    const phaseBreakdown = (() => {
        if (useRepoMigrationMode) {
            // Vault mode: Discovery | Migration | Handover
            const phMigration = isDesignOnly ? 0 : Math.round(
                implementationHours
                + vb365RepoMigrationHours + vbrRepoMigrationHours
            );
            const phases = [
                {
                    id: 'discovery', title: 'Discovery', hours: phPlanDesign,
                    description: 'Kick-off, discovery, and solution design.',
                    subItems: [
                        { id: 'disc:kickoff', label: 'Internal Kick-off', hours: Math.round(kickoffHours) },
                        { id: 'disc:customer-kickoff', label: 'Customer (external) project kick-off', hours: Math.round(customerKickoffHours) },
                        ...(discoveryHours > 0 ? [{ label: 'Discovery effort', hours: Math.round(discoveryHours) }] : []),
                        ...(designHours > 0 ? [{ label: 'Solution design', hours: Math.round(designHours) }] : []),
                    ].filter(i => i.hours > 0),
                },
                {
                    id: 'migration', title: 'Migration', hours: phMigration,
                    description: 'Repository assessment, target configuration, data migration, job reconfiguration, and post-migration validation.',
                    subItems: [
                        ...(vb365RepoMigrationHours > 0 ? [{ label: 'VB365 repository migration', hours: Math.round(vb365RepoMigrationHours) }] : []),
                        ...(vbrRepoMigrationHours   > 0 ? [{ label: 'VBR repository migration',   hours: Math.round(vbrRepoMigrationHours)   }] : []),
                        ...(implementationHours > 0 ? [{ label: 'Job reconfiguration', hours: Math.round(implementationHours) }] : []),
                    ].filter(i => i.hours > 0),
                },
            ];
            if (phHandover > 0) {
                phases.push({
                    id: 'handover', title: 'Handover', hours: phHandover,
                    description: 'Documentation, acceptance testing, and project closure.',
                    subItems: [
                        ...(uatHours > 0 ? [{ label: 'UAT & acceptance', hours: Math.round(uatHours) }] : []),
                        ...(asBuiltHours > 0 ? [{ id: 'hand:as-built', label: 'As-Built documentation', hours: Math.round(asBuiltHours) }] : []),
                        ...(drRunbookHours > 0 ? [{ id: 'hand:dr-runbook', label: 'DR runbook', hours: Math.round(drRunbookHours) }] : []),
                        ...(sopHours > 0 ? [{ id: 'hand:sop', label: 'Standard Operating Procedures', hours: Math.round(sopHours) }] : []),
                        ...(enablementHours > 0 ? [{ id: 'hand:skills-transfer', label: 'Skills Transfer Workshop', hours: Math.round(enablementHours) }] : []),
                        { id: 'hand:workshop', label: 'Handover workshop', hours: Math.round(handoverWorkshopHours) },
                        { id: 'hand:signoff', label: 'Sign-off', hours: handoverSignOffHours },
                    ].filter(i => i.hours > 0),
                });
            }
            return phases;
        }

        // Standard engagement: P&D | Implementation | Handover
        // Implementation sub-items mirror the capability categories
        const phCorePlatform = isDesignOnly ? 0 : Math.round(
            implementationHours + vbrJobReimplementationHours + newVbrInstallHours
            + newVbfCloudInstallHours + newVb365InstallHours
            + distributedDeploymentHours + vsaV13AdjustmentHours + architecturePlatformHours
        );
        const phPreFlight      = isDesignOnly ? 0 : Math.round(upgradePreFlightHours);
        const phHcWork         = isDesignOnly ? 0 : Math.round(upgradeHealthCheckHours + jobValidationHours);
        const phUpgradeWork    = isDesignOnly ? 0 : Math.round(upgradeComponentHours);
        const phHcUpgrade      = phHcWork + phUpgradeWork; // kept for phImplementation sum
        const phProtection = isDesignOnly ? 0 : Math.round(
            sureBackupHours + backupCopyHours
            + replicationHours + sureReplicaHours + cdpReplicationHours + tapeIntegrationHours
        );
        const phOperations    = isDesignOnly ? 0 : Math.round(veeamOneHours + veeamOneOptionHours);
        const phOrchestration = isDesignOnly ? 0 : Math.round(vroHours);
        const phHvm = isDesignOnly ? 0 : Math.round(
            hypervisorMigrationHours + migrationTargetImplHours
            + migrationTargetSureBackupHours + migrationTargetBackupCopyHours
            + migrationTargetReplicationHours + migrationTargetCdpHours
        );
        const phVb365RepoMigration = isDesignOnly ? 0 : Math.round(vb365RepoMigrationHours);
        const phVbrRepoMigration   = isDesignOnly ? 0 : Math.round(vbrRepoMigrationHours);
        const phAdvisory = Math.round(advisoryHours);
        const phImplementation = phCorePlatform + phPreFlight + phHcUpgrade + phProtection
            + phOperations + phOrchestration + phHvm + phVb365RepoMigration + phVbrRepoMigration + phAdvisory;

        const implSubItems = [
            // ── Platform deployment ───────────────────────────────────────────────
            ...(newVbrInstallHours > 0 && !isDesignOnly              ? [{ id: 'impl:vbr-install',    label: 'VBR server installation & base configuration',       hours: Math.round(newVbrInstallHours) }] : []),
            ...(_vbrImplHoursAdj > 0                                 ? [{ id: 'impl:backup-jobs',   label: 'Backup job implementation',                            hours: _vbrImplHoursAdj }] : []),
            ...(vbrJobReimplementationHours > 0 && !isDesignOnly     ? [{ id: 'impl:job-reconfig',  label: 'Backup job reconfiguration (replacement VBR)',         hours: Math.round(vbrJobReimplementationHours) }] : []),
            ...(expansionHours > 0 && !isDesignOnly                  ? [{ id: 'impl:expansion',     label: 'Existing job scope expansion',                         hours: Math.round(expansionHours) }] : []),
            ...(newVbfCloudInstallHours > 0 && !isDesignOnly         ? [{ id: 'impl:vbfcloud-install', label: 'VBfAWS / VBfAz / VBfGC appliance deployment',      hours: Math.round(newVbfCloudInstallHours) }] : []),
            ...(_vbfCloudImplHoursAdj > 0                            ? [{ id: 'impl:vbfcloud-jobs', label: 'Cloud-native backup policy configuration',             hours: _vbfCloudImplHoursAdj }] : []),
            ...(newVb365InstallHours > 0 && !isDesignOnly             ? [{ id: 'impl:vb365-install', label: 'VB365 server deployment & connector configuration',    hours: Math.round(newVb365InstallHours) }] : []),
            ...(_vb365ImplHoursAdj > 0                               ? [{ id: 'impl:vb365-jobs', label: 'Microsoft 365 backup policy configuration',              hours: _vb365ImplHoursAdj }] : []),
            ...(architecturePlatformHours > 0 && !isDesignOnly       ? [{ id: 'impl:multisite',     label: 'Multi-site architecture configuration',                hours: Math.round(architecturePlatformHours) }] : []),
            // ── Health check / upgrade ────────────────────────────────────────────
            ...(phPreFlight > 0             ? [{ id: 'impl:preflight', label: 'Pre-flight checklist & validation', hours: phPreFlight }] : []),
            ...(upgradeHealthCheckHours > 0 ? [{ id: 'impl:hc-assessment', label: 'Health check assessment & reporting', hours: Math.round(upgradeHealthCheckHours) }] : []),
            ...(jobValidationHours > 0      ? [{ id: 'impl:job-validation', label: 'Job & policy validation', hours: Math.round(jobValidationHours) }] : []),
            ...(phUpgradeWork > 0           ? [{ id: 'impl:component-upgrade', label: 'Component upgrade', hours: phUpgradeWork }] : []),
            // ── Protection policies ───────────────────────────────────────────────
            ...(sureBackupHours > 0     ? [{ id: 'impl:surebackup', label: 'SureBackup verification configuration', hours: Math.round(sureBackupHours) }] : []),
            ...(backupCopyHours > 0     ? [{ id: 'impl:backup-copy', label: 'Backup Copy configuration', hours: Math.round(backupCopyHours) }] : []),
            ...(replicationHours > 0    ? [{ id: 'impl:replication', label: 'Replication job configuration', hours: Math.round(replicationHours) }] : []),
            ...(sureReplicaHours > 0    ? [{ id: 'impl:sureplica', label: 'SureReplica setup', hours: Math.round(sureReplicaHours) }] : []),
            ...(cdpReplicationHours > 0 ? [{ id: 'impl:cdp', label: 'CDP Replication configuration', hours: Math.round(cdpReplicationHours) }] : []),
            ...(tapeIntegrationHours > 0 ? [{ id: 'impl:tape', label: 'Tape integration & media management', hours: Math.round(tapeIntegrationHours) }] : []),
            // ── Operations & orchestration ────────────────────────────────────────
            ...(veeamOneHours > 0       ? [{ id: 'impl:veeamone', label: 'Veeam ONE deployment & dashboards', hours: Math.round(veeamOneHours) }] : []),
            ...(veeamOneOptionHours > 0 ? [{ id: 'impl:veeamone-opts', label: 'Veeam ONE custom integrations', hours: Math.round(veeamOneOptionHours) }] : []),
            ...(vroHours > 0            ? [{ id: 'impl:vro', label: 'Recovery orchestration (VRO)', hours: Math.round(vroHours) }] : []),
            // ── Migration ─────────────────────────────────────────────────────────
            ...(hypervisorMigrationHours > 0      ? [{ id: 'impl:hvm-reconfig', label: 'Hypervisor migration — job reconfiguration', hours: Math.round(hypervisorMigrationHours) }] : []),
            ...(migrationTargetImplHours > 0       ? [{ id: 'impl:hvm-target', label: 'Hypervisor migration — target platform implementation', hours: Math.round(migrationTargetImplHours) }] : []),
            ...((migrationTargetSureBackupHours + migrationTargetBackupCopyHours + migrationTargetReplicationHours + migrationTargetCdpHours) > 0
                ? [{ id: 'impl:hvm-protection', label: 'Hypervisor migration — target protection policies', hours: Math.round(migrationTargetSureBackupHours + migrationTargetBackupCopyHours + migrationTargetReplicationHours + migrationTargetCdpHours) }] : []),
            ...(phVb365RepoMigration > 0 ? [{ id: 'impl:vb365-repo', label: 'VB365 repository migration', hours: phVb365RepoMigration }] : []),
            ...(phVbrRepoMigration > 0   ? [{ id: 'impl:vbr-repo', label: 'VBR repository migration', hours: phVbrRepoMigration }] : []),
            // ── Advisory ─────────────────────────────────────────────────────────
            ...(phAdvisory > 0 ? [{ id: 'impl:advisory', label: 'Implementation advisory (shadow)', hours: phAdvisory }] : []),
        ].filter(i => i.hours > 0);

        const phDiscovery = Math.round(kickoffHours + customerKickoffHours + discoveryHours);
        const designWorkshopHours = 3;
        const designSignOffHours  = 1;
        const hcDesignHours = hcActive ? effortConfig.upgradeHealthCheck.designHours : 0;
        const phDesign = Math.round(designHours + designWorkshopHours + designSignOffHours + hcDesignHours + vbrRepoMigrationDesignHours + vb365RepoMigrationDesignHours);

        const phases = [
            {
                id: 'discovery', title: 'Discovery', hours: phDiscovery,
                description: 'Project kick-off, discovery workshop, and requirements gathering.',
                subItems: [
                    { id: 'disc:kickoff', label: 'Internal Kick-off', hours: Math.round(kickoffHours) },
                    { id: 'disc:customer-kickoff', label: 'Customer (external) project kick-off', hours: Math.round(customerKickoffHours) },
                    ...(discoveryWorkloadHours > 0  ? [{ id: 'disc:workload', label: 'Workload & environment discovery', hours: Math.round(discoveryWorkloadHours) }] : []),
                    ...(componentDiscoveryHours > 0 ? [{ id: 'disc:component', label: 'Veeam component discovery & review', hours: Math.round(componentDiscoveryHours) }] : []),
                    ...(discoverySiteHours > 0      ? [{ id: 'disc:site-planning', label: `Site planning (${siteCount} site${siteCount !== 1 ? 's' : ''})`, hours: Math.round(discoverySiteHours) }] : []),
                ].filter(i => i.hours > 0),
            },
        ];
        if (phDesign > 0) {
            const upgradeDesignOnly = upgradeActive && !_hW;
            phases.push({
                id: 'design', title: 'Design', hours: phDesign,
                description: upgradeDesignOnly
                    ? 'Upgrade procedure, pre-flight checklist, and sign-off documentation.'
                    : 'Architecture design documentation.',
                subItems: [
                    ...(_vbrDesignHours > 0 ? [{ id: 'des:vbr-design', label: 'VBR design (backup infrastructure)', hours: _vbrDesignHours }] : []),
                    ...(_vb365DesignHours > 0 ? [{ id: 'des:vb365-design', label: 'VB365 design (M365 workloads)', hours: _vb365DesignHours }] : []),
                    ...(_kastenDesignHours > 0 ? [{ id: 'des:kasten-design', label: 'Kasten K10 design (Kubernetes)', hours: _kastenDesignHours }] : []),
                    ...(_vbfCloudDesignHours > 0 ? [{ id: 'des:vbfcloud-design', label: 'Cloud-native backup design (VBfAWS / VBfAz / VBfGC)', hours: _vbfCloudDesignHours }] : []),
                    ...(_voneDesignHours > 0 ? [{ id: 'des:vone-design', label: 'Veeam ONE design (monitoring & alarms)', hours: _voneDesignHours }] : []),
                    ...(_vroDesignHours > 0 ? [{ id: 'des:vro-design', label: 'Veeam Recovery Orchestrator design (recovery plans)', hours: _vroDesignHours }] : []),
                    ...(hcDesignHours > 0 ? [{ id: 'des:hc-scope', label: 'Health check scope & reporting plan', hours: hcDesignHours }] : []),
                    ...(vbrRepoMigrationDesignHours > 0 ? [{ id: 'des:vbr-repo-design', label: 'VBR repository migration design', hours: Math.round(vbrRepoMigrationDesignHours) }] : []),
                    ...(vb365RepoMigrationDesignHours > 0 ? [{ id: 'des:vb365-repo-design', label: 'VB365 repository migration design', hours: Math.round(vb365RepoMigrationDesignHours) }] : []),
                    ...(upgradeDesignOnly
                        ? (designWorkshopHours > 0 ? [{ id: 'des:workshop', label: 'Upgrade procedure & checklist', hours: designWorkshopHours }] : [])
                        : [{ id: 'des:workshop', label: 'Design Review Workshop', hours: designWorkshopHours }]),
                    { id: 'des:signoff', label: 'Sign-off', hours: designSignOffHours },
                ].filter(i => i.hours > 0),
            });
        }
        if (phImplementation > 0) {
            const implPhaseTitle = 'Implementation';
            const implPhaseDesc = 'Deployment and configuration of all Veeam components, protection policies, and services in scope.';
            phases.push({
                id: 'implementation', title: implPhaseTitle, hours: phImplementation,
                description: implPhaseDesc,
                subItems: implSubItems,
            });
        }
        if (phHandover > 0) {
            phases.push({
                id: 'handover', title: 'Handover', hours: phHandover,
                description: 'Acceptance testing, documentation, skills transfer, and project closure.',
                subItems: [
                    ...(uatHours > 0 ? [{ id: 'hand:uat', label: 'User acceptance testing (UAT)', hours: Math.round(uatHours) }] : []),
                    ...(asBuiltHours > 0 ? [{ id: 'hand:as-built', label: 'As-Built documentation', hours: Math.round(asBuiltHours) }] : []),
                    ...(drRunbookHours > 0 ? [{ id: 'hand:runbook', label: 'DR runbook', hours: Math.round(drRunbookHours) }] : []),
                    ...(sopHours > 0 ? [{ id: 'hand:sop', label: 'Standard Operating Procedures', hours: Math.round(sopHours) }] : []),
                    ...(enablementHours > 0 ? [{ id: 'hand:enablement', label: 'Skills Transfer Workshop', hours: Math.round(enablementHours) }] : []),
                    { id: 'hand:workshop', label: 'Handover workshop', hours: Math.round(handoverWorkshopHours) },
                    { id: 'hand:signoff', label: 'Sign-off', hours: handoverSignOffHours },
                ].filter(i => i.hours > 0),
            });
        }
        return phases;
    })();

    // ── Project Overhead phase (PM + contingency — each independently excludable) ──
    if (projectManagementHours > 0 || contingencyHours > 0) {
        const activePmHours          = window.pmExcluded          ? 0 : projectManagementHours;
        const activeContingencyHours = window.contingencyExcluded ? 0 : contingencyHours;
        phaseBreakdown.push({
            id: 'pm-overhead',
            title: 'Project Overhead',
            hours: activePmHours + activeContingencyHours,
            description: 'Project management and contingency — exclude either line item to remove it from the total.',
            subItems: [
                ...(projectManagementHours > 0 ? [{ id: 'pm',          label: 'Project management', hours: projectManagementHours, excluded: !!window.pmExcluded }]          : []),
                ...(contingencyHours > 0        ? [{ id: 'contingency', label: 'Contingency',        hours: contingencyHours,        excluded: !!window.contingencyExcluded }] : []),
            ],
        });
    }

    const hypervisorSummary = hypervisorCounts.length
        ? hypervisorCounts.map((item) => `${item.label} (${getWorkloadQuantitySummary(item.hypervisor, item.count)})`).join(', ')
        : 'None selected';
    const pluginSummary = pluginQuantities.length
        ? pluginQuantities.map((item) => `${item.label} (${item.quantity})`).join(', ')
        : 'None selected';
    const cloudSummary = cloudWorkloadCounts.length
        ? cloudWorkloadCounts.map((item) => `${item.label} (${getWorkloadQuantitySummary(item.workload, item.count)})`).join(', ')
        : 'None selected';
    const saasSummary = saasWorkloadCounts.length
        ? saasWorkloadCounts.map((item) => `${item.label} (${getWorkloadQuantitySummary(item.workload, item.count)})`).join(', ')
        : 'None selected';
    const containerSummary = containerWorkloadCounts.length
        ? containerWorkloadCounts.map((item) => `${item.label} (${getWorkloadQuantitySummary(item.workload, item.count)})`).join(', ')
        : 'None selected';
    const agentSummary = agentCounts.length
        ? agentCounts.map((item) => `${item.label} (${getWorkloadQuantitySummary(item.agent, item.count)})`).join(', ')
        : 'None selected';
    const unstructuredDataSummary = unstructuredDataCounts.length
        ? unstructuredDataCounts
            .map((item) => useInstanceMetrics
                ? `${item.label} (${getProtectedTbSummary(item.protectedTb)} / ${item.count} instances)`
                : `${item.label} (${getProtectedTbSummary(item.protectedTb)})`)
            .join(', ')
        : 'None selected';
    const veeamOneOptionsSummary = veeamOneOptions.length
        ? veeamOneOptions.map((option) => veeamOneOptionLabels[option]).join(', ')
        : 'None selected';

    // Unified canvas: isExistingEnvironmentInScope() always false.
    // Build split machine / TB display for the summary card.
    const _hasMachines = totalWorkloadCount > 0;
    const _hasTb       = totalUnstructuredDataProtectedTb > 0;
    let scopedQuantityValue;
    let scopeMetricMeta;
    if (useRepoMigrationMode) {
        scopedQuantityValue = getProtectedTbSummary(getRepoMigrationTotalProtectedTb(sites));
        scopeMetricMeta     = getScopeMetricMetaText(engagementType);
    } else if (_hasMachines && _hasTb) {
        scopedQuantityValue = `${totalWorkloadCount} machines · ${formatProtectedTbValue(totalUnstructuredDataProtectedTb)} TB`;
        scopeMetricMeta     = 'Machine + unstructured data workloads across all sites';
    } else if (_hasTb) {
        scopedQuantityValue = `${formatProtectedTbValue(totalUnstructuredDataProtectedTb)} TB`;
        scopeMetricMeta     = 'Unstructured data (NAS / object storage) across all sites';
    } else {
        scopedQuantityValue = String(totalWorkloadCount);
        scopeMetricMeta     = getScopeMetricMetaText(engagementType);
    }
    const siteScopeMetricLabel = getSiteScopeMetricLabel(engagementType);

    // Prune adjustments for categories that are no longer in scope
    const activeCategoryIds = new Set(categoryBreakdown.map((c) => c.id));
    for (const id of [...window.categoryHourAdjustments.keys()]) {
        if (!activeCategoryIds.has(id)) {
            window.categoryHourAdjustments.delete(id);
        }
    }

    // Prune sub-item adjustments for sub-items that are no longer in the current phase breakdown
    const activeSubItemIds = new Set(
        phaseBreakdown.flatMap(p => (p.subItems || []).map(s => s.id).filter(Boolean))
    );
    for (const id of [...window.subItemHourAdjustments.keys()]) {
        if (!activeSubItemIds.has(id)) {
            window.subItemHourAdjustments.delete(id);
        }
    }

    // Phase-level adjustments are no longer user-editable — clear any stale values
    window.phaseHourAdjustments.clear();

    // Expose per-phase recommended hours for inline DOM updates (no re-render needed)
    window.phaseRecommendedHours = {};
    phaseBreakdown.forEach(p => {
        window.phaseRecommendedHours[p.id] = p.hours;
        if (p.subItems) p.subItems.forEach(s => {
            if (s.id) window.phaseRecommendedHours[s.id] = s.hours;
        });
    });

    // Sub-item adjustments are the source of truth for the estimate total.
    // Phase totals are derived from their adjusted sub-item sums.
    // PM/contingency overhead respects include/exclude AND subItemHourAdjustments.
    const _mainPhasesList = phaseBreakdown.filter(p => p.id !== 'pm-overhead');
    const _pmOverheadPhase = phaseBreakdown.find(p => p.id === 'pm-overhead');
    const subItemRecMainTotal = _mainPhasesList.reduce((phaseSum, phase) =>
        phaseSum + (phase.subItems || []).reduce((s, sub) => s + sub.hours, 0), 0);
    const subItemAdjMainTotal = _mainPhasesList.reduce((phaseSum, phase) =>
        phaseSum + (phase.subItems || []).reduce((s, sub) =>
            s + (window.subItemHourAdjustments.has(sub.id)
                ? window.subItemHourAdjustments.get(sub.id)
                : sub.hours), 0), 0);
    const pmOverheadRecHours = _pmOverheadPhase
        ? (_pmOverheadPhase.subItems || []).reduce((s, sub) => sub.excluded ? s : s + sub.hours, 0)
        : 0;
    const pmOverheadHours = _pmOverheadPhase
        ? (_pmOverheadPhase.subItems || []).reduce((s, sub) => {
            if (sub.excluded) return s;
            return s + (window.subItemHourAdjustments.has(sub.id)
                ? window.subItemHourAdjustments.get(sub.id)
                : sub.hours);
        }, 0)
        : 0;
    // Both totals derived from the same phase sub-item sums so rounding never causes a phantom delta.
    const totalRecommendedHours = subItemRecMainTotal + pmOverheadRecHours;
    const totalAdjustedHours    = subItemAdjMainTotal + pmOverheadHours;
    const totalHoursDelta       = totalAdjustedHours - totalRecommendedHours;
    const hasAdjustments        = totalHoursDelta !== 0;
    const displayHours          = totalAdjustedHours;
    const effectiveDelta        = totalHoursDelta;
    const effectiveHasAdj  = effectiveDelta !== 0;
    const displayDays = Math.ceil(displayHours / 8);
    // Expose for inline DOM updates in the sub-item input handler
    window.lastTotalRecommendedHours = totalRecommendedHours;
    window.lastPmOverheadHours       = pmOverheadHours;
    const adjustmentTagClass = effectiveDelta > 0
        ? 'results-metric-card__adj-tag--increase'
        : 'results-metric-card__adj-tag--decrease';
    const adjustmentTagHtml = effectiveHasAdj
        ? ` &middot; <span class="results-metric-card__adj-tag ${adjustmentTagClass}">${effectiveDelta > 0 ? '+' : ''}${effectiveDelta} hrs adjusted</span>`
        : '';

    const summaryMetricsMarkup = `
        <section class="results-section">
            <div class="results-section__header">
                <h4 class="results-section__title">Summary</h4>
            </div>
            <div class="results-metric-grid">
                <article class="results-metric-card results-metric-card--highlight">
                    <span class="results-metric-card__label">Indicative Effort</span>
                    <strong class="results-metric-card__value" id="summary-effort-value">${displayHours} hours</strong>
                    <span class="results-metric-card__meta" id="summary-effort-meta">${displayDays} delivery days${adjustmentTagHtml}</span>
                </article>
                <article class="results-metric-card">
                    <span class="results-metric-card__label">Project</span>
                    <strong class="results-metric-card__value">${escapeHtml(projectName || 'Not provided')}</strong>
                    <span class="results-metric-card__meta">${escapeHtml(engagementTypeLabels[engagementType])} • ${escapeHtml(getImplementationModeLabel(implementationMode))}</span>
                </article>
                <article class="results-metric-card">
                    <span class="results-metric-card__label">${useRepoMigrationMode ? 'Total Migration Data' : 'Scoped Workloads'}</span>
                    <strong class="results-metric-card__value">${scopedQuantityValue}</strong>
                    <span class="results-metric-card__meta">${scopeMetricMeta}</span>
                </article>
                <article class="results-metric-card">
                    <span class="results-metric-card__label">Sites</span>
                    <strong class="results-metric-card__value">${siteCount}</strong>
                    <span class="results-metric-card__meta">${escapeHtml(getSiteScopeSummaryText(engagementType))}</span>
                </article>
            </div>
        </section>
    `;

    const siteBreakdownMarkup = `
        <section class="results-section">
            <div class="results-section__header">
                <span class="results-section__eyebrow">Scope</span>
                <h4 class="results-section__title">Site Breakdown</h4>
            </div>
            <div class="results-site-grid">
                ${siteSummaries.map((site) => {
                    const _siteObjForMetrics = getSiteById(site.id);
                    const _metricsHtml = (() => {
                        if (!useInstanceMetrics || useRepoMigrationMode) {
                            return `<div class="results-site-card__metric">
                                <strong>${getSiteScopeMetricValue(site, engagementType)}</strong>
                                <span>${siteScopeMetricLabel}</span>
                            </div>`;
                        }
                        // Build per-workload-type metrics from source workloads
                        const _srcWl = Array.isArray(_siteObjForMetrics?.sourceWorkloads) ? _siteObjForMetrics.sourceWorkloads : [];
                        const _byType = new Map();
                        _srcWl.forEach((sw) => {
                            const wv = sw.workloadValue || '';
                            const count = Math.max(0, parseInt(sw.totalCount, 10) || 0);
                            if (!count) return;
                            if (!_byType.has(wv)) _byType.set(wv, { count: 0, isTb: Object.hasOwn(unstructuredDataLabels, wv) });
                            _byType.get(wv).count += count;
                        });
                        // Machines first, unstructured data (TB) last
                        const _machineMetrics = [], _tbMetrics = [];
                        _byType.forEach(({ count, isTb }, wv) => {
                            const label = sourceWorkloadLabels[wv] || agentLabels[wv] || wv;
                            const valueStr = isTb ? `${formatProtectedTbValue(count)} TB` : String(count);
                            const div = `<div class="results-site-card__metric"><strong>${escapeHtml(valueStr)}</strong><span>${escapeHtml(label)}</span></div>`;
                            (isTb ? _tbMetrics : _machineMetrics).push(div);
                        });
                        const _allDivs = [..._machineMetrics, ..._tbMetrics];
                        // Fallback for legacy (non-source-workload) sites
                        if (!_allDivs.length) {
                            _allDivs.push(`<div class="results-site-card__metric"><strong>${getSiteScopeMetricValue(site, engagementType)}</strong><span>to protect</span></div>`);
                        }
                        return `<div class="results-site-card__metrics">${_allDivs.join('')}</div>`;
                    })();
                    return `
                    <article class="results-site-card">
                        <div class="results-site-card__top">
                            <div>
                                <span class="results-site-card__label">Site</span>
                                <h5 class="results-site-card__title">${escapeHtml(site.name)}</h5>
                            </div>
                            ${_metricsHtml}
                        </div>
                        ${(() => {
                            const siteObj = getSiteById(site.id);
                            const siteJobs = siteObj && Array.isArray(siteObj.jobs) ? siteObj.jobs : [];
                            const ee = siteObj?.existingEnvironment || {};
                            const repoRows = [];
                            // VBR repos stored as workloads with isRepositoryComponent=true, falling back to legacy existingEnvironment.repositoryServers
                            const siteWorkloads = Array.isArray(siteObj?.workloads) ? siteObj.workloads : [];
                            const repoComponents = siteWorkloads.filter(w => w.isRepositoryComponent);
                            const vbrRepos = repoComponents.length || (parseInt(ee.repositoryServers, 10) || 0);
                            const vb365Repos = parseInt(ee.vb365RepositoryServers, 10) || 0;
                            const vbfRepos = parseInt(ee.vbfCloudRepositoryServers, 10) || 0;
                            if (vbrRepos > 0) repoRows.push({ name: 'VBR Backup Repository', count: vbrRepos });
                            if (vb365Repos > 0) repoRows.push({ name: 'VB365 Repository', count: vb365Repos });
                            if (vbfRepos > 0) repoRows.push({ name: 'VBfCloud Repository', count: vbfRepos });
                            if (siteJobs.length === 0 && repoRows.length === 0) return '';
                            return `<div class="results-site-card__workloads">
                                ${siteJobs.map((job) => {
                                    const typeDef = getJobTypeDef(job.jobType);
                                    const typeLabel = typeDef ? typeDef.label : job.jobType;
                                    const wlDef = getJobWorkloadDef(job.workloadType);
                                    const wlLabel = wlDef ? wlDef.label : (job.workloadType || '—');
                                    const countStr = typeDef?.hasCount !== false && job.count > 0
                                        ? `${job.count} VMs`
                                        : '';
                                    return `<div class="results-workload-row">
                                        <span class="results-workload-row__primary">
                                            <span class="results-workload-row__name">${escapeHtml(typeLabel)}</span>
                                            ${typeDef?.hasCount !== false
                                                ? `<span class="results-workload-row__detail">${escapeHtml(wlLabel)}</span>`
                                                : ''}
                                        </span>
                                        <strong class="results-workload-row__count">${escapeHtml(countStr)}</strong>
                                    </div>`;
                                }).join('')}
                                ${repoRows.map((r) => `<div class="results-workload-row">
                                    <span class="results-workload-row__primary">
                                        <span class="results-workload-row__name">${escapeHtml(r.name)}</span>
                                        <span class="results-workload-row__detail">Current state</span>
                                    </span>
                                    <strong class="results-workload-row__count">${r.count} repo${r.count !== 1 ? 's' : ''}</strong>
                                </div>`).join('')}
                            </div>`;
                        })()}
                    </article>
                `;
                }).join('')}
            </div>
        </section>
    `;

    // §Configuration Snapshot ─────────────────────────────────────────────────────
    // Build a composite project-scope label reflecting all active engagement components.
    // A project can combine multiple work types (e.g. Health Check + Upgrade + New workload protection).
    const projectComponents = [];
    if (_hW) projectComponents.push('New workload protection');
    if (hcActive) projectComponents.push('Health Check');
    if (upgradeActive) projectComponents.push('Component upgrade');
    if (hypervisorMigrationRequired) projectComponents.push('Hypervisor migration');
    if (vbrRepoMigrationRequired) projectComponents.push('VBR repository migration');
    if (vb365RepoMigrationRequired) projectComponents.push('VB365 repository migration');
    if (_hV) projectComponents.push('VDC Vault migration');
    const projectScopeLabel = projectComponents.length > 0
        ? projectComponents.join(' · ')
        : 'No scope configured';

    // Summarise configured managed jobs (existing Veeam — Step 1 canvas) across all sites
    const allJobsForSummary = sites.flatMap((s) => Array.isArray(s.jobs) ? s.jobs : []);
    const jobTypeCounts = {};
    allJobsForSummary.forEach((j) => {
        const typeDef = getJobTypeDef(j.jobType);
        const label = typeDef ? typeDef.label : j.jobType;
        if (!jobTypeCounts[label]) jobTypeCounts[label] = { count: 0, totalVms: 0 };
        jobTypeCounts[label].count++;
        jobTypeCounts[label].totalVms += (parseInt(j.count, 10) || 0);
    });
    const jobsSummary = Object.keys(jobTypeCounts).length > 0
        ? Object.entries(jobTypeCounts)
            .map(([label, { count, totalVms }]) => totalVms > 0 ? `${count}× ${label} (${totalVms} VMs)` : `${count}× ${label}`)
            .join(', ')
        : 'None';

    // Named Veeam server summary (existing components from Step 1)
    const _namedServerTypeCounts = {};
    sites.forEach((s) => {
        (Array.isArray(s.veeamServers) ? s.veeamServers : []).forEach((sv) => {
            _namedServerTypeCounts[sv.type] = (_namedServerTypeCounts[sv.type] || 0) + 1;
        });
    });
    const _serverTypeLabels = { vbr: 'VBR', vb365: 'VB365', vbfaws: 'VBfAWS', vbfaz: 'VBfAz', vbfgc: 'VBfGC', kasten: 'Kasten' };
    const namedServerSummary = Object.keys(_namedServerTypeCounts).length > 0
        ? Object.entries(_namedServerTypeCounts)
            .map(([type, count]) => count > 1
                ? `${_serverTypeLabels[type] || type} (${count}×)`
                : (_serverTypeLabels[type] || type))
            .join(', ')
        : null;

    // Upgrade component detail string (e.g. "VBR ×2, Proxy ×4, Repo ×3")
    const _upgradeComponentParts = [];
    if (anyUpgradeVbr && upgradeVbrCount > 0) _upgradeComponentParts.push(`VBR ×${upgradeVbrCount}`);
    if (anyUpgradeProxy && upgradeProxyCount > 0) _upgradeComponentParts.push(`Proxy ×${upgradeProxyCount}`);
    if (anyUpgradeRepo && upgradeRepoCount > 0) _upgradeComponentParts.push(`Repo ×${upgradeRepoCount}`);
    if (anyUpgradeVb365) {
        const _vb365Parts = ['VB365'];
        if (upgradeVb365ProxyCount > 0) _vb365Parts.push(`proxy ×${upgradeVb365ProxyCount}`);
        if (upgradeVb365RepoCount > 0) _vb365Parts.push(`repo ×${upgradeVb365RepoCount}`);
        _upgradeComponentParts.push(_vb365Parts.join(' '));
    }
    if (anyUpgradeVbfAws && upgradeVbfAwsCount > 0) _upgradeComponentParts.push(`VBfAWS ×${upgradeVbfAwsCount}`);
    if (anyUpgradeVbfAz  && upgradeVbfAzCount  > 0) _upgradeComponentParts.push(`VBfAz ×${upgradeVbfAzCount}`);
    if (anyUpgradeVbfGc  && upgradeVbfGcCount  > 0) _upgradeComponentParts.push(`VBfGC ×${upgradeVbfGcCount}`);
    if (anyUpgradeKasten && upgradeKastenCount > 0)  _upgradeComponentParts.push(`Kasten ×${upgradeKastenCount}`);
    const upgradeComponentDetail = _upgradeComponentParts.length > 0 ? _upgradeComponentParts.join(', ') : null;

    const configurationRows = [
        ['Project Scope', projectScopeLabel],
        ['Sites', siteCount > 1
            ? `${siteCount} — ${architectureOptionLabels.siteTopology[architecture.siteTopology] || architecture.siteTopology}`
            : '1 (single-site)'],
        ['Delivery Mode', getImplementationModeLabel(implementationMode)
            + (advisorySupportRequired === 'yes' ? ' + Implementation Advisory' : '')
            + (!isDesignOnly ? (' · ' + (_implDeliveryStyle === 'white-glove' ? 'White-glove' : 'Skeleton jobs')) : '')],
    ];

    // ── New workloads to protect ──────────────────────────────────────────────────
    if (hypervisorCounts.length > 0)        configurationRows.push(['Hypervisors', hypervisorSummary]);
    if (cloudWorkloadCounts.length > 0)     configurationRows.push(['Cloud Workloads', cloudSummary]);
    if (saasWorkloadCounts.length > 0)      configurationRows.push(['SaaS Workloads', saasSummary]);
    if (containerWorkloadCounts.length > 0) configurationRows.push(['Container Workloads', containerSummary]);
    if (pluginQuantities.length > 0)        configurationRows.push(['Enterprise Plug-Ins', pluginSummary]);
    if (unstructuredDataCounts.length > 0)  configurationRows.push(['Unstructured Data', unstructuredDataSummary]);
    if (agentCounts.length > 0)             configurationRows.push(['Veeam Agents', agentSummary]);

    // ── Existing environment (managed jobs + named Veeam servers) ─────────────────
    if (_hC || allJobsForSummary.length > 0) {
        configurationRows.push(['Managed Backup Jobs', jobsSummary]);
        if (namedServerSummary) configurationRows.push(['Veeam Components', namedServerSummary]);
        configurationRows.push(['Health Check', hcActive ? 'Yes' : 'No']);
        if (upgradeActive) {
            configurationRows.push(['Upgrade', upgradeComponentDetail || 'Yes']);
        } else if (_hC) {
            configurationRows.push(['Upgrade', 'No']);
        }
    }

    // ── Migration scope ───────────────────────────────────────────────────────────
    if (hypervisorMigrationRequired) {
        const _hvmCount = hypervisorMigrationScopes.length;
        configurationRows.push(['Hypervisor Migration', `${_hvmCount} scope${_hvmCount !== 1 ? 's' : ''}`]);
    }
    if (vbrRepoMigrationRequired) {
        const _vbrCount = vbrRepoMigrationScopes.length;
        configurationRows.push(['VBR Repo Migration', `${_vbrCount} repositor${_vbrCount !== 1 ? 'ies' : 'y'}`]);
    }
    if (vb365RepoMigrationRequired) {
        const _v365Count = vb365RepoMigrationScopes.length;
        configurationRows.push(['VB365 Repo Migration',
            `${_v365Count} scope${_v365Count !== 1 ? 's' : ''} — ${vb365TotalRepoCount} repositor${vb365TotalRepoCount !== 1 ? 'ies' : 'y'}, ${getProtectedTbSummary(vb365TotalConsumedTb)}`]);
    }
    if (_hV) {
        configurationRows.push(['VDC Vault Data', getProtectedTbSummary(getRepoMigrationTotalProtectedTb(sites))]);
    }

    // ── Architecture (only when standard design sections are in scope) ────────────
    // Helper: resolve a display label from the architectureOptionLabels map, falling back
    // to the raw value if no label entry exists.
    const archLabel = (key) => architectureOptionLabels[key]?.[architecture[key]] ?? architecture[key];

    if (useStandardDesignSections) {
        configurationRows.push(
            ['VBR Platform', archLabel('osPlatform')],
            ['Veeam Version', archLabel('veeamVersion')]
        );
        if (architecture.osPlatform === 'linux') {
            configurationRows.push(['VSA HA', archLabel('vbrHa')]);
        }
        configurationRows.push(['Remote DB', archLabel('remoteDb')]);
        if (architecture.remoteDb === 'yes') {
            configurationRows.push(['Remote DB Type', archLabel('databaseType')]);
            if (architecture.databaseType === 'mssql') {
                configurationRows.push(['SQL AOAG', archLabel('sqlAoag')]);
            }
        }
        configurationRows.push(
            ['Advanced RBAC', archLabel('advancedRbac')],
            ['VBEM', archLabel('vbem')]
        );
        if (siteCount > 1) {
            configurationRows.push(['Site Topology', archLabel('siteTopology')]);
        }

        // ── Protection options (only show rows where something is active) ─────────
        if (backupVerificationRequired === 'yes_limited') {
            configurationRows.push(['Backup Verification', sureBackupRequired === 'yes' ? 'SureBackup' : 'No']);
        }
        if (backupCopyRequired === 'yes') configurationRows.push(['Backup Copy', 'Yes']);
        if (replicationRequired === 'yes') {
            configurationRows.push(
                ['Replication', `${replicationHours} hrs`],
                ['CDP Replication', toggleLabel(cdpReplicationRequired)],
                ['SureReplica', toggleLabel(sureReplicaRequired)]
            );
        }
        if (tapeIntegrationRequired === 'yes') {
            configurationRows.push(['Tape Integration', tapeConnectivityTypeLabels[tapeConnectivityType] || tapeConnectivityType]);
        }
        if (siemForwardingRequired === 'yes') configurationRows.push(['Backup Logs to SIEM', 'Yes']);

        // ── Monitoring & orchestration ────────────────────────────────────────────
        if (veeamOneRequired === 'yes') {
            configurationRows.push(
                ['Veeam ONE', 'Yes'],
                ['Veeam ONE Customisations', veeamOneOptions.length > 0 ? veeamOneOptionsSummary : 'None']
            );
        }
        if (vroRequired === 'yes') configurationRows.push(['VRO', 'Yes']);
    }

    // ── Design ────────────────────────────────────────────────────────────────────
    if (_templateJobsActive) {
        configurationRows.push(['Template / Skeleton Jobs', 'Yes — PS creates framework jobs; customer populates remaining workloads']);
    }

    // ── Post-deployment ───────────────────────────────────────────────────────────
    if (usePostDeploySection) {
        configurationRows.push(
            ['Enablement', toggleLabel(enablementRequired)],
            ['UAT', toggleLabel(uatRequired)],
            ['As-Built Documentation', toggleLabel(asBuiltRequired)],
            ['DR Runbook', toggleLabel(drRunbookRequired)],
            ['SOPs', toggleLabel(sopRequired)]
        );
    }

    const configurationMarkup = `
        <section class="results-section">
            <div class="results-section__header">
                <span class="results-section__eyebrow">Inputs</span>
                <h4 class="results-section__title">Configuration Snapshot</h4>
            </div>
            <dl class="results-definition-list">
                ${configurationRows.map(([label, value]) => `
                    <div class="results-definition-row">
                        <dt>${escapeHtml(label)}</dt>
                        <dd>${escapeHtml(value)}</dd>
                    </div>
                `).join('')}
            </dl>
        </section>
    `;

    // Assumptions section for Step 3 output
    const assumptionEntries = [
        ['Network bandwidth is adequate', toggleValue(document.getElementById('assumption-bandwidth-adequate')) ?? 'yes'],
        ['Customer resource available throughout', toggleValue(document.getElementById('assumption-customer-resource-available')) ?? 'yes'],
        ['Remote delivery accepted', toggleValue(document.getElementById('assumption-remote-delivery-accepted')) ?? 'yes'],
        ['Admin access provided before start', toggleValue(document.getElementById('assumption-admin-access-provided')) ?? 'yes'],
        ['Valid Veeam licence in place', toggleValue(document.getElementById('assumption-licence-in-place')) ?? 'yes'],
        ['Customer manages change control / CAB process', toggleValue(document.getElementById('assumption-change-control')) ?? 'yes'],
    ];
    const vaultScopes = vbrRepoMigrationScopes.filter(
        (s) => s.targetType === 'vdc-vault-aws' || s.targetType === 'vdc-vault-azure'
    );
    const preEngagementEntries = vaultScopes.length > 0 ? [
        ['Vault analyzer tool ran and no blockers found', vaultScopes.every((s) => s.vaultAnalyzerClear !== false) ? 'yes' : 'no'],
    ] : [];
    const customRisksNotes = (document.getElementById('custom-risks-notes')?.value ?? '').trim();
    const hasAssumptionRisk = assumptionEntries.some(([, v]) => v === 'no');
    const hasPreEngagementRisk = preEngagementEntries.some(([, v]) => v === 'no');
    const assumptionsMarkup = `
        <section class="results-section">
            <div class="results-section__header">
                <span class="results-section__eyebrow">Risks &amp; Constraints</span>
                <h4 class="results-section__title">Assumptions${hasAssumptionRisk ? ' <span class="results-assumption-risk-badge">Risk flagged</span>' : ''}</h4>
            </div>
            <dl class="results-definition-list">
                ${assumptionEntries.map(([label, value]) => `
                    <div class="results-definition-row${value === 'no' ? ' results-definition-row--risk' : ''}">
                        <dt>${escapeHtml(label)}</dt>
                        <dd>${escapeHtml(toggleLabel(value))}</dd>
                    </div>
                `).join('')}
            </dl>
            ${customRisksNotes ? `<div class="results-custom-notes"><strong>Additional notes:</strong> ${escapeHtml(customRisksNotes)}</div>` : ''}
        </section>
        <section class="results-section">
            <div class="results-section__header">
                <span class="results-section__eyebrow">Risks &amp; Constraints</span>
                <h4 class="results-section__title">Pre-engagement checks${hasPreEngagementRisk ? ' <span class="results-assumption-risk-badge">Risk flagged</span>' : ''}</h4>
            </div>
            <dl class="results-definition-list">
                ${preEngagementEntries.map(([label, value]) => `
                    <div class="results-definition-row${value === 'no' ? ' results-definition-row--risk' : ''}">
                        <dt>${escapeHtml(label)}</dt>
                        <dd>${escapeHtml(toggleLabel(value))}</dd>
                    </div>
                `).join('')}
            </dl>
        </section>
    `;

    const isPhasesView = window.step3ViewMode === 'phases';

    // ── Phase track markup (used when isPhasesView is true) ─────────────────────
    // Phase adjustments (phaseHourAdjustments) are the primary way to tune the
    // estimate. Each main phase card has +/− steppers. PM/contingency are
    // toggled via include/exclude buttons in the Project Overhead bar.
    const phasesMarkup = isPhasesView ? `
        <section class="results-section">
            <div class="results-section__header">
                <span class="results-section__eyebrow">Timeline</span>
                <h4 class="results-section__title">Project Phases</h4>
                <p class="results-section__copy">Use the − / + controls to adjust each phase. The total and summary update automatically.</p>
            </div>
            <div class="phase-track">
                ${phaseBreakdown.filter(p => p.id !== 'pm-overhead').map((phase, idx) => {
                    const phaseAdjHrs = (phase.subItems || []).reduce((s, sub) =>
                        s + (window.subItemHourAdjustments.has(sub.id)
                            ? window.subItemHourAdjustments.get(sub.id)
                            : sub.hours), 0);
                    const phaseRecHrs  = phase.hours;
                    const phaseDeltaHrs = phaseAdjHrs - phaseRecHrs;
                    const phaseDeltaBadge = phaseDeltaHrs !== 0
                        ? `<span class="phase-card__adj-tag phase-card__adj-tag--${phaseDeltaHrs > 0 ? 'increase' : 'decrease'}">${phaseDeltaHrs > 0 ? '+' : ''}${phaseDeltaHrs}&thinsp;hrs</span>`
                        : '';
                    const isExpanded   = window.expandedPhaseIds && window.expandedPhaseIds.has(phase.id);
                    const taskCount    = (phase.subItems || []).length;
                    return `
                    ${idx > 0 ? '<div class="phase-connector" aria-hidden="true"></div>' : ''}
                    <article class="phase-card phase-card--${escapeHtml(phase.id)}${isExpanded ? ' is-expanded' : ''}" data-phase-id="${phase.id}">
                        <div class="phase-card__header" data-expand-phase="${phase.id}" role="button" tabindex="0" aria-expanded="${isExpanded}" aria-label="${escapeHtml(phase.title)} — ${isExpanded ? 'collapse' : 'expand'} tasks">
                            <span class="phase-card__num">${idx + 1}</span>
                            <div class="phase-card__title-block">
                                <h5 class="phase-card__title">${escapeHtml(phase.title)}</h5>
                                ${!isExpanded && taskCount > 0 ? `<span class="phase-card__task-count">${taskCount} task${taskCount !== 1 ? 's' : ''}</span>` : ''}
                            </div>
                            <span class="phase-card__chevron" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><polyline points="3,5 7,9 11,5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </span>
                        </div>
                        <div class="phase-card__metric">
                            <strong class="phase-card__hrs">${phaseAdjHrs}</strong>
                            <span class="phase-card__unit">hrs</span>
                            <span class="phase-card__days">${phaseAdjHrs > 0 ? Math.ceil(phaseAdjHrs / 8) + '&thinsp;days' : '0&thinsp;days'}</span>
                            ${phaseDeltaBadge}
                        </div>
                        <p class="phase-card__desc">${escapeHtml(phase.description)}</p>
                        ${(phase.subItems && phase.subItems.length > 0 && isExpanded) ? `
                            <ul class="phase-card__sub-items">
                                ${phase.subItems.map(sub => {
                                    const adjSubHrs = window.subItemHourAdjustments.has(sub.id)
                                        ? window.subItemHourAdjustments.get(sub.id)
                                        : sub.hours;
                                    const subDelta = adjSubHrs - sub.hours;
                                    return `
                                    <li class="phase-card__sub-item${subDelta !== 0 ? ' phase-card__sub-item--modified' : ''}">
                                        <span class="phase-card__sub-label">${escapeHtml(sub.label)}</span>
                                        <div class="phase-card__sub-adj">
                                            <button type="button" class="phase-card__sub-btn" data-sub-step="-1" data-sub-item-id="${sub.id}" data-sub-item-rec="${sub.hours}" aria-label="Decrease ${escapeHtml(sub.label)} hours">−</button>
                                            <span class="phase-card__sub-rec" data-sub-rec-id="${sub.id}">${sub.hours}</span>
                                            <span class="phase-card__sub-val" data-sub-val-id="${sub.id}">${adjSubHrs}</span>
                                            <button type="button" class="phase-card__sub-btn" data-sub-step="1" data-sub-item-id="${sub.id}" data-sub-item-rec="${sub.hours}" aria-label="Increase ${escapeHtml(sub.label)} hours">+</button>
                                            <span class="phase-card__sub-unit">hrs</span>
                                        </div>
                                    </li>`;
                                }).join('')}
                            </ul>
                        ` : ''}
                    </article>
                    `;
                }).join('')}
            </div>
            ${(() => {
                const pm = phaseBreakdown.find(p => p.id === 'pm-overhead');
                if (!pm) return '';
                const allExcluded = pm.subItems.length > 0 && pm.subItems.every(s => s.excluded);
                const pmAdjHrs = pm.subItems.reduce((s, sub) => {
                    if (sub.excluded) return s;
                    return s + (window.subItemHourAdjustments.has(sub.id)
                        ? window.subItemHourAdjustments.get(sub.id)
                        : sub.hours);
                }, 0);
                const pmRecHrs = pm.subItems.reduce((s, sub) => sub.excluded ? s : s + sub.hours, 0);
                const pmDelta = pmAdjHrs - pmRecHrs;
                const pmDeltaBadge = pmDelta !== 0
                    ? `<span class="phase-card__adj-tag phase-card__adj-tag--${pmDelta > 0 ? 'increase' : 'decrease'}">${pmDelta > 0 ? '+' : ''}${pmDelta}&thinsp;hrs</span>`
                    : '';
                return `
                <div class="phase-overhead-bar${allExcluded ? ' phase-overhead-bar--excluded' : ''}">
                    <div class="phase-overhead-bar__label">
                        <h5 class="phase-overhead-bar__title">${escapeHtml(pm.title)}</h5>
                        <p class="phase-overhead-bar__desc">${escapeHtml(pm.description)}</p>
                    </div>
                    <div class="phase-overhead-bar__metric">
                        <strong class="phase-card__hrs">${pmAdjHrs}</strong>
                        <span class="phase-card__unit">hrs</span>
                        <span class="phase-card__days">${allExcluded ? 'excluded' : Math.ceil(pmAdjHrs / 8) + '&thinsp;days'}</span>
                        ${pmDeltaBadge}
                    </div>
                    <ul class="phase-overhead-bar__items">
                        ${pm.subItems.map(sub => {
                            const adjHrs = window.subItemHourAdjustments.has(sub.id)
                                ? window.subItemHourAdjustments.get(sub.id)
                                : sub.hours;
                            const delta = adjHrs - sub.hours;
                            const deltaHtml = !sub.excluded && delta !== 0
                                ? `<span class="phase-card__sub-delta phase-card__sub-delta--${delta > 0 ? 'increase' : 'decrease'}">${delta > 0 ? '+' : ''}${delta}</span>`
                                : '';
                            return `
                            <li class="phase-overhead-toggle${sub.excluded ? ' phase-overhead-toggle--excluded' : ''}">
                                ${sub.id ? `<button type="button" class="phase-overhead-toggle__btn" data-item-exclude="${sub.id}" aria-pressed="${!sub.excluded}" aria-label="${sub.excluded ? 'Include' : 'Exclude'} ${escapeHtml(sub.label)}">
                                    <span class="phase-overhead-toggle__check" aria-hidden="true">${sub.excluded ? `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><line x1="2" y1="2" x2="10" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="10" y1="2" x2="2" y2="10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>` : `<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`}</span>
                                    <span class="phase-overhead-toggle__label">${escapeHtml(sub.label)}</span>
                                </button>` : `
                                    <span class="phase-overhead-toggle__check" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                                    <span class="phase-overhead-toggle__label">${escapeHtml(sub.label)}</span>
                                `}
                                ${sub.excluded
                                    ? `<span class="phase-overhead-toggle__hrs">excluded</span>`
                                    : `<div class="phase-card__sub-adj">
                                        <button type="button" class="phase-card__sub-btn" data-sub-step="-1" data-sub-item-id="${sub.id}" data-sub-item-rec="${sub.hours}" aria-label="Decrease ${escapeHtml(sub.label)} hours">−</button>
                                        <span class="phase-card__sub-val" data-sub-val-id="${sub.id}">${adjHrs}</span>
                                        <button type="button" class="phase-card__sub-btn" data-sub-step="1" data-sub-item-id="${sub.id}" data-sub-item-rec="${sub.hours}" aria-label="Increase ${escapeHtml(sub.label)} hours">+</button>
                                        <span class="phase-card__sub-unit">hrs</span>
                                        ${deltaHtml}
                                    </div>`
                                }
                            </li>`;
                        }).join('')}
                    </ul>
                </div>`;
            })()}
            ${isDesignOnly ? `
            <p class="phase-oos-notice">Implementation and deployment are out of scope for this engagement — physical configuration and deployment are the responsibility of the customer or a nominated third party.</p>
            ` : ''}
        </section>
    ` : '';

    // Collect raw formula inputs for the developer debug panel
    const debugData = {
        engagementType,
        effortTotalInstances,
        unprotectedVmCount,
        effortSelectedScopeCount,
        siteCount,
        implementationMode,
        tapeConnectivityType,
        veeamOneOptionCount: veeamOneOptions.length,
        // Toggle/select states needed to pre-populate the live calculator
        veeamOneRequired,
        vroRequired,
        backupVerificationRequired,
        sureBackupRequired,
        backupCopyRequired,
        replicationRequired,
        cdpReplicationRequired,
        enablementRequired,
        uatRequired,
        asBuiltRequired,
        drRunbookRequired,
        osPlatform:          architecture.osPlatform || 'windows',
        distributedTopology: siteCount > 1 && architecture.siteTopology === 'distributed',
        tapeCalcValue:       tapeIntegrationRequired === 'yes' ? tapeConnectivityType : 'none',
        kickoffHours,
        customerKickoffHours,
        discoveryHours,
        discoveryWorkloadHours,
        componentDiscoveryHours,
        discoverySiteHours,
        discoveryWorkloadCount,
        vb365TotalConsumedTb,
        designHours,
        _implDeliveryStyle,
        _templateJobsActive,
        _vbrDesignVms,
        _vb365DesignVms,
        _kastenDesignVms,
        _vbfCloudDesignVms,
        _voneDesignVms,
        _voneDesignHours,
        _vroDesignVms,
        _vroDesignHours,
        implementationHours,

        veeamOneHours,
        veeamOneOptionHours,
        vroHours,
        totalHcUnits,
        upgradeHealthCheckHours,
        jobValidationHours,
        upgradePreFlightHours,
        upgradeComponentHours,
        upgradeVbrCount,
        upgradeProxyCount,
        upgradeRepoCount,
        upgradeVb365ProxyCount,
        upgradeVb365RepoCount,
        upgradeVbfCloudRepoCount,
        upgradeVbfAwsCount,
        upgradeVbfAzCount,
        upgradeVbfGcCount,
        upgradeKastenCount,
        sureBackupHours,
        backupCopyHours,
        replicationHours,
        sureReplicaHours,
        cdpReplicationHours,
        tapeIntegrationHours,
        distributedDeploymentHours,
        vsaV13AdjustmentHours,
        newVbrInstallHours,
        architecturePlatformHours,
        hypervisorMigrationHours,
        vb365RepoMigrationHours,
        vbrRepoMigrationHours,
        vbrRepoMigrationTotalTb,
        vbrRepoMigrationDesignHours,
        vb365RepoMigrationDesignHours,
        enablementHours,
        uatHours,
        asBuiltHours,
        drRunbookHours,
        sopHours,
        handoverWorkshopHours,
        projectManagementHours,
        contingencyHours,
        baseHours,
        estimatedHours,
        estimatedDays,
    };

    // Build adjusted phaseBreakdown for the SOW — apply subItemHourAdjustments so
    // per-phase rows in the SOW table match the adjusted totals shown in Step 3.
    const adjustedPhaseBreakdown = phaseBreakdown.map(phase => {
        if (!phase.subItems || phase.subItems.length === 0) return phase;
        const adjHours = phase.subItems.reduce((s, sub) =>
            s + (window.subItemHourAdjustments.has(sub.id)
                ? window.subItemHourAdjustments.get(sub.id)
                : sub.hours), 0);
        return { ...phase, hours: adjHours };
    });

    // Build SOW data bundle for generateSowMarkup()
    const sowData = {
        projectName,
        engagementType,
        implementationMode,
        siteCount,
        // Resolve display names so SOW shows the pre-populated default ("Primary Site",
        // etc.) instead of "Unnamed site" when the user leaves the default untouched.
        // Default names live only in getSiteDisplayName — they're never stored on site.name.
        sites: sites.map((site, index) => ({ ...site, name: getSiteDisplayName(site, index) })),
        categoryBreakdown,
        categoryHourAdjustments: window.categoryHourAdjustments,
        displayHours,
        displayDays,
        architecture,
        backupVerificationRequired,
        sureBackupRequired,
        backupCopyRequired,
        replicationRequired,
        sureReplicaRequired,
        cdpReplicationRequired,
        tapeIntegrationRequired,
        tapeConnectivityType,
        siemForwardingRequired,
        veeamOneRequired,
        veeamOneOptions,
        vroRequired,
        hypervisorMigrationRequired,
        vb365RepoMigrationRequired,
        vb365RepoMigrationScopes,
        vb365TotalRepoCount,
        vb365InstalledVersion,
        vbrRepoMigrationRequired,
        vbrRepoMigrationScopes,
        enablementRequired,
        uatRequired,
        asBuiltRequired,
        drRunbookRequired,
        sopRequired,
        hypervisorCounts,
        cloudWorkloadCounts,
        saasWorkloadCounts,
        containerWorkloadCounts,
        agentCounts,
        unstructuredDataCounts,
        pluginQuantities,
        assumptionEntries,
        preEngagementEntries,
        customRisksNotes,
        advisorySupportRequired,
        implDeliveryStyle: _implDeliveryStyle,
        hcActive,
        upgradeActive,
        useRepoMigrationMode,
        useStandardDesignSections,
        useInstanceMetrics,
        totalWorkloadCount,
        phaseBreakdown: adjustedPhaseBreakdown,
        hC: _hC,
        hW: _hW,
        expansionHours,
        expansionScopes,
        // ── Server mapping: new deployments vs reused existing servers ──────────
        // Used by sow.js to generate context-aware deployment/configuration steps.
        newVbrCount:       newVbrLanes.length + (impliedNewVbr ? 1 : 0),
        existingVbrCount:  window.step2VbrLanes.filter((l) => l.type === 'existing').length,
        newVb365Count:     _vb365NewServerCount,
        existingVb365Count: Array.isArray(window.step2Vb365Lanes)
            ? window.step2Vb365Lanes.filter((l) => l.type === 'existing').length
            : 0,
        // Unique cloud providers with lanes in Step 2 (each lane = a new appliance deployment).
        // Values: 'vbfaws' | 'vbfaz' | 'vbfgc' | 'kasten'
        cloudProviders: [...new Set(
            (Array.isArray(window.step2CloudLanes) ? window.step2CloudLanes : []).map((l) => l.cloudProvider)
        )],
    };
    const isSowView = window.step3ViewMode === 'sow';
    const sowMarkup = generateSowMarkup(sowData);

    if (isSowView) {
        document.getElementById('output').innerHTML = sowMarkup + (AppState.devModeEnabled ? buildFormulaDebugPanelMarkup(debugData) : '');
    } else {
        document.getElementById('output').innerHTML = `
            ${summaryMetricsMarkup}
            ${isPhasesView ? phasesMarkup : ''}
            ${siteBreakdownMarkup}
            ${assumptionsMarkup}
            ${configurationMarkup}
            ${AppState.devModeEnabled ? buildFormulaDebugPanelMarkup(debugData) : ''}
        `;
    }
}


// ─── Exports ─────────────────────────────────────────────────────────────────
export {
    GF_SVG_ICONS,
    VEEAM_SERVER_TYPES,
    UNMANAGED_MODIFIER_DEFS,
    getExistingEnvironmentFieldDependents,
    getExistingEnvironmentOrderedChildKeys,
    normalizeExistingEnvironmentToggle,
    hasExistingEnvironmentFieldValue,
    clearExistingEnvironmentDependentFields,
    enforceExistingEnvironmentDependencies,
    createEmptyExistingEnvironment,
    normalizeSiteForEngagement,
    getExistingEnvironmentSelectableFieldOrder,
    getAvailableExistingEnvironmentFields,
    createExistingEnvironmentSelectionState,
    getGfChipIcon,
    normalizeExistingEnvironmentCustomLabel,
    normalizeExistingEnvironmentCustomLabels,
    normalizeExistingEnvironment,
    getExistingEnvironmentItems,
    getExistingEnvironmentSnapshotQuantity,
    hasExistingEnvironment,
    pruneExistingEnvironmentCustomLabels,
    getExistingEnvironmentDisplayItems,
    buildJobRowMarkup,
    buildUnprotectedWorkloadRowMarkup,
    getWorkloadLabel,
    getWorkloadEffortUnits,
    getWorkloadScopeCount,
    hasMeaningfulWorkloadScope,
    getSiteDisplayName,
    getRepoMigrationProtectedTb,
    isModifierActive,
    buildExistingEnvGroupMarkup,
    buildGfSiteCardMarkup,
    buildGfVaultChipMarkup,
    buildNamedServerChipsMarkup,
    buildSectionedVeeamGroupContent,
    buildUnprotectedWorkloadListMarkup,
    buildSourceWorkloadSectionMarkup,
    buildSwpChipMarkup,
    buildSwpChipDetail,
    buildSwpChipBadges,
    getExistingEnvironmentSummary,
    getSiteScopeSummaryText,
    getRepoMigrationScopedItemCount,
    getRepoMigrationTotalProtectedTb,
    hasExistingEnvironmentInAnySite,
    normalizeArchitectureState,
    buildArchitectureStateSnapshot,
    getSelectedVeeamOneOptions,
    getScopeMetricMetaText,
    getSiteScopeMetricLabel,
    getSiteScopeMetricValue,
    getCanvasCompItems,
    collectUpgradeVbrRows,
    buildAggregatedWorkloadData,
    needsNewVbrDeployment,
    isUnmanagedWorkloadScopeActive,
    scheduleRenderEstimate,
    renderEstimate,
    GF_SVG_PLUS
};
